/**
 * The runtime (web-plan.md F1) — the web twin of `app/src/runtime/`: owns the
 * immutable vanilla store, the socket, and every async flow. Components send
 * commands (plain method calls); renderer adapters subscribe to snapshots.
 *
 * The one discipline ported wholesale from the desktop: every network
 * handler snapshots what it needs, awaits, then checks the account lifetime
 * and **re-checks `feed.channelId` before writing**. `appendMessages` dedupes on `seq`, and
 * `seq` is per channel — fifty of channel A's messages would splice silently
 * into B's history without the guard.
 */
import { freeze, produce } from "immer";
import { createStore, type StoreApi } from "zustand/vanilla";

import type {
  BootstrapResponse,
  ChatRowsResponse,
  DirectoryEntry,
  Message,
  PageResponse,
  SendBody,
  ServerEvent,
  Sticker,
} from "../gen/dto";

import * as api from "../lib/api/rest";
import { UploadError, upload } from "../lib/api/uploads";
import { ChatSocket, type SocketEvent } from "../lib/api/ws";
import { type Dict, type Lang, detectLang, dict } from "../lib/i18n";
import {
  assertCurrent,
  currentLifetime,
  endLifetime,
  isCurrent,
  sessionDelay,
  type SessionLifetime,
} from "../lib/lifetime";
import { ORIGINAL_MAX_BYTES, photoWeights, photoWeightsSupported } from "../lib/photo";
import { maskedPreview } from "../lib/preview";
import { newUpload, setUploadSent } from "../lib/upload";
import { armReauthWindow, inReauthWindow, session, setSession } from "../lib/session";
import * as sounds from "../lib/sounds";
import { isValidTtl } from "../lib/ttl";
import {
  VoiceSession,
  type SharePainter,
  type VoiceEvents,
  shareViewSupported,
  voiceSupported,
} from "../lib/voice/session";
import {
  type ParsedMarkdown,
  type SplitResult,
  findTags,
  parseCommand,
  parseMarkdown,
  splitMessage,
} from "../lib/wasm";
import { isStaff, may } from "../lib/broadcast";
import type { BroadcastRef } from "../lib/links";
import * as mut from "./mutators";
import * as people from "./people";
import * as stickers from "./stickers";
import { attachmentOf, isSticker } from "../lib/stickers";
import {
  AFK_AFTER_MS,
  PAGE_SIZE,
  PENDING_SEQ,
  RING_FOR_MS,
  TICK_MS,
  WINDOW_SIZE,
  type ChannelCardEntry,
  type ForwardDraft,
  type SendState,
  type LinkPreviewEntry,
  type SecurityNotice,
  type StickerSheet,
  type StickersState,
  type ThreadRef,
  type Toast,
  type UiState,
  channelCardKey,
  handleCardKey,
  initialState,
  isBroadcastRoom,
  isDmRoom,
  isOfficial,
  isPet,
} from "./types";

/** The message that takes a Stop back (pets-plan.md D368) —
 * `echo_wire::command::RESTART` behind its sigil. */
const RESTART_TEXT = "/restart";

const MAX_SPLIT_PARTS = 10;

/** How long a forward that met the message budget's 429 waits before asking
 * again (forwarding.md D377): a little over the budget's refill — 20 a
 * minute, one every three seconds (chat-api.md §4) — so the next ask finds a
 * token. The desktop's `FORWARD_RETRY_AFTER`. */
const FORWARD_RETRY_AFTER_MS = 3_500;
/** How long a page of history that failed is not asked for again (D1075). A failed
 * page takes its spinner away, the column moves under the reader's anchor, and the
 * scroll that makes asks again: without this, one request per round trip for as
 * long as the gateway refuses. */
const PAGE_RETRY_WAIT_MS = 5_000;
/** How many of the gateway's waits `GET /v1/me` sits through after a sign-in or a
 * reload (D1075): the window has no account in it without the answer. */
const SIGN_IN_BUSY_PATIENCE = 3;
/** How many of those waits one message of a forward sits through before it is
 * given up as failed: two minutes, which the budget alone never takes. */
const FORWARD_RETRIES = 35;

/** The rows of the first page (docs/fast-start.md D1053): a screen and a half of
 * the sidebar at its tallest. The desktop's `FIRST_PAGE`. */
const FIRST_PAGE = 20;
/** How many rows one `GET /v1/chats/rows` asks for (D1059): the route's limit. */
const ROWS_PER_REQUEST = 50;
/** How many messages of each chat of the first page are read ahead of its
 * opening (D1060). The desktop's `READ_AHEAD`. */
const READ_AHEAD = 10;
/** How many of the gateway's waits a read behind the first screen sits through
 * (D321, D1075): nobody is waiting on it. The desktop's `LANE_PATIENCE`. */
const LANE_PATIENCE = 5;
/** How long such a read waits after the lane's 429: the gateway refills a page's
 * token within the second (`gateway/src/chat/lane.rs`). */
const LANE_WAIT_MS = 1_000;
/** How long the walk through the rest of the list waits before asking again a page
 * the gateway did not answer, one wait after another and then the last for as long
 * as it takes (D1084): the rungs `syncAfter` climbs. */
const REST_RETRY_MS = [5_000, 15_000, 35_000, 55_000] as const;
/** How long ids are gathered before they are asked about (D1057): every read of
 * one render lands in one request. */
const PEOPLE_GATHER_MS = 60;
/** The most ids one `GET /v1/users/lookup` names (chat-api.md §2.1a). */
const PEOPLE_BATCH = 200;
/** The most handles of one message asked about (the route's own limit). */
const PEOPLE_TAGS = 20;
/** How long the lookups rest after a request that failed (D1057). */
const PEOPLE_REST_MS = 5_000;
/** How long something said once waits for the name it says (D1063): the news
 * matters more than the name. */
const NAME_WAIT_MS = 1_500;
/** How long a search must stand still before the gateway is asked (D1058). */
const PEOPLE_SEARCH_PAUSE_MS = 250;
/** Fewer characters than this mean nobody, to the gateway as here
 * (`echo_wire::search::MIN_WORD_CHARS`). */
const PEOPLE_SEARCH_MIN_CHARS = 2;

/** Page previews in the air at once, across the app (D280): a feed full of
 * links is otherwise a burst of fetches, each of which may hold the gateway
 * for nine seconds and all of which draw on one per-account budget. */
const PREVIEW_LANE_WIDTH = 4;
/** Channel cards in the air at once (D281) — cheap to answer, but budgeted at
 * sixty a minute per account, and a slow page must not queue them. */
const CARD_LANE_WIDTH = 4;
/** How long a link card that failed for want of time — the budget, a fetch
 * that outlived its request, the network — waits before one may ask again. */
const LINK_RETRY_MS = 60_000;
/** How often a broadcast channel read without a subscription asks what is new
 * in it (docs/broadcast-channels-plan.md D839): it hears no frames. */
const GUEST_POLL_MS = 30_000;

/** How many messages a line's question asks for (D468): the newest one is the
 * answer, and the rest are there for a newest one already past its deadline
 * and not swept yet. The desktop's `LINE_PAGE`. */
const LINE_PAGE = 10;
/** How many times one line's question is asked again because the chat moved
 * while it was out — a stream of deletions settles long before this; it is
 * here so that nothing asks for ever. The desktop's `LINE_ROUNDS`. */
const LINE_ROUNDS = 8;

/** Bytes as standard base64, padded — what the sticker route takes (D548). In
 * chunks, so a 128 KiB file does not blow the argument list. */
function toBase64(bytes: Uint8Array): string {
  let text = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    text += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(text);
}

/** Captured at Send, never reconstructed from whichever screen opens later. */
interface SendIntent {
  readonly lifetime: SessionLifetime;
  readonly accountId: number;
  readonly selfName: string;
  readonly channel: number;
  readonly replyTo: number | null;
  readonly editing: number | null;
  readonly parts: ReadonlyArray<{
    readonly text: string;
    readonly entities: import("../gen/dto").Entity[];
    readonly nonce: string;
  }>;
  readonly staged: ReadonlyArray<Readonly<StagedFile>>;
  readonly unlimited: boolean;
  readonly pause: number;
  /** The sender closed the link's card in the composer (D289): every part of
   * the send says so, and so does each optimistic bubble, so no card flashes
   * up under one before its echo arrives. */
  readonly noLinkPreview: boolean;
  /** What the composer's clock stood at for this channel when the send was
   * made (disappearing-messages.md D298), seconds, or null for a message that
   * stays. Read by the runtime and not handed over by the box, so that a voice
   * note and a dropped file go out under it too; every part of a split send
   * carries it. Always null for an edit: `PATCH` neither sets nor moves a
   * deadline (D296). */
  readonly ttlS: number | null;
  /** The forward that goes with this send (forwarding.md D377): what waited
   * over the composer when the send was queued, sent after its words — the
   * comment first, as a message of its own, as Telegram sends one. Taken when
   * the send is queued and not when it is prepared: a voice note is prepared
   * when its recording starts, and what goes with it is whatever waits over
   * the box when it stops. */
  readonly forward: ForwardDraft | null;
  /** What the box held when the send was made, as typed (markdown) — what a
   * send called off from the pill (chat-plan.md D398), or an edit the gateway
   * did not take, gives back to the composer, whose own copy cleared itself
   * with the gesture. */
  readonly draft: string;
  /** The sticker this send is, instead of words of its own
   * (docs/stickers-plan.md D550) — its one part carries the nonce. */
  readonly sticker?: Sticker | null;
  /** The handles the words tag and this client has not met (docs/fast-start.md
   * D1057), when there are any: the gateway is asked about them when this send's
   * turn comes, and the words read again with whoever it names. */
  readonly tags?: readonly string[];
  /** The channel's limit on one message, as it stood at the gesture. */
  readonly maxChars: number;
  /** A post: the send is into a broadcast channel's own feed, where the
   * channel speaks (docs/broadcast-channels-plan.md D841) — never split, and
   * carrying the post's two switches (D851). */
  readonly post: boolean;
  /** A comment in the channel's name (D848): the face button of a holder of
   * `comment_as_channel`, as it stood at the gesture. */
  readonly asChannel: boolean;
  /** The post's switches as the composer had them (D851) — comments and
   * reactions on or off, absent the channel's default (D852). */
  readonly comments?: boolean;
  readonly reactions?: boolean;
}

type SendContext = Omit<
  SendIntent,
  "parts" | "staged" | "noLinkPreview" | "forward" | "draft" | "sticker" | "tags"
> & {
  readonly nonce: string;
};

/** What a send says beside its words and its files. */
export interface SendOptions {
  /** `no_link_preview` (link-previews.md D289): the composer's bar was
   * closed on the draft's link. */
  readonly noLinkPreview?: boolean;
}

/** Finishing a recording may await an encoder; its target is already fixed. */
export type PreparedSend = (text: string, staged?: StagedFile[], options?: SendOptions) => void;

export type RuntimeTransitionKind = "screen" | "center" | "sheet" | "pop" | "tab" | "toast";

/** Rendering and commit timing belong to the host, never the network runtime. */
export interface RuntimeHost {
  transition(kind: RuntimeTransitionKind, commit: () => void): void;
}

export class Runtime {
  readonly store: StoreApi<UiState>;
  get state(): UiState {
    return this.store.getState();
  }
  private started = false;
  private disposed = false;
  private resumedLifetime: SessionLifetime | null = null;
  private unregisterUnauthorized: (() => void) | null = null;
  private channelGeneration = 0;
  private requestedChannel: number | null = null;
  /** When the page above the window may be asked for again after a refusal
   * (D1075): the chat it was refused in, and the moment. */
  private olderRetry: { channel: number; at: number } | null = null;
  /** The same for the page below it (docs/fast-start.md D1091). */
  private newerRetry: { channel: number; at: number } | null = null;
  /** The window a move to another stretch of its chat is in the air for, by its
   * `channelGeneration` (`moveWindow`): a second send from up in the history asks
   * no second page. */
  private windowMoving: number | null = null;
  /** The state asked for again after a refusal (D1075, `syncAfter`). */
  private readonly syncAgain = {
    waiting: false,
    tries: 0,
    timer: null as ReturnType<typeof setTimeout> | null,
  };
  /** Moves with every answer a sync takes: the rest of the list read behind an
   * older answer gives way to the newer one (docs/fast-start.md D1059). */
  private syncGeneration = 0;
  /** The newest reading of the rest of the list, for whoever waits on an older
   * one that gave way. */
  private restReading: Promise<void> = Promise.resolve();
  /** Every chat on the list as the last sync's answer has it (D1053), whether or
   * not its row has been read yet. */
  private listed: ReadonlySet<number> = new Set();
  /** Every chat that went this session, in order (D326): an answer asked before
   * one went must not bring its row back. Read from where it stood at the
   * asking. */
  private wentChats: number[] = [];
  /** The chat the address names, as the URL's coordinator last said
   * (`cancelChannelNavigation`). A row further down the list than the first page
   * is not here yet when a sync would open the first one in its place. */
  private addressed: number | null = null;
  /** What was read ahead of each chat's opening (D1060), in memory until the chat
   * opens or a frame says it is stale. */
  private readonly aheadPages = new Map<number, PageResponse>();
  /** Whether this lifetime has asked for it: once, after the first sync. */
  private aheadAsked = false;
  /** While that read is out, the chats a frame spoke of since it left: what it
   * brings of them is older than the frame. Null with none out, or once a dropped
   * socket has made the whole answer stale. */
  private aheadMoved: Set<number> | null = null;
  /** The people a read found nobody for (D1057): the timer that gathers them, and
   * the same timer while the lookups rest after a failure. */
  private peopleTimer: ReturnType<typeof setTimeout> | null = null;
  /** The search waiting for the typing to stop (D1058). */
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private unwatchPeople: (() => void) | null = null;
  /** Somebody not met being asked about for something that waits on their name
   * (`named`). */
  private readonly namesOut = new Map<number, Promise<void>>();
  private cardGeneration = 0;
  private readonly activityListener = () => this.noteActivity();
  private readonly visibilityListener = () => {
    if (document.visibilityState !== "visible") return;
    this.noteActivity();
    // A hidden tab's timers run late, by as much as a minute: what came due
    // behind it goes before anybody looks at it again (D298).
    this.expireDue();
    // …and a window no frame reaches asks what is new in it as the window
    // comes back (docs/broadcast-channels-plan.md D839).
    this.pollGuest(Date.now(), true);
  };
  private socket: ChatSocket | null = null;
  readonly lang: Lang;
  readonly t: Dict;
  /** One worker drains this — sends must stay in composed order, because
   * reconcile adopts by nonce and patches in place. */
  private sendQueue: SendIntent[] = [];
  private sending = false;
  /** The cross on the pill over the composer (chat-plan.md D398): the send
   * whose attachments are going up, by its first nonce, and the stop that
   * calls it off. Set and taken by `sendNow` around its attachment loop. */
  private uploadStop: { nonce: string; stop: AbortController } | null = null;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  /** Armed for the nearest deadline among the messages this client holds a
   * copy of (D298); null while none of them is going anywhere. */
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private toastTimers = new Set<ReturnType<typeof setTimeout>>();
  /** Link cards (D280, D281): the two lines their requests wait in, and what
   * is queued or in the air in each — the dedupe, kept apart from the state
   * so that a `loading` a torn-down runtime left behind is asked again. */
  private readonly previewLane = new Lane(PREVIEW_LANE_WIDTH);
  private readonly cardLane = new Lane(CARD_LANE_WIDTH);
  private readonly previewsAsked = new Set<string>();
  private readonly cardsAsked = new Set<string>();
  /** Channels an invite link is joining right now: a second press waits. */
  private readonly linkJoins = new Set<number>();
  /** The broadcast links being resolved right now (D859, D871), by what they
   * name: a second press of the same link waits for the first. */
  private readonly broadcastOpens = new Set<string>();
  /** Which window no frame reaches last asked what is new in it, and when
   * (D839): another window starts the clock again — it has just read what
   * there was. */
  private guestPolled: { channel: number; at: number } | null = null;
  /** Invalidates a thread's page in the air when another opens (D846). */
  private threadGeneration = 0;
  /** Per chat, the number of the `/` list last asked for (pets-plan.md D360):
   * an answer to an older ask — or to one a pet's move dropped since — is
   * thrown away rather than written over the newer truth. */
  private readonly commandAsks = new Map<number, number>();
  private commandAskSeq = 0;
  private lastActivityMs = Date.now();
  /** Callbacks for the host layer (sounds, notifications) — F3 wires them. */
  onRing: ((start: boolean) => void) | null = null;

  constructor(
    private readonly host: RuntimeHost = {
      transition: (_kind, commit) => commit(),
    },
  ) {
    this.store = createStore<UiState>(() => freeze(initialState(), true));
    this.lang = detectLang();
    this.t = dict(this.lang);
    // The server words its refusals in the window's language (D339).
    api.setRequestLang(this.lang);
  }

  /** Bootstrap owns this registration; rerendering and route mounts are harmless. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.disposed = false;
    try {
      // A word this build does not know leaves the default: a tab saved by a
      // newer build is not one this one can draw. `pets` is kept even before
      // `/v1/me` has said whether the server knows pets — the sidebar draws
      // All in its place until it does, and for good on a server that does
      // not (pets-plan.md §10).
      const tab = localStorage.getItem("echofloat.tab");
      if (
        tab === "all" ||
        tab === "channels" ||
        tab === "broadcasts" ||
        tab === "friends" ||
        tab === "pets"
      ) {
        this.mutate((s) => {
          s.tab = tab;
        });
      }
    } catch {
      /* Storage can be unavailable; navigation still works in memory. */
    }
    this.unregisterUnauthorized =
      api.setUnauthorizedHandler(() => {
        if (!inReauthWindow()) void this.logout();
      }) ?? null;
    this.unwatchPeople = people.onWanted(() => this.wakeLookups());
    window.addEventListener("pointerdown", this.activityListener, {
      passive: true,
    });
    window.addEventListener("keydown", this.activityListener, { passive: true });
    document.addEventListener("visibilitychange", this.visibilityListener);
  }

  /** Technical teardown keeps the stored login. A subsequent resume reconnects. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    endLifetime();
    this.releaseResources();
    this.unregisterUnauthorized?.();
    this.unregisterUnauthorized = null;
    this.unwatchPeople?.();
    this.unwatchPeople = null;
    if (this.started) {
      window.removeEventListener("pointerdown", this.activityListener);
      window.removeEventListener("keydown", this.activityListener);
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.started = false;
    }
    this.mutate((s) => {
      s.connected = false;
      s.busy = false;
      s.voice = null;
      s.ringing = null;
      s.toasts = [];
    });
  }

  private transition(kind: RuntimeTransitionKind, commit: () => void): void {
    const lifetime = currentLifetime();
    this.host.transition(kind, () => {
      if (isCurrent(lifetime)) commit();
    });
  }

  /** UI commands report errors only to their originating account. Multi-step
   * commands use check() before issuing another request; apply never runs late. */
  private async sessionAction<T>(
    request: (check: () => void) => Promise<T>,
    apply?: (result: T) => void,
  ): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const result = await request(() => assertCurrent(lifetime));
      if (isCurrent(lifetime)) apply?.(result);
    } catch (error) {
      if (isCurrent(lifetime)) throw error;
    }
  }

  private mutate(fn: (draft: UiState) => void): void {
    const before = this.state;
    let next = produce(before, fn);
    // Disappearing messages (D298): whatever a change brought in — a history
    // page, which the gateway does not filter (D297); a served row's line; an
    // edit's frame that raced the deadline — nothing already
    // past its deadline is ever published, and the timer stands for the
    // nearest deadline of what is. Structural sharing says when to look: the
    // copies that carry a deadline, and the clock they are read against, can
    // only have changed if one of these three did.
    const timed =
      next.feed.bubbles !== before.feed.bubbles ||
      next.rooms !== before.rooms ||
      next.clock !== before.clock;
    if (timed) next = this.withoutExpired(next);
    this.store.setState(next, true);
    if (timed) this.armExpiry();
    if (next.rooms !== before.rooms) this.askLines();
  }

  /** Every sidebar line left blank with nothing on this client to put on it
   * asks the gateway which message is newest now (D468): its newest page, the
   * reader's own — without what they hid or cleared, with the service lines.
   * Once per line, however many deletions follow while the question is out;
   * claimed in a publish of its own, outside the one that blanked the line,
   * because a recipe asks nothing of the network. */
  private askLines(): void {
    if (!mut.hasLineQuestion(this.state.rooms)) return;
    let asked: mut.LineQuestion[] = [];
    const next = produce(this.state, (s) => {
      asked = mut.askLines(s.rooms);
    });
    this.store.setState(next, true);
    for (const q of asked) void this.refillLine(q);
  }

  /** One line's question, asked again while the chat moves under it. A
   * refusal leaves the line blank until the chat moves again or the next
   * row served quotes its newest message. */
  private async refillLine(q: mut.LineQuestion): Promise<void> {
    const lifetime = currentLifetime();
    let epoch = q.epoch;
    for (let round = 0; round < LINE_ROUNDS; round += 1) {
      let page: Message[] | null;
      try {
        page = (await this.pageOf(q.channelId, { limit: LINE_PAGE })).messages;
      } catch {
        page = null;
      }
      if (!isCurrent(lifetime)) return;
      const out: { answer: mut.LineAnswer } = { answer: { t: "settled" } };
      this.mutate((s) => {
        const now = Date.now();
        out.answer = mut.answerLine(
          s.rooms,
          q.channelId,
          epoch,
          page,
          mut.serverNow(s.clock, now),
          now,
          this.t,
        );
      });
      if (out.answer.t !== "again") return;
      epoch = out.answer.epoch;
    }
    this.mutate((s) => mut.dropLineQuestion(s.rooms, q.channelId));
  }

  private newSocket(): ChatSocket {
    const lifetime = currentLifetime();
    const socket = new ChatSocket((event) => {
      if (isCurrent(lifetime) && this.socket === socket) this.onSocketEvent(event);
    });
    return socket;
  }

  private releaseResources(): void {
    this.channelGeneration += 1;
    this.cardGeneration += 1;
    this.requestedChannel = null;
    this.resumedLifetime = null;
    this.socket?.dispose();
    this.socket = null;
    this.stopTick();
    this.stopExpiry();
    for (const timer of this.toastTimers) clearTimeout(timer);
    this.toastTimers.clear();
    this.previewLane.clear();
    this.cardLane.clear();
    this.previewsAsked.clear();
    this.cardsAsked.clear();
    this.linkJoins.clear();
    this.broadcastOpens.clear();
    this.guestPolled = null;
    this.threadGeneration += 1;
    this.commandAsks.clear();
    // The next account's refusal is its own to wait out.
    if (this.syncAgain.timer !== null) clearTimeout(this.syncAgain.timer);
    this.syncAgain.timer = null;
    this.syncAgain.waiting = false;
    this.syncAgain.tries = 0;
    this.syncGeneration += 1;
    this.restReading = Promise.resolve();
    this.listed = new Set();
    this.wentChats = [];
    this.aheadPages.clear();
    this.aheadAsked = false;
    this.aheadMoved = null;
    people.forgetWanted();
    if (this.peopleTimer !== null) clearTimeout(this.peopleTimer);
    this.peopleTimer = null;
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this.namesOut.clear();
    this.sendQueue = [];
    this.sending = false;
    this.uploadStop = null;
    this.voice?.leave();
    this.voice = null;
    this.sharePainterOwner = null;
    this.onRing?.(false);
  }

  /** Logout/account replacement invalidates I/O before any synchronous close callback. */
  private resetSession(): void {
    endLifetime();
    this.releaseResources();
    setSession(null);
    this.store.setState(freeze(initialState(), true), true);
  }

  // -------------------------------------------------------------------------
  // Login / logout

  async login(username: string, password: string): Promise<void> {
    this.start();
    this.resetSession();
    await this.authenticate(username, password);
  }

  private async authenticate(username: string, password: string): Promise<void> {
    const lifetime = currentLifetime();
    if ("Notification" in window && Notification.permission === "default") {
      void Notification.requestPermission();
    }
    this.mutate((s) => {
      s.busy = true;
      s.notice = null;
    });
    try {
      const { jwt } = await api.login(username, password);
      if (!isCurrent(lifetime)) return;
      setSession({ jwt, userId: 0, base: "" });
      this.mutate((s) => {
        s.selfUsername = username;
      });
      // Best-effort identity: a gateway too old to know /v1/me still logs in.
      // Waited for a little when it has too much in work (D1075): it answered
      // the sign-in, and a window with no account in it is worse than a card
      // that takes a few seconds longer.
      try {
        const me = await api.patiently("requests", SIGN_IN_BUSY_PATIENCE, () => api.fetchMe());
        if (!isCurrent(lifetime)) return;
        setSession({ jwt, userId: me.user_id, base: "" });
        this.adoptMe(me, true);
        // D311: the gateway refuses the socket too; the window shows the
        // address card, and `confirmAddress` opens the socket afterwards.
        if (me.must_confirm_email === true) return;
      } catch {
        if (!isCurrent(lifetime)) return;
        // The first answer of the sync that follows brings the account
        // (docs/fast-start.md D1053).
      }
      this.startSocket();
    } catch (error) {
      if (isCurrent(lifetime)) throw error;
    } finally {
      if (isCurrent(lifetime)) {
        this.mutate((s) => {
          s.busy = false;
        });
      }
    }
  }

  async register(username: string, email: string, password: string): Promise<void> {
    this.start();
    this.resetSession();
    const lifetime = currentLifetime();
    this.mutate((s) => {
      s.busy = true;
      s.notice = null;
    });
    try {
      await api.register(username, email, password);
      if (!isCurrent(lifetime)) return;
      await this.authenticate(username, password);
      if (!isCurrent(lifetime)) return;
    } catch (error) {
      if (isCurrent(lifetime)) throw error;
    } finally {
      if (isCurrent(lifetime)) {
        this.mutate((s) => {
          s.busy = false;
        });
      }
    }
  }

  // -------------------------------------------------------------------------
  // Confirming an address (gateway/src/confirm.rs)
  //
  // Each step is its own method rather than one state machine here, because
  // the state between them — which screen, which ticket, what was typed — is
  // the login card's and nothing else in the app has a use for it. What the
  // runtime owns is the two things a view must not: the `busy` flag every
  // control reads, and the lifetime check that keeps a reply from a signed-out
  // session out of the store.

  private async step<T>(work: () => Promise<T>): Promise<T> {
    const lifetime = currentLifetime();
    this.mutate((s) => {
      s.busy = true;
      s.notice = null;
    });
    try {
      return await work();
    } finally {
      if (isCurrent(lifetime)) {
        this.mutate((s) => {
          s.busy = false;
        });
      }
    }
  }

  /** Submits the sign-up form. Nothing exists afterwards but a ticket and a
   * letter — see {@link confirmRegistration}. */
  registerStart(username: string, email: string, password: string): Promise<api.CodeSent> {
    this.start();
    return this.step(() => api.registerStart(username, email, password, this.lang));
  }

  /** The account appears here, and the sign-in that follows is the same one
   * one-step registration always did. */
  async confirmRegistration(
    ticket: string,
    code: string,
    username: string,
    password: string,
  ): Promise<void> {
    this.start();
    await this.step(() => api.registerConfirm(ticket, code));
    this.resetSession();
    await this.authenticate(username, password);
  }

  /** «Send it again» — the same code, for as long as it lives. */
  resendCode(ticket: string): Promise<api.CodeSent> {
    return this.step(() => api.resendCode(ticket, this.lang));
  }

  /** `login` is an address or a username. Answers the same whether or not it
   * names an account here, which is why nothing branches on the result. */
  passwordForgot(login: string): Promise<api.CodeSent> {
    this.start();
    return this.step(() => api.passwordForgot(login, this.lang));
  }

  passwordVerify(ticket: string, code: string): Promise<{ reset_token: string }> {
    return this.step(() => api.passwordVerify(ticket, code));
  }

  /** Sets the password and ends every session the account had — including,
   * if the person was somehow signed in here, this one. The card comes back
   * to sign-in either way. */
  async passwordReset(resetToken: string, password: string): Promise<void> {
    await this.step(() => api.passwordReset(resetToken, password));
  }

  /** Resume from a stored session (page load). Identity is re-adopted the
   * same best-effort way login does it — a reloaded page otherwise holds an
   * empty username, and the first profile save would send it. */
  resume(): boolean {
    this.start();
    const s = session();
    if (!s) return false;
    const lifetime = currentLifetime();
    if (this.resumedLifetime === lifetime) return true;
    this.resumedLifetime = lifetime;
    void api
      .patiently("requests", SIGN_IN_BUSY_PATIENCE, () => api.fetchMe())
      .then((me) => {
        if (!isCurrent(lifetime)) return;
        this.adoptMe(me, true);
        // D311: the gateway refuses this account's socket until it proves an
        // address, and a socket that is refused retries forever. The window
        // shows the address card; `confirmAddress` opens a fresh one.
        if (me.must_confirm_email === true) {
          this.socket?.dispose();
          this.socket = null;
        }
      })
      .catch(() => {});
    // Not held back for the answer above: a reload of a confirmed account —
    // every reload but the rare one — reconnects without the extra round trip.
    this.startSocket();
    return true;
  }

  async logout(): Promise<void> {
    this.resetSession();
  }

  /** «Sign out» pressed (docs/signins.md D497, D502): the gateway is told —
   * this browser's sign-in is a line on the account's list of sessions, and
   * would otherwise stay there until its day ran out — and the session is
   * forgotten here at once, whatever it answers. Not what a refused token
   * calls ({@link Runtime.logout}): the gateway has already ended that one. */
  async signOut(): Promise<void> {
    api.logout();
    await this.logout();
  }

  // -------------------------------------------------------------------------
  // An account that has not proven its address (D311)

  /** Mails a code to the address the person typed — which may differ from
   * the one on record — once the gateway has the account's password
   * (docs/signins.md D499). */
  addressStart(email: string, password: string): Promise<api.CodeSent> {
    return this.step(() => api.addressStart(email, password, this.lang));
  }

  /** Writes the address with its proof, lifts the gate, and opens the app:
   * the same token works from here on, nothing is signed in again. */
  async confirmAddress(ticket: string, code: string): Promise<void> {
    const lifetime = currentLifetime();
    const me = await this.step(() => api.addressConfirm(ticket, code));
    if (!isCurrent(lifetime)) return;
    this.adoptMe(me);
    this.startSocket();
  }

  // -------------------------------------------------------------------------
  // Changing a proven address (D312)
  //
  // Which step is open, which ticket and which grant are the dialog's own
  // (`views/change-email.tsx`), for the reason the login card keeps its: nothing
  // else in the app has a use for them. Not through `step`: its `busy` is the
  // login card's, and a flag that greys the whole window has no business
  // here. What the runtime adds is the language the letters are written in,
  // and the toast at the end.

  /** The address on record as the gateway masks it, or `null` for none. */
  async emailOnRecord(): Promise<string | null> {
    return (await api.fetchEmailOnRecord()).email;
  }

  emailChangeStart(): Promise<api.CodeSent> {
    return api.emailChangeStart(this.lang);
  }

  /** Spends the old mailbox's code; the grant it buys is what every letter to
   * a new address is paid with. */
  async emailChangeVerify(ticket: string, code: string): Promise<string> {
    return (await api.emailChangeVerify(ticket, code)).change_token;
  }

  emailChangeNew(changeToken: string, email: string): Promise<api.CodeSent> {
    return api.emailChangeNew(changeToken, email, this.lang);
  }

  /** Writes the new address, then says so the way the app says things that
   * happened: a toast, which outlives the dialog closing under it. */
  async emailChangeConfirm(ticket: string, code: string): Promise<void> {
    const lifetime = currentLifetime();
    await api.emailChangeConfirm(ticket, code);
    if (isCurrent(lifetime)) this.pushToast(this.t.emailChanged(), null);
  }

  /** `probe` for the answer of `GET /v1/me` itself, and the same account on
   * `GET /v1/bootstrap` (docs/fast-start.md D1053) — the ones that carry `pets`,
   * the capability probe (pets-plan.md §10). Every other route that answers a
   * UserJson (the profile, the avatar, the status) leaves the key out, and must
   * not be read as a server that has forgotten pets. */
  private adoptMe(me: import("../gen/dto").UserJson, probe = false): void {
    this.mutate((s) => {
      s.selfUserId = me.user_id;
      s.selfUsername = me.username;
      s.directory.selfId = me.user_id;
      s.account.displayName = me.display_name;
      s.account.bio = me.bio;
      s.account.avatarFileId = me.avatar_file_id;
      s.account.createdAt = me.created_at;
      s.account.bubbleLight = me.bubble_light;
      s.account.bubbleDark = me.bubble_dark;
      s.account.manualStatus = normalizeManual(me.manual_status);
      s.account.isAdmin = me.is_admin;
      if (probe) s.account.pets = me.pets ?? null;
      // The statistics switch (D437) rides `GET /v1/me`, the same account on
      // `GET /v1/bootstrap`, and `PATCH /v1/me` alone: absent from the first two,
      // it is a gateway without the switch; absent from any other answer, it says
      // nothing.
      if (probe) s.account.analyticsOff = me.analytics_off ?? null;
      else if (typeof me.analytics_off === "boolean") s.account.analyticsOff = me.analytics_off;
      s.mustConfirmEmail = me.must_confirm_email === true;
      s.directory.people[me.user_id] = {
        username: me.username,
        displayName: me.display_name,
        bio: me.bio,
        avatarFileId: me.avatar_file_id,
        createdAt: me.created_at,
        bubbleLight: me.bubble_light,
        bubbleDark: me.bubble_dark,
      };
    });
  }

  private startSocket(): void {
    const s = session();
    if (!s) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.socket?.dispose();
    this.socket = this.newSocket();
    this.resumedLifetime = currentLifetime();
    this.socket.start(`${proto}://${location.host}/v1/chat/ws`, s.jwt);
    this.startTick();
    // Every sign-in comes through here: the packs and the recents are read
    // now (docs/stickers-plan.md D560, D561), so the composer knows whether
    // this gateway has stickers before its door is first pressed.
    void this.loadStickers();
    // A teardown stood the expiry timer down and kept the feed (`dispose`):
    // what it holds still goes at its deadline, connected or not (D298).
    this.armExpiry();
  }

  // -------------------------------------------------------------------------
  // Socket events

  private onSocketEvent(e: SocketEvent): void {
    switch (e.t) {
      case "status":
        // No frame says what moves from here on (D1060).
        if (!e.connected) this.dropAllAhead();
        this.mutate((s) => {
          s.connected = e.connected;
        });
        return;
      case "ready": {
        this.dropAllAhead();
        this.mutate((s) => {
          // A fresh connection is "here" as far as the server is concerned.
          s.presence.selfAfk = false;
          if (mut.selfIdOf(s.directory) === 0) {
            s.directory.selfId = e.user_id;
            s.selfUserId = e.user_id;
          }
        });
        void this.sync();
        return;
      }
      case "auth_rejected": {
        // The address gate (D311, docs/fast-start.md D1089), which says nothing
        // against the token: the sign-in stands, and the window shows the address
        // card. A reload gets here, its socket going up before `/v1/me` answers;
        // `confirmAddress` opens a fresh one.
        if (e.reason === "email_unconfirmed") {
          this.socket?.dispose();
          this.socket = null;
          this.mutate((s) => {
            s.mustConfirmEmail = true;
          });
          return;
        }
        // The socket is handed its JWT once and cannot refresh one — except
        // right after a password change, where the fresh JWT exists and this
        // rejection is the revocation doing its job (R44).
        if (inReauthWindow()) {
          const s = session();
          if (s) {
            this.startSocket();
            return;
          }
        }
        void this.logout();
        this.mutate((s) => {
          s.notice = e.message;
        });
        return;
      }
      case "event":
        this.onServerEvent(e.event);
        return;
    }
  }

  private onServerEvent(e: ServerEvent): void {
    // Whatever moved in a chat, what was read ahead of its opening no longer
    // says what is there (D1060). A clearing and a chat going come through
    // `onHistoryCleared` and `onChannelDeleted`, which say so themselves.
    switch (e.t) {
      case "message":
      case "message_edited":
      case "message_deleted":
      case "message_expiring":
      case "reaction":
      case "comments":
        this.dropAhead(e.channel_id);
        break;
      default:
        break;
    }
    switch (e.t) {
      case "message":
        this.onMessage(e as unknown as Message & { t: string });
        return;
      case "message_edited":
        this.onEdited(e as unknown as Message & { t: string });
        return;
      case "message_deleted": {
        this.mutate((s) => this.forget(s, e.channel_id, e.seq));
        // A post deleted takes its comments with it (D846): its thread, open
        // here, gives way to the channel.
        const thread = this.state.thread;
        if (thread !== null && thread.channelId === e.channel_id && thread.seq === e.seq) {
          void this.closeThread();
        }
        return;
      }
      case "message_expiring":
        // Somebody other than the author has read them, so their clocks have
        // started (D302). Nothing is removed here: `mutate` runs the expiry
        // pass over every snapshot it makes and re-arms the timer from it, so
        // writing the deadlines is the whole of this.
        this.mutate((s) => {
          mut.armExpiry(s.feed, s.rooms, e.channel_id, e.messages);
        });
        return;
      case "reaction":
        this.mutate((s) => {
          if (s.feed.channelId === e.channel_id) {
            mut.onReactionEvent(
              s.feed,
              e.seq,
              e.user_id,
              e.emoji,
              e.reactions,
              mut.selfIdOf(s.directory),
            );
          }
        });
        return;
      case "comments":
        // A post's comment count moved (docs/broadcast-channels-plan.md
        // D849): the whole count, onto the post wherever the window holds it.
        this.mutate((s) => {
          if (s.feed.channelId === e.channel_id) mut.applyComments(s.feed, e.seq, e.count);
        });
        return;
      case "restricted": {
        // What the reader may not do in a channel moved (D853) — the frame
        // reaches the staff about everybody, and the reader about themselves.
        const now = this.serverNow();
        this.mutate((s) => {
          const mine = mut.roomsOnRestricted(
            s.rooms,
            e.channel_id,
            e.user_id,
            e.abilities ?? [],
            e.until_ms ?? null,
            mut.selfIdOf(s.directory),
          );
          // An edit or a reply open in this channel's thread ends as
          // commenting is taken (D923): both are writing, and the box gives
          // way to why — a mode left under it would keep Escape from closing
          // the thread (`lib/hotkeys`). An edit's words were the message's;
          // the draft from before it is the chat's.
          if (
            mine &&
            s.thread !== null &&
            s.thread.channelId === e.channel_id &&
            s.feed.channelId === s.thread.threadId &&
            s.feed.mode.t !== "new" &&
            mut.restrictedUntil(mut.roomOf(s.rooms, e.channel_id), "comment", now) !== null
          ) {
            if (s.feed.mode.t === "editing") s.feed.composerRevision += 1;
            s.feed.mode = { t: "new" };
          }
        });
        return;
      }
      case "typing":
        // Every chat's, not only the open one's: the list draws it on the
        // chat's row (D464).
        this.mutate((s) => {
          mut.onRemoteTyping(
            s.typing,
            e.channel_id,
            e.user_id,
            mut.selfIdOf(s.directory),
            Date.now(),
          );
        });
        return;
      case "read":
        // user_id is ignored: read marks only ever reach the reader's own
        // devices.
        this.applyReadMark(e.channel_id, e.last_read_seq);
        return;
      case "channel_created":
        this.mutate((s) => {
          mut.roomsOnCreated(
            s.rooms,
            e.channel_id,
            e.name,
            e.kind ?? "standard",
            e.peer_id ?? null,
            e.created_by,
            mut.selfIdOf(s.directory),
          );
          // The official dialog opened after a card for it came (D936, D943):
          // it rises for the card as the row it is.
          mut.raiseForNotices(s.rooms, s.directory, s.securityNotices);
        });
        return;
      case "channel_deleted":
        this.onChannelDeleted(e.channel_id);
        return;
      // A friendship ended over a conversation with something in it
      // (friends-plan.md D411): the row stays, read-only, on All alone.
      case "dm_ended":
        this.mutate((s) => {
          mut.roomsOnDmEnded(s.rooms, e.channel_id);
        });
        return;
      // The reader deleted a dialog for themselves on another device
      // (friends-plan.md D465): the history goes here too, and the row stays.
      case "history_cleared":
        this.onHistoryCleared(e.channel_id, e.through_seq);
        return;
      case "channel_renamed":
        this.mutate((s) => {
          mut.roomsOnUpdated(s.rooms, e);
          if (s.feed.channelId === e.channel_id) s.feed.channelName = e.name;
          // A broadcast channel speaks under its name as it is now (D841),
          // in its feed and in its threads alike.
          const voice = s.feed.voice;
          if (voice !== null && voice.channelId === e.channel_id) {
            voice.name = e.name;
            voice.avatarFileId = e.avatar_file_id;
            voice.verified = e.verified === true;
            mut.resolveNames(s.feed, s.directory, this.t);
          }
        });
        return;
      case "channel_settings":
        this.mutate((s) => {
          const row = mut.roomOf(s.rooms, e.channel_id);
          if (row) row.settings = e.settings;
        });
        return;
      case "membership": {
        // A pet came or went (pets-plan.md D360): the chat's `/` list is not
        // what it was. An account the directory has not heard of may be a
        // pet made a moment ago, so its frame counts too.
        const who = this.state.directory.people[e.user_id];
        // Put back into a chat this tab does not hold (channel-admin-plan.md
        // D487): a group was invisible to us until the ban ended (D114), so
        // the frame has no row to land on — the row comes whole from the
        // state, as after a join by invite.
        const self = mut.selfIdOf(this.state.directory);
        const unheld =
          self !== 0 &&
          e.user_id === self &&
          e.change !== "left" &&
          !mut.roomOf(this.state.rooms, e.channel_id);
        if (unheld) void this.sync();
        this.mutate((s) => {
          mut.roomsOnMembership(
            s.rooms,
            e.channel_id,
            e.user_id,
            e.change,
            e.admin,
            mut.selfIdOf(s.directory),
            e.rights ?? [],
          );
        });
        if (!who || isPet(who)) this.dropCommands([e.channel_id]);
        return;
      }
      case "profile_updated": {
        // Somebody this client has not met stays unmet (docs/fast-start.md
        // D1056): the frame reaches every socket on the stand.
        if (!this.state.directory.people[e.user_id]) return;
        // Written inside the draft below, so declared through `as`.
        let moved = null as mut.PetMove | null;
        this.mutate((s) => {
          // The whole entry, a pet's three keys with it (D349): rebuilt
          // without them, the next frame about a pet would turn it back into
          // a person.
          moved = mut.applyDirectoryUpdate(s.directory, {
            user_id: e.user_id,
            username: e.username,
            display_name: e.display_name,
            bio: e.bio,
            avatar_file_id: e.avatar_file_id,
            created_at: e.created_at,
            bubble_light: e.bubble_light,
            bubble_dark: e.bubble_dark,
            kind: e.kind,
            pet_rev: e.pet_rev,
            retired: e.retired,
            official: e.official,
          });
          if (mut.isSelf(s.directory, e.user_id)) {
            s.selfUsername = e.username;
            s.account.displayName = e.display_name;
            s.account.avatarFileId = e.avatar_file_id;
            s.account.bubbleLight = e.bubble_light;
            s.account.bubbleDark = e.bubble_dark;
          }
          this.resyncNames(s);
        });
        if (moved) this.onPetsMoved([moved]);
        return;
      }
      case "seen": {
        // Somebody else has read our messages in `(after, seq]` (D422): the
        // readers kept for those are asked for again at their next opening,
        // and the checks turn (D423). A gateway older than `after` says
        // nothing of where the mark was, which reads as all of them. A frame
        // that changes nothing publishes nothing.
        const after = e.after ?? 0;
        this.mutate((s) => {
          mut.readsSeen(s.reads, e.channel_id, after, e.seq);
          mut.roomsOnSeen(s.rooms, e.channel_id, e.seq);
        });
        return;
      }
      case "pet_stopped":
        // Stop, or `/restart` taking it back (D368) — from any of the
        // reader's devices, this one included.
        this.mutate((s) => {
          mut.roomsOnPetStopped(s.rooms, e.channel_id, e.stopped);
        });
        return;
      case "call_started":
        this.onCallStarted(e);
        return;
      case "call_participants":
        this.mutate((s) => {
          mut.callsApply(s.calls, e);
          const row = mut.roomOf(s.rooms, e.channel_id);
          if (row) mut.setRoomCall(row, e.participants, s.directory);
        });
        return;
      case "call_ended": {
        const stopRing = this.state.ringing?.channelId === e.channel_id;
        this.mutate((s) => {
          mut.callsEnd(s.calls, e.channel_id);
          const row = mut.roomOf(s.rooms, e.channel_id);
          if (row) mut.setRoomCall(row, [], s.directory);
          if (s.ringing?.channelId === e.channel_id) {
            s.ringing = null;
          }
        });
        if (stopRing) this.onRing?.(false);
        return;
      }
      case "call_declined": {
        const stopRing = this.state.ringing?.channelId === e.channel_id;
        this.mutate((s) => {
          // Our own other device declined: stop ringing here too.
          if (s.ringing?.channelId === e.channel_id) {
            s.ringing = null;
          }
        });
        if (stopRing) this.onRing?.(false);
        return;
      }
      case "friends":
        this.onFriends(e);
        return;
      case "presence":
        this.onPresence(e.user_id, e.status);
        return;
      case "presence_sync":
        this.mutate((s) => {
          mut.applyPresenceSync(s.presence, e.presence);
        });
        void this.recheckCard();
        return;
      case "my_presence":
        // Only the manual half is folded — `status` is derived from manual
        // plus THIS machine's idleness, and this machine is the authority.
        this.mutate((s) => {
          s.account.manualStatus = normalizeManual(e.manual);
        });
        return;
      case "security_notice":
        this.onSecurityNotice(e);
        return;
      case "error":
        this.mutate((s) => {
          s.notice = e.message;
        });
        return;
      default:
        return;
    }
  }

  private onMessage(m: Message): void {
    // A post of the reader's own is served to them with the person behind it
    // (`posted_by`, D842) and `sender_id` 0: it is still theirs.
    const own = mut.isSelf(this.state.directory, mut.authorOf(m));
    let lost = false;
    this.mutate((s) => {
      // A live frame is handed over the moment the message is stored: one
      // reading of the gateway's clock (D298).
      mut.noteClockSample(s.clock, m.sent_at_ms, Date.now());
      mut.roomsOnMessage(s.rooms, m, s.selectedChannel, own, Date.now(), this.t);
      // What they were typing has arrived: their line goes with it (D464).
      mut.typingStopped(s.typing, m.channel_id, m.sender_id);
      if (s.feed.channelId === m.channel_id) {
        lost = mut.reconcile(s.feed, stripTag(m), this.t);
        // Our own message is read where it lands. Short of the end it has landed
        // nowhere, and what lies between was not read for having written this: a
        // send from here is on its way to the end, whose arrival says so
        // (`comeBack`), and a send from another device is that device's to say,
        // which it does in a `read` frame (docs/fast-start.md D1091).
        if (own && s.feed.reachedEnd) s.feed.lastReadSeq = Math.max(s.feed.lastReadSeq, m.seq);
        mut.resolveNames(s.feed, s.directory, this.t);
      }
    });
    if (lost) void this.closeGap();
    // The notification gate: own → no; service → no, but for the official
    // pet's — a promo code waiting raises its banner as an announcement does
    // (docs/official-pet-plan.md D981); visible open channel → no. A hidden
    // tab notifies even for the open channel — hidden is hidden.
    const official = isOfficial(this.state.directory.people[m.sender_id]);
    if (own || (m.service?.kind !== undefined && !official)) return;
    // A comment (docs/broadcast-channels-plan.md D861) is news only to whom it
    // answers or names: a toast, never the chat's sound or a notification for
    // every comment of every channel.
    if (m.thread_of != null) {
      this.onComment(m, m.thread_of.channel_id);
      return;
    }
    // The tap (D341): every message but one the reader is looking at — the
    // open channel in a tab that is visible *and* focused, the same «in
    // front» the read marks go by. A visible tab behind another window is not
    // being looked at.
    if (!(this.canRead() && this.state.selectedChannel === m.channel_id)) {
      sounds.play("message");
    }
    const visibleHere = !document.hidden && this.state.selectedChannel === m.channel_id;
    if (visibleHere) return;
    if ("Notification" in window && Notification.permission === "granted") {
      this.named(m.sender_id, () => {
        const room = this.state.rooms.find((r) => r.channelId === m.channel_id);
        const sender = mut.nameOf(this.state.directory, m.sender_id);
        // A post is the channel's own words (D841): the channel is the title.
        const title =
          m.as_channel === true && room
            ? room.name
            : room && room.kind !== "dm"
              ? `${sender} — ${room.name}`
              : sender;
        const n = new Notification(title, {
          // A notification is a string and cannot blur, so what a spoiler
          // hides goes as speckle (D343).
          body: maskedPreview(mut.preview(m, this.t)),
          tag: `echofloat-${m.channel_id}`,
        });
        n.onclick = () => {
          window.focus();
          this.openChannel(m.channel_id);
          n.close();
        };
      });
    }
  }

  /** A comment under a post of `channelId` (docs/broadcast-channels-plan.md
   * D861): a toast for the writer of the comment it answers — as far as the
   * reader's copy names them — and for whoever it mentions, a mention passing
   * nothing here, since the web keeps no mute. Pressed, it opens the thread.
   * Nobody else hears of it: a channel is not noise. */
  private onComment(m: Message, channelId: number): void {
    const self = mut.selfIdOf(this.state.directory);
    if (self === 0) return;
    const quote = m.reply_to_seq != null ? m.reply_to : null;
    const answersMe = quote?.state === "live" && quote.sender_id === self;
    const namesMe = (m.entities ?? []).some((e) => e.type === "mention" && e.user_id === self);
    if (!answersMe && !namesMe) return;
    const open = this.state.thread?.threadId === m.channel_id && !document.hidden;
    if (open) return;
    const room = mut.roomOf(this.state.rooms, channelId);
    const name = room?.name ?? "";
    sounds.play("message");
    const seq = m.thread_of?.seq;
    const thread = seq === undefined ? undefined : { channelId, seq, threadId: m.channel_id };
    this.pushToast(this.t.commentReplyToast(name), null, thread);
    // A tab nobody is looking at gets the system notification as well, as a
    // message does: a toast inside a hidden page is seen by no one.
    if (
      (document.hidden || !document.hasFocus()) &&
      "Notification" in window &&
      Notification.permission === "granted"
    ) {
      const n = new Notification(this.t.commentReplyToast(name), {
        body: maskedPreview(mut.preview(m, this.t)),
        tag: `echofloat-thread-${m.channel_id}`,
      });
      n.onclick = () => {
        window.focus();
        if (thread !== undefined) void this.openThread(thread);
        else this.openChannel(channelId);
        n.close();
      };
    }
  }

  private onEdited(m: Message): void {
    let lost = false;
    this.mutate((s) => {
      // The post a thread is open under, as it now stands: its words, and
      // whether it still takes comments (docs/broadcast-channels-plan.md D851).
      const thread = s.thread;
      if (thread !== null && thread.channelId === m.channel_id && thread.seq === m.seq) {
        thread.post = stripTag(m);
      }
      if (s.feed.channelId === m.channel_id) {
        lost = mut.reconcile(s.feed, stripTag(m), this.t);
        // A call widget grows a participant on every edit; the roster is
        // resolved in resolveNames.
        mut.resolveNames(s.feed, s.directory, this.t);
      }
      mut.roomsOnEdited(s.rooms, m, this.t);
    });
    if (lost) void this.closeGap();
  }

  private onChannelDeleted(channelId: number): void {
    if (this.requestedChannel === channelId) this.cancelChannelNavigation();
    const wasSelected = this.state.selectedChannel === channelId;
    this.wentChats.push(channelId);
    this.dropAhead(channelId);
    this.commandAsks.delete(channelId);
    this.mutate((s) => {
      const at = s.rooms.findIndex((r) => r.channelId === channelId);
      if (at !== -1) s.rooms.splice(at, 1);
      if (wasSelected) s.selectedChannel = null;
      mut.callsEnd(s.calls, channelId);
      delete s.commands[channelId];
    });
    if (wasSelected) {
      const next = mut.firstListed(this.state.rooms)?.channelId;
      if (next !== undefined) {
        void this.selectChannel(next);
      } else {
        this.mutate((s) => {
          mut.openFeed(s.feed, 0, "", 0, 0);
          s.feed.loading = false;
        });
      }
    }
  }

  /** The reader deleted this chat for themselves up to `throughSeq`
   * (friends-plan.md D465) — on this device, or on another (the
   * `history_cleared` frame). The chat is cleared, not gone: its row stays
   * where it is, emptied, and a forward waiting with one of its messages lets
   * it go. An open chat is read again in place from the gateway, which serves
   * nothing up to the mark, so the reader stays in it and sees it empty.
   * Idempotent — the answer and its frame both come through here, and the
   * second finds the mark where it is. */
  private onHistoryCleared(channelId: number, throughSeq: number): void {
    let moved = false;
    this.dropAhead(channelId);
    this.mutate((s) => {
      moved = mut.roomsOnHistoryCleared(s.rooms, channelId, throughSeq);
      if (!moved) return;
      const draft = s.forward;
      if (draft !== null && draft.from === channelId) {
        let left: typeof draft | null = draft;
        for (const m of draft.messages) {
          if (m.seq <= throughSeq) left = mut.forwardWithout(left, channelId, m.seq);
        }
        s.forward = left;
      }
    });
    if (moved && this.state.selectedChannel === channelId) {
      void this.selectChannel(channelId, true);
    }
  }

  private onCallStarted(call: import("../gen/dto").Call): void {
    let startRing = false;
    this.mutate((s) => {
      mut.callsApply(s.calls, call);
      const row = mut.roomOf(s.rooms, call.channel_id);
      if (row) mut.setRoomCall(row, call.participants, s.directory);
      // Ring gate: not our own start (another device is normal), and only
      // for channels we are a member of.
      const own = mut.isSelf(s.directory, call.started_by);
      if (own || !row?.member) return;
      s.ringing = {
        channelId: call.channel_id,
        starterId: call.started_by,
        starterName: mut.nameOf(s.directory, call.started_by),
        channelName: row.name,
        sinceMs: Date.now(),
      };
      startRing = true;
    });
    if (startRing) this.onRing?.(true);
  }

  private onFriends(e: import("../gen/dto").FriendsSnapshot): void {
    let news: mut.FriendNews[] = [];
    this.mutate((s) => {
      news = mut.applyFriendsSnapshot(s.friends, e);
    });
    // The two quiet notifications (D174): derived by diffing, never claimed
    // by the frame; the request one opens the sender's card.
    for (const item of news) {
      // A stranger asking is the commonest somebody not met (D1057).
      this.named(item.userId, () => {
        const name = mut.nameOf(this.state.directory, item.userId);
        this.pushToast(
          item.t === "requested" ? this.t.toastRequest(name) : this.t.toastAccepted(name),
          item.t === "requested" ? item.userId : null,
        );
      });
    }
    void this.recheckCard();
  }

  private noticeSeq = 0;

  /**
   * A sign-in of the account, told to this page while it is online
   * (docs/official-pet-plan.md D940). Its card lands at the foot of the
   * official dialog, which rises in the list (D943) — always, «Не беспокоить»
   * or not. Unless the account is in «Не беспокоить» (D946), which keeps the
   * card silent (no letter goes either, D1002), the alarm goes with it: a browser
   * notification when the page may show one, a toast that opens the dialog,
   * and the message tap. In the page and nowhere else (D941): a reload and it
   * is gone.
   */
  private onSecurityNotice(e: Extract<ServerEvent, { t: "security_notice" }>): void {
    const notice: SecurityNotice = {
      id: ++this.noticeSeq,
      kind: e.kind,
      signin: e.signin ?? null,
      atMs: e.at_ms,
      state: "waiting",
    };
    this.mutate((s) => {
      s.securityNotices.unshift(notice);
      mut.raiseForNotices(s.rooms, s.directory, s.securityNotices);
    });
    if (this.state.account.manualStatus === "dnd") return;
    const text = mut.noticeKnown(notice)
      ? this.t.noticeToast(mut.noticeDevice(notice, this.t))
      : this.t.noticeUnknown();
    const room = mut.officialRoomOf(this.state.rooms, this.state.directory);
    sounds.play("message");
    this.pushToast(text, null, undefined, room?.channelId);
    if ("Notification" in window && Notification.permission === "granted") {
      const pet = mut.officialPetOf(this.state.directory);
      const n = new Notification(
        pet !== null ? mut.nameOf(this.state.directory, pet) : "EchoFloat",
        { body: text, tag: `echofloat-security-${notice.signin?.id ?? notice.atMs}` },
      );
      n.onclick = () => {
        window.focus();
        if (room) this.openChannel(room.channelId);
        n.close();
      };
    }
  }

  /** «Это я» (D942), or «Сменить пароль» once the session is ended: the card
   * goes, and the official row goes back where its messages put it. */
  dismissNotice(id: number): void {
    this.mutate((s) => {
      const at = s.securityNotices.findIndex((n) => n.id === id);
      if (at === -1) return;
      s.securityNotices.splice(at, 1);
      mut.raiseForNotices(s.rooms, s.directory, s.securityNotices);
    });
  }

  /** «Завершить сеанс», answered yes (D942): the sign-in the card names ends
   * (`DELETE /v1/me/signins/{id}`, D504 — a 404 is done as well) and the card
   * says so. A refusal puts the card back as it was and says why. */
  async endNoticeSignin(id: number): Promise<void> {
    const notice = this.state.securityNotices.find((n) => n.id === id);
    const signin = notice?.signin?.id;
    if (!notice || notice.state !== "waiting" || signin === undefined) return;
    const set = (state: SecurityNotice["state"]) =>
      this.mutate((s) => {
        const card = s.securityNotices.find((n) => n.id === id);
        if (card) card.state = state;
        mut.raiseForNotices(s.rooms, s.directory, s.securityNotices);
      });
    set("ending");
    try {
      await this.sessionAction(
        () => api.endSignin(signin),
        () => set("ended"),
      );
    } catch (e) {
      set("waiting");
      this.showNotice(e instanceof Error ? e.message : String(e));
    }
  }

  private toastSeq = 0;

  private pushToast(
    text: string,
    userId: number | null,
    thread?: ThreadRef,
    channelId?: number,
  ): void {
    const lifetime = currentLifetime();
    const id = ++this.toastSeq;
    this.transition("toast", () => {
      this.mutate((s) => {
        const toast: Toast = { id, text, userId };
        if (thread !== undefined) toast.thread = thread;
        if (channelId !== undefined) toast.channelId = channelId;
        s.toasts.push(toast);
      });
    });
    const timer = setTimeout(() => {
      this.toastTimers.delete(timer);
      if (!isCurrent(lifetime)) return;
      this.transition("toast", () => {
        this.mutate((s) => {
          const at = s.toasts.findIndex((x) => x.id === id);
          if (at !== -1) s.toasts.splice(at, 1);
        });
      });
    }, 6000);
    this.toastTimers.add(timer);
  }

  dismissToast(id: number): void {
    this.transition("toast", () => {
      this.mutate((s) => {
        const at = s.toasts.findIndex((x) => x.id === id);
        if (at !== -1) s.toasts.splice(at, 1);
      });
    });
  }

  private onPresence(userId: number, status: import("../gen/dto").PublicStatus): void {
    const before =
      this.state.card.userId !== null
        ? mut.statusOf(this.state.presence, this.state.card.userId) !== null
        : null;
    this.mutate((s) => {
      mut.applyPresenceFrame(s.presence, userId, status);
    });
    const after =
      this.state.card.userId !== null
        ? mut.statusOf(this.state.presence, this.state.card.userId) !== null
        : null;
    // Only the here↔gone crossing re-reads the relation (D192/D193).
    if (this.state.card.userId !== null && before !== after) void this.recheckCard();
  }

  private async recheckCard(): Promise<void> {
    const lifetime = currentLifetime();
    const userId = this.state.card.userId;
    const generation = this.cardGeneration;
    if (userId === null) return;
    // A pet is nobody's friend (pets-plan.md D364): what its card shows is its
    // own card (D362), read fresh the same way — on a server that says it
    // knows pets; elsewhere it is drawn as a person, as §10 has it.
    if (this.state.account.pets !== null && isPet(this.state.directory.people[userId])) {
      try {
        const pet = await api.fetchPet(userId);
        if (!isCurrent(lifetime)) return;
        this.mutate((s) => {
          if (generation === this.cardGeneration && s.card.userId === userId) s.card.pet = pet;
        });
      } catch {
        // The card keeps what the directory says; a re-open retries.
      }
      return;
    }
    try {
      const relation = await api.fetchFriendship(userId);
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        if (generation === this.cardGeneration && s.card.userId === userId)
          s.card.relation = relation;
      });
    } catch {
      // The card keeps its placeholder; a re-open retries.
    }
  }

  // -------------------------------------------------------------------------
  // The resync (chat-api.md §3.3 + D160)

  /** One question, whatever is held (docs/fast-start.md D1053): what the first
   * screen draws, less the rows this client says it holds as they are (D1054). The
   * rest of the list and the last messages of the first page follow behind it
   * (D1059, D1060), and nobody asks about anybody to draw any of it. Settles once
   * the list is whole and the window current, which is what whoever goes on to
   * open a chat waits for. */
  async sync(): Promise<void> {
    const lifetime = currentLifetime();
    if (!session()) return;
    const asked = this.wentChats.length;
    let answer: BootstrapResponse;
    try {
      answer = await api.fetchBootstrap(FIRST_PAGE, mut.knownRows(this.state.rooms, FIRST_PAGE));
      if (!isCurrent(lifetime)) return;
      this.syncAgain.tries = 0;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.syncAfter(e);
      this.fail(e);
      return;
    }
    // Read before the chat went (D326): the frame is the newer word.
    const went = new Set(this.wentChats.slice(asked));
    const chats = answer.chats.filter(([id]) => !went.has(id));
    const rows = (answer.rows ?? []).filter((row) => !went.has(row.channel_id));
    this.listed = new Set(chats.map(([id]) => id));
    const generation = ++this.syncGeneration;
    this.adoptMe(answer.me, true);
    this.meet(answer.people);
    // Written inside the draft below, so declared through `as`: a plain
    // `= null` would narrow it to null for good after the callback.
    let mark = null as { channel: number; seq: number } | null;
    this.mutate((s) => {
      s.notice = null;
      // The gateway's clock as it wrote this answer (D298), before anything
      // the answer carries: a client that has just started has heard no live
      // frame yet, and the rows below — and the page that follows them — are
      // already read through the offset. A gateway older than the field says
      // nothing, and the clocks are taken to agree until a frame says otherwise.
      if (typeof answer.server_time_ms === "number") {
        mut.noteClockSample(s.clock, answer.server_time_ms, Date.now());
      }
      // Friends BEFORE rooms: a just-appeared DM row must already have a
      // friendship behind it when the Friends tab counts.
      mut.applyFriendsSnapshot(s.friends, answer.friends);
      mut.applyPresenceState(s.presence, answer.presence ?? null);
      // A row left out of the answer is one this client holds as it is: only
      // what `chats` no longer lists goes.
      mut.applyRows(s.rooms, rows, s.directory, Date.now(), this.t);
      mut.retainListed(s.rooms, this.listed);
      // A card that came before its dialog's row did still raises it (D943).
      mut.raiseForNotices(s.rooms, s.directory, s.securityNotices);
      // The `seen` frames of the time the socket was down are not coming
      // (chat-api.md §3.3): every reader list kept may be missing somebody,
      // and each is asked for again at its next opening (D423).
      mut.forgetReads(s.reads);
      if (s.selectedChannel !== null && !mut.roomOf(s.rooms, s.selectedChannel)) {
        s.selectedChannel = null;
      }
      mark = this.settleOpenRow(s);
    });
    if (mark) void this.markReadAt(mark.channel, mark.seq);

    const settling = this.settleWindow(lifetime);
    // Behind the first screen, and past the chat that began to open just now
    // (D1059, D1060).
    this.readAhead(chats);
    const rest = this.readRest(mut.staleRows(this.state.rooms, chats), generation);
    this.restReading = rest;
    await Promise.all([settling, rest]);
  }

  /** The open chat's row was just served: the window's ceiling and mark go by it.
   * Answers the mark the gateway is to be told of, when the window's is past its
   * own. */
  private settleOpenRow(s: UiState): { channel: number; seq: number } | null {
    const open = s.selectedChannel;
    if (open === null) return null;
    const row = mut.roomOf(s.rooms, open);
    // Not while a post's comments are open over the channel (D846): the
    // window is the thread's, and the channel's ceiling is not its own.
    if (!row || s.feed.channelId !== open) return null;
    // Below the new ceiling the window cannot tell messages deleted
    // while we were away from messages it missed: closeGap() below
    // asks, and its answer is what says the window holds the end again.
    mut.resyncCeiling(s.feed, row.maxSeq);
    s.feed.lastReadSeq = Math.max(s.feed.lastReadSeq, row.lastReadSeq);
    // And the other way round (D346): a mark the window holds past the
    // gateway's is one the gateway was never told — the feed counts
    // your own message read on arrival, and only a posted mark reaches
    // it. Taking the gateway's word drew a badge over a feed scrolled
    // to its end, which nothing would post past. The row takes the
    // window's mark, and the gateway is told right after.
    if (s.feed.lastReadSeq <= row.lastReadSeq) return null;
    mut.roomsOnRead(s.rooms, open, s.feed.lastReadSeq);
    return { channel: open, seq: s.feed.lastReadSeq };
  }

  /** The window brought up to date behind what a sync's answer said of its row:
   * what it missed while nothing was listening, or the first chat when none is
   * open. */
  private async settleWindow(lifetime: SessionLifetime): Promise<void> {
    // A URL coordinator may have requested an open synchronously from the
    // store notification above while its visual commit is still deferred.
    // Resync must not replace that navigation with the first/previous room.
    if (this.requestedChannel !== null) return;
    const open = this.state.selectedChannel;
    if (this.state.thread !== null && this.state.feed.channelId === this.state.thread.threadId) {
      // A thread's frames were missed with the socket's, and nothing raised its
      // ceiling: what is new in it is asked for whatever the ceiling says.
      await this.catchUp();
      if (!isCurrent(lifetime)) return;
      await this.refreshWindowReactions();
      return;
    }
    if (open !== null) {
      const newest = mut.newestSeq(this.state.feed) ?? 0;
      if (this.state.feed.maxSeq - newest > WINDOW_SIZE) {
        // The whole window is stale: reopening lands on the bottom in two
        // requests instead of dozens trimHead would throw away.
        await this.selectChannel(open, true);
        if (!isCurrent(lifetime)) return;
      } else {
        await this.closeGap();
        if (!isCurrent(lifetime)) return;
        await this.refreshWindowReactions();
        if (!isCurrent(lifetime)) return;
      }
    } else {
      // The address names a chat further down the list, whose row is on its way:
      // the coordinator opens it the moment it lands, and the first chat opened
      // in its place would only flash by.
      const addressed = this.addressed;
      if (
        addressed !== null &&
        this.listed.has(addressed) &&
        !mut.roomOf(this.state.rooms, addressed)
      ) {
        return;
      }
      const first = mut.firstListed(this.state.rooms)?.channelId;
      if (first !== undefined) await this.selectChannel(first);
      if (!isCurrent(lifetime)) return;
    }
  }

  /** The rows the whole list says this client lacks (docs/fast-start.md D1059), 50
   * to a request, behind the first screen. Under the sync's generation: the next
   * answer says again what is lacking. A page the gateway did not answer is asked
   * again by itself (D1084): with the socket up no next answer may ever come, and the
   * rest of the list would stay away for good. */
  private async readRest(stale: readonly number[], generation: number, tries = 0): Promise<void> {
    const lifetime = currentLifetime();
    for (let at = 0; at < stale.length; at += ROWS_PER_REQUEST) {
      const read = await this.readRows(stale.slice(at, at + ROWS_PER_REQUEST));
      if (!isCurrent(lifetime)) return;
      // Whoever waits on this reading waits on the one that took over from it.
      if (generation !== this.syncGeneration) return this.restReading;
      if (!read) {
        // Any progress starts the climb again.
        this.readRestAfter(stale.slice(at), generation, at === 0 ? tries : 0);
        return;
      }
    }
  }

  /** The rest of the walk, once the wait a held lane names has passed, or the next
   * rung's. Off to one side of `restReading`: whoever waits on a sync waits for an
   * answer or for a failure, and never for a gateway to come back. */
  private readRestAfter(stale: readonly number[], generation: number, tries: number): void {
    const lifetime = currentLifetime();
    const left = api.busyLeftMs("requests");
    const rung = REST_RETRY_MS[Math.min(tries, REST_RETRY_MS.length - 1)]!;
    setTimeout(
      () => {
        if (!isCurrent(lifetime) || generation !== this.syncGeneration) return;
        void this.readRest(stale, generation, tries + 1);
      },
      left > 0 ? left : rung,
    );
  }

  /** These rows, read and folded in, whoever they name with them: the list is
   * already whole as far as `chats` goes, so none of this drops anything. Answers
   * whether the gateway answered. */
  private async readRows(ids: readonly number[]): Promise<boolean> {
    const lifetime = currentLifetime();
    const asked = this.wentChats.length;
    let answer: ChatRowsResponse;
    try {
      answer = await patientLane(() => api.fetchChatRows(ids), lifetime);
    } catch {
      return false;
    }
    if (!isCurrent(lifetime)) return false;
    // Read before the chat went (D326): the frame is the newer word.
    const went = new Set(this.wentChats.slice(asked));
    const rows = answer.rows.filter((row) => !went.has(row.channel_id));
    this.meet(answer.people);
    const open = this.state.selectedChannel;
    const opened = rows.some((row) => row.channel_id === open);
    let mark = null as { channel: number; seq: number } | null;
    this.mutate((s) => {
      mut.applyRows(s.rooms, rows, s.directory, Date.now(), this.t);
      // A card that came before its dialog's row did still raises it (D943).
      mut.raiseForNotices(s.rooms, s.directory, s.securityNotices);
      if (opened) mark = this.settleOpenRow(s);
    });
    if (mark) void this.markReadAt(mark.channel, mark.seq);
    // The open chat's own row came here and not with the first page: what the
    // sync did for a chat on the first page is done for it now.
    if (opened) await this.settleWindow(lifetime);
    return isCurrent(lifetime);
  }

  /** The last messages of the chats of the first page (D1060), once per lifetime,
   * after the first sync with the socket up: before it, nothing would say that
   * what was read has gone stale, and the socket coming up lets go of it anyway.
   * Every ask is a bare id, the web holding no revisions, and not the chat being
   * opened, which reads its own. A failure loses nothing: a chat opens from the
   * gateway as it always did. */
  private readAhead(chats: ReadonlyArray<readonly [number, number]>): void {
    if (this.aheadAsked || !this.state.connected) return;
    this.aheadAsked = true;
    const opening = this.requestedChannel ?? this.state.selectedChannel;
    const asks = chats
      .slice(0, FIRST_PAGE)
      .map(([id]) => id)
      .filter((id) => {
        const row = mut.roomOf(this.state.rooms, id);
        return id !== opening && row !== undefined && row.maxSeq > 0;
      });
    if (asks.length === 0) return;
    const lifetime = currentLifetime();
    const moved = new Set<number>();
    this.aheadMoved = moved;
    patientLane(() => api.fetchLatest(asks, READ_AHEAD), lifetime).then(
      (answer) => {
        if (!isCurrent(lifetime) || this.aheadMoved !== moved) return;
        this.aheadMoved = null;
        this.meet(answer.people);
        // A chat opened while the read was out has read its own by now.
        const open = this.requestedChannel ?? this.state.selectedChannel;
        for (const { channel_id: id, page } of answer.chats) {
          if (page && page.messages.length > 0 && id !== open && !moved.has(id)) {
            this.aheadPages.set(id, page);
          }
        }
      },
      () => {},
    );
  }

  /** A frame about this chat (D1060): what was read ahead of it no longer says what
   * is there, and a read still out may have left before the frame did. */
  private dropAhead(channelId: number): void {
    this.aheadPages.delete(channelId);
    this.aheadMoved?.add(channelId);
  }

  /** The socket went, or came back: no frame said what moved in between, so nothing
   * read ahead can be believed, an answer still on its way included. */
  private dropAllAhead(): void {
    this.aheadPages.clear();
    this.aheadMoved = null;
  }

  /** The page read ahead of this opening, when one stands (D1060): taken, whatever
   * becomes of it, and drawn in the window's first frame if it can stand as the
   * window. The gateway's own pages are still asked for, and fold into it. */
  private drawAhead(s: UiState, id: number): void {
    const page = this.aheadPages.get(id);
    if (page === undefined) return;
    this.aheadPages.delete(id);
    const feed = s.feed;
    if (!mut.aheadDrawable(page.messages, feed.maxSeq, feed.lastReadSeq)) return;
    splicePage(feed, page, true, this.t);
    feed.drawnAhead = true;
    feed.reachedStart = (page.messages[0]?.seq ?? 0) <= 1;
    // The newest page is the end by definition, and anything since would have
    // taken the page away.
    feed.reachedEnd = mut.endHolds(feed, true, feed.maxSeq);
    mut.insertUnreadDivider(feed);
    mut.resolveNames(feed, s.directory, this.t);
  }

  /** The state asked for again after a gateway with too much in work refused it
   * (docs/admission.md D1075). Nothing else would ask: the socket stays up through
   * this, so no `ready` comes, and a first load would sit on an empty list. One
   * such wait at a time, each longer than the last (nothing, then 5, 15, 35 and at
   * most 55 seconds on top of the gateway's own), so that a gateway refusing for
   * minutes is not asked by everybody on the very second it names. */
  private syncAfter(e: unknown): void {
    if (!(e instanceof api.ApiError) || e.status !== 503 || this.syncAgain.waiting) return;
    const left = api.busyLeftMs("requests");
    if (!(left > 0)) return;
    const lifetime = currentLifetime();
    const longer = Math.min((5 << Math.min(this.syncAgain.tries, 4)) - 5, 55) * 1000;
    this.syncAgain.tries += 1;
    this.syncAgain.waiting = true;
    this.syncAgain.timer = setTimeout(() => {
      this.syncAgain.timer = null;
      this.syncAgain.waiting = false;
      if (isCurrent(lifetime)) void this.sync();
    }, left + longer);
  }

  /** Everything past the window's newest message, asked of the gateway
   * whatever its ceiling says — for a window no frame keeps current: a thread
   * after a reconnect (D846), a channel read without a subscription (D839).
   * Best-effort, as the gap-fill is. */
  private async catchUp(): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    const channel = this.state.feed.channelId;
    if (channel === 0 || this.state.feed.loading) return;
    for (let round = 0; round < 10; round += 1) {
      const newest = mut.newestSeq(this.state.feed);
      const askedAt = this.state.feed.maxSeq;
      let page: import("../gen/dto").PageResponse;
      try {
        page = await this.pageOf(
          channel,
          newest === undefined ? { limit: PAGE_SIZE } : { after: newest, limit: PAGE_SIZE },
        );
      } catch {
        return;
      }
      if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      if (this.state.feed.channelId !== channel) return;
      // The window moved on under the page (`moveWindow`), or a scroll asked first.
      if (mut.newestSeq(this.state.feed) !== newest) return;
      const short = page.messages.length < PAGE_SIZE;
      this.mutate((s) => {
        splicePage(s.feed, page, true, this.t);
        mut.trimHead(s.feed);
        mut.resolveNames(s.feed, s.directory, this.t);
        if (short) s.feed.reachedEnd = mut.endHolds(s.feed, true, askedAt);
      });
      // Short, and something arrived past it meanwhile: once more.
      if (short && this.state.feed.reachedEnd) return;
    }
  }

  /** A page of history, whoever it names in the directory before anything draws
   * it (docs/fast-start.md D1056): the one door every page comes through, so a chat
   * just opened draws names rather than `#123` for a round trip, and nothing asks
   * about them. */
  private async pageOf(channel: number, query: api.PageQuery): Promise<PageResponse> {
    const lifetime = currentLifetime();
    const page = await api.fetchMessages(channel, query);
    if (isCurrent(lifetime)) this.meet(page.people);
    return page;
  }

  /** People an answer named (D1056), into the directory: merged, and everybody else
   * stays met. Whatever drew them under a placeholder is drawn again, and a pet
   * whose card moved has the `/` lists read against it asked for again. */
  private meet(rows: readonly DirectoryEntry[] | null | undefined): void {
    // Answers repeat the same people constantly: nothing new publishes nothing.
    if (rows === null || rows === undefined || mut.holdsAll(this.state.directory, rows)) return;
    let moved: mut.PetMove[] = [];
    this.mutate((s) => {
      moved = mut.applyDirectory(s.directory, rows, s.selfUsername);
      this.resyncNames(s);
    });
    this.onPetsMoved(moved);
  }

  /** An id was noted (D1057): the gateway is asked a moment later, so that every
   * read of one render lands in one request. A timer already standing — gathering,
   * or the rest after a failure — carries this id with it. Called from inside
   * renders and recipes: nothing here may publish. */
  private wakeLookups(): void {
    if (this.peopleTimer !== null) return;
    this.peopleTimer = setTimeout(() => {
      this.peopleTimer = null;
      void this.lookUpWanted();
    }, PEOPLE_GATHER_MS);
  }

  /** Asks about whoever is wanted, up to 200 ids a request and each id once a
   * session. An id the gateway does not answer for is not asked about again; a
   * request that failed puts its ids back and rests, the rest being a timer that
   * every id noted meanwhile leaves standing. */
  private async lookUpWanted(): Promise<void> {
    const lifetime = currentLifetime();
    if (!session()) return;
    while (people.hasWanted()) {
      // Not whoever an answer named while the ids were gathering.
      const ids = people
        .takeWanted(PEOPLE_BATCH)
        .filter((id) => this.state.directory.people[id] === undefined);
      if (ids.length === 0) continue;
      try {
        const rows = await api.lookupPeople(ids, []);
        if (!isCurrent(lifetime)) return;
        this.meet(rows);
      } catch {
        if (!isCurrent(lifetime)) return;
        people.askAgain(ids);
        if (this.peopleTimer !== null) clearTimeout(this.peopleTimer);
        this.peopleTimer = setTimeout(() => {
          this.peopleTimer = null;
          void this.lookUpWanted();
        }, PEOPLE_REST_MS);
        return;
      }
    }
  }

  /** `say`, once this account has a name: something said once and never drawn again
   * — a toast, a system notification — waits for the gateway when it is about
   * somebody not met (D1063). At once when they are, which is nearly always; a
   * failure says `#id`, as anything does until the gateway answers, and so does a
   * gateway that has said nothing by `NAME_WAIT_MS`. One question however many
   * messages somebody not met sends while it is out, and the batches leave it be. */
  private named(userId: number, say: () => void): void {
    if (userId === 0 || this.state.directory.people[userId] !== undefined) {
      say();
      return;
    }
    const lifetime = currentLifetime();
    let asked = this.namesOut.get(userId);
    if (asked === undefined) {
      people.askApart(userId);
      asked = api
        .lookupPeople([userId], [])
        .then(
          (rows) => {
            if (isCurrent(lifetime)) this.meet(rows);
          },
          () => {
            if (!isCurrent(lifetime)) return;
            people.askAgain([userId]);
            this.wakeLookups();
          },
        )
        .finally(() => this.namesOut.delete(userId));
      this.namesOut.set(userId, asked);
    }
    const waited = new Promise<void>((done) => setTimeout(done, NAME_WAIT_MS));
    void Promise.race([asked, waited]).then(() => {
      if (isCurrent(lifetime)) say();
    });
  }

  /** Somebody not met is the gateway's to find (D1058): `query`, once the typing has
   * stood still, unless it is too short to mean anybody or was asked a moment ago.
   * Only the newest search waits; the answer merges into the directory, and the
   * screens that filter it read it again. */
  findPeople(query: string): void {
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    this.searchTimer = null;
    const needle = query.trim();
    if ([...needle].length < PEOPLE_SEARCH_MIN_CHARS) return;
    if (people.queryAnswered(needle, Date.now())) return;
    const lifetime = currentLifetime();
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      people.noteQuery(needle, Date.now());
      api.searchPeople(needle).then(
        (rows) => {
          if (isCurrent(lifetime)) this.meet(rows);
        },
        () => {
          // Nothing was answered, so typing it again asks again: the words would
          // otherwise stand for half a minute as if nobody answered to them.
          if (isCurrent(lifetime)) people.queryAgain(needle);
        },
      );
    }, PEOPLE_SEARCH_PAUSE_MS);
  }

  /** The live pets, which the Pets tab shows to everybody (D405, D1058): once in
   * five minutes, so that somebody's new pet reaches the tab, and again after a read
   * that failed. */
  wantLivePets(): void {
    if (!people.takePets(Date.now())) return;
    const lifetime = currentLifetime();
    api.fetchLivePets().then(
      (rows) => {
        if (isCurrent(lifetime)) this.meet(rows);
      },
      () => {
        if (isCurrent(lifetime)) people.petsAgain();
      },
    );
  }

  /** This account read again now, whatever the directory holds of it (D1057): a pet
   * this account has just made, which no frame will name to a client that has not
   * met it (D1056). Best-effort: a read of its name asks later. */
  async refreshPerson(userId: number): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const rows = await api.lookupPeople([userId], []);
      if (isCurrent(lifetime)) this.meet(rows);
    } catch {
      // The name stays what it was until something reads it again.
    }
  }

  /** Pets whose cards moved (pets-plan.md D360, D362): every `/` list read
   * against an older one is asked for again, and so is the card open on one
   * of them — which is also how a card opened before the directory knew its
   * account for a pet becomes a pet's card. */
  private onPetsMoved(moved: readonly mut.PetMove[]): void {
    if (moved.length === 0) return;
    const stale = new Set<number>();
    for (const move of moved) {
      for (const channel of mut.staleCommandLists(this.state.commands, move)) stale.add(channel);
    }
    this.dropCommands([...stale]);
    const card = this.state.card.userId;
    if (card !== null && moved.some((move) => move.petId === card)) void this.recheckCard();
  }

  /** The one function that rewrites every directory-fed cache. */
  private resyncNames(s: UiState): void {
    mut.resolveNames(s.feed, s.directory, this.t);
    for (const row of s.rooms) {
      if (row.callMemberIds.length > 0) {
        mut.setRoomCall(row, row.callMemberIds, s.directory);
      }
    }
    if (s.ringing !== null) s.ringing.starterName = mut.nameOf(s.directory, s.ringing.starterId);
  }

  private fail(e: unknown): void {
    if (e instanceof api.ApiError && e.unauthorized) {
      if (!inReauthWindow()) void this.logout();
      return;
    }
    this.mutate((s) => {
      s.notice = e instanceof Error ? e.message : String(e);
    });
  }

  // -------------------------------------------------------------------------
  // Channel lifecycle

  openChannel(id: number): void {
    // A channel the reader holds no row of opens only when it is a public
    // broadcast channel, read without a subscription
    // (docs/broadcast-channels-plan.md D859); anything else stays the quiet
    // no-op it always was.
    if (!mut.roomOf(this.state.rooms, id)) {
      void this.openBroadcast({ channelId: id, handle: null, seq: null }, true);
      return;
    }
    void this.selectChannel(id);
  }

  /** Invalidate an abandoned route even if its transition has not committed yet. */
  cancelChannelNavigation(nextTarget?: number | null): void {
    this.addressed = nextTarget ?? null;
    // URL feedback for a runtime command acknowledges the same in-flight open;
    // invalidating it here would strand the loading feed without its response.
    if (nextTarget != null && nextTarget === this.requestedChannel) return;
    this.channelGeneration += 1;
    this.requestedChannel = null;
  }

  /** Opens a room of the list — or a public broadcast channel read without a
   * subscription — at its unread messages, or, with `around`, at that message
   * (docs/broadcast-channels-plan.md D859: a link to a post, and the way back
   * from a post's comments). */
  async selectChannel(id: number, force = false, around: number | null = null): Promise<void> {
    const lifetime = currentLifetime();
    if (!this.state.rooms.some((r) => r.channelId === id)) return;
    // A window whose opening failed is not an open window: the press opens it.
    if (
      !force &&
      around === null &&
      (this.requestedChannel === id ||
        (this.requestedChannel === null &&
          this.state.selectedChannel === id &&
          this.state.feed.channelId === id &&
          !this.state.feed.loading &&
          this.state.feed.error === null))
    )
      return;
    const generation = ++this.channelGeneration;
    this.requestedChannel = id;
    const current = () => isCurrent(lifetime) && generation === this.channelGeneration;
    // The host may defer its commit. Wait for it before reading the resulting
    // feed, and also check ownership *inside* it: A -> B -> A is three opens.
    await new Promise<void>((resolve) => {
      // A host may suppress its deferred callback when the account ends. The
      // caller must still settle instead of waiting forever for an old frame.
      const finish = () => {
        lifetime.signal.removeEventListener("abort", finish);
        resolve();
      };
      lifetime.signal.addEventListener("abort", finish, { once: true });
      this.host.transition("center", () => {
        try {
          if (current()) {
            this.mutate((s) => {
              const row = s.rooms.find((r) => r.channelId === id);
              if (!row) return;
              // A channel read without a subscription is kept while it is the
              // one read (D837): another opening forgets it, so the next link
              // to it reads it afresh — a handle since moved, a channel gone
              // private — instead of this copy. Its card goes with it.
              if (s.rooms.some((r) => r.guest && r.channelId !== id)) {
                s.rooms = s.rooms.filter((r) => !r.guest || r.channelId === id);
                const card = s.broadcastCard;
                if (card !== null && !s.rooms.some((r) => r.channelId === card)) {
                  s.broadcastCard = null;
                }
              }
              s.selectedChannel = id;
              // Whatever comments were open over the last channel close with
              // it (D846).
              s.thread = null;
              mut.openFeed(s.feed, id, row.name, row.maxSeq, row.lastReadSeq);
              // A broadcast channel speaks in its own name (D841).
              s.feed.voice = isBroadcastRoom(row) ? mut.voiceOf(row) : null;
              // With nothing to wait for, in this very frame (D1060). An opening
              // at a message wants the page around it, and leaves this one for
              // the next.
              if (around === null) this.drawAhead(s, id);
            });
          }
        } finally {
          finish();
        }
      });
    });
    if (!current() || this.state.feed.channelId !== id) return;
    if (around !== null) {
      await this.openAround(id, around, current);
      return;
    }
    const { maxSeq, lastReadSeq } = this.state.feed;
    const unread = maxSeq > lastReadSeq && lastReadSeq > 0;
    try {
      const head = unread
        ? await this.pageOf(id, { before: lastReadSeq + 1, limit: 25 })
        : await this.pageOf(id, { limit: PAGE_SIZE });
      if (!current()) return;
      const tail = unread
        ? await this.pageOf(id, { after: lastReadSeq, limit: PAGE_SIZE })
        : { messages: [] as Message[] };
      if (!current()) return;
      const reachedStart = head.messages.length === 0 || (head.messages[0]?.seq ?? 0) <= 1;
      // The newest page is the end by definition; the page past the mark is the
      // end when it came back short. Either way only if nothing arrived past it
      // while it was in flight (D277).
      const saidSo = !unread || tail.messages.length < PAGE_SIZE;
      const land = () => {
        if (!current() || this.state.feed.channelId !== id) return;
        this.requestedChannel = null;
        this.mutate((s) => {
          const drawnAhead = s.feed.drawnAhead;
          s.feed.loading = false;
          s.feed.drawnAhead = false;
          splicePage(s.feed, head, true, this.t);
          splicePage(s.feed, tail as import("../gen/dto").PageResponse, true, this.t);
          s.feed.reachedStart = reachedStart;
          s.feed.reachedEnd = mut.endHolds(s.feed, saidSo, maxSeq);
          // A window drawn ahead had its line then: a reader at the end has
          // taken it down by now, and once down it stays down.
          if (!drawnAhead) mut.insertUnreadDivider(s.feed);
          mut.resolveNames(s.feed, s.directory, this.t);
        });
        if (this.state.feed.reachedEnd) return;
        // A send made while the pages were out goes to the end, as one made a
        // moment later does (`comeBack`); and pages that said this is the end, with
        // something past them since, are one page short of it (the desktop's `lost`).
        if (mut.sendWaiting(this.state.feed)) void this.jumpToLatest();
        else if (saidSo) void this.closeGap();
      };
      // A window drawn ahead has had its swap, with the first frame (D1060): a
      // second would have the chat that just appeared sink and rise.
      if (this.state.feed.drawnAhead) land();
      else this.transition("center", land);
    } catch (e) {
      if (!current() || this.state.feed.channelId !== id) return;
      this.requestedChannel = null;
      // A window drawn ahead stands (D1060): frames have kept it current, and what
      // these pages would have added is above it, where the scroll goes on its own
      // and a column too short to scroll asks by itself (docs/fast-start.md D1093).
      if (this.state.feed.drawnAhead) {
        this.mutate((s) => {
          s.feed.loading = false;
          s.feed.drawnAhead = false;
        });
        this.fail(e);
        return;
      }
      if (!this.openAfterWait(e, id, null, current)) this.openingFailed(e);
    }
  }

  /** The page with `around` in its middle (D270), and the window brought to
   * that message once it lands (`focusSeq`): a link to a post (D859), the way
   * back from its comments. Whether the window holds the channel's end is the
   * page's to say — its newest message at the ceiling — and the start, its
   * first at seq 1. */
  private async openAround(id: number, around: number, current: () => boolean): Promise<void> {
    const maxSeq = this.state.feed.maxSeq;
    try {
      const page = await this.pageOf(id, { around, limit: PAGE_SIZE });
      if (!current()) return;
      // A row that knows no ceiling — a channel read without a subscription,
      // made from its card (D837) — leaves the end to the page (chat-api.md
      // §2.2): fewer than half a page above `around` is the channel's end.
      const above = page.messages.filter((m) => m.seq > around).length;
      this.transition("center", () => {
        if (!current() || this.state.feed.channelId !== id) return;
        this.requestedChannel = null;
        this.mutate((s) => {
          s.feed.loading = false;
          splicePage(s.feed, page, true, this.t);
          s.feed.reachedStart = page.messages.length === 0 || (page.messages[0]?.seq ?? 0) <= 1;
          s.feed.reachedEnd =
            maxSeq === 0 ? above < PAGE_SIZE / 2 : mut.endHolds(s.feed, false, maxSeq);
          mut.resolveNames(s.feed, s.directory, this.t);
          s.feed.focusSeq = around;
        });
        // The rest of the channel down to its end: the window holds the end,
        // which is where everything new arrives, and the reader stays where
        // they landed.
        if (!this.state.feed.reachedEnd) void this.catchUp();
      });
    } catch (e) {
      if (!current() || this.state.feed.channelId !== id) return;
      this.requestedChannel = null;
      if (!this.openAfterWait(e, id, around, current)) this.openingFailed(e);
    }
  }

  /** A gateway with too much in work (D1075) has said nothing about the chat: the
   * pane goes on saying «loading», which is true where «Start the conversation»
   * would not be, and the chat opens by itself, where it was going, once the wait
   * the gateway named has passed. Answers whether that is what was refused. */
  private openAfterWait(
    e: unknown,
    id: number,
    around: number | null,
    current: () => boolean,
  ): boolean {
    const left = api.busyLeftMs("requests");
    if (!(e instanceof api.ApiError) || e.status !== 503 || !(left > 0)) return false;
    this.fail(e);
    setTimeout(() => {
      if (current() && this.state.feed.channelId === id && this.state.feed.loading) {
        void this.selectChannel(id, true, around);
      }
    }, left);
    return true;
  }

  /** The window's first pages did not come, for no reason a wait would cure: the pane
   * stops saying «loading», and says why over the composer rather than letting an
   * empty window pass for an empty chat. A second press opens it again. */
  private openingFailed(e: unknown): void {
    this.mutate((s) => {
      s.feed.loading = false;
      s.feed.error = e instanceof Error ? e.message : String(e);
    });
    this.fail(e);
  }

  async loadOlder(): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    const feed = this.state.feed;
    // Not while the opening's own pages are out (a window drawn ahead, D1060):
    // they bring what is above it.
    if (feed.loading || feed.loadingOlder || feed.reachedStart || feed.bubbles.length === 0) return;
    if (this.olderRetry?.channel === feed.channelId && Date.now() < this.olderRetry.at) return;
    const oldest = mut.oldestSeq(feed);
    if (oldest === undefined) return;
    const channel = feed.channelId;
    this.mutate((s) => {
      s.feed.loadingOlder = true;
    });
    try {
      const page = await this.pageOf(channel, {
        before: oldest,
        limit: PAGE_SIZE,
      });
      if (!isCurrent(lifetime)) return;
      if (this.state.feed.channelId !== channel) return; // only the setter clears the flag
      // Taken only while it is still where the window begins (the desktop's
      // `prepend_page`): the window may have moved since, and a page spliced on
      // top of another stretch would leave a hole no cursor asks for again.
      const continues =
        generation === this.channelGeneration && mut.oldestSeq(this.state.feed) === oldest;
      this.mutate((s) => {
        s.feed.loadingOlder = false;
        if (!continues) return;
        if (page.messages.length === 0) {
          s.feed.reachedStart = true;
        } else {
          splicePage(s.feed, page, false, this.t);
          mut.trimTail(s.feed);
          mut.resolveNames(s.feed, s.directory, this.t);
        }
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (this.state.feed.channelId !== channel) return;
      const current = generation === this.channelGeneration;
      if (current) this.olderRetry = { channel, at: Date.now() + PAGE_RETRY_WAIT_MS };
      this.mutate((s) => {
        s.feed.loadingOlder = false;
        if (current) s.notice = e instanceof Error ? e.message : String(e);
      });
    }
  }

  /** The page below a window short of the channel's end, for a reader nearing its
   * bottom (docs/fast-start.md D1091): a chat opened on a backlog longer than a page,
   * a bottom a long scroll upwards trimmed. One page per call, the next being a wheel
   * notch away; `closeGap` is the loop. `loadOlder`'s mirror, and `newerRetry` is what
   * keeps a refusal from being asked again on every scroll event. */
  async loadNewer(): Promise<void> {
    const channel = this.state.feed.channelId;
    if (this.newerRetry?.channel === channel && Date.now() < this.newerRetry.at) return;
    await this.newerPage();
  }

  /** Pages down until the window reaches the end (the desktop's `close_gap`): the gap
   * a reconnect left, whose ceiling can be hundreds of seqs above the window, and the
   * one a live message opens by arriving out of turn. Stops at anything that is not
   * progress, so a ceiling no page can reach cannot spin here. */
  private async closeGap(): Promise<void> {
    let more = true;
    while (more) more = await this.newerPage();
  }

  /** The ?after= gap-fill, one page, onto the end of the run while that is still
   * where it ended (the desktop's `newer_page`). Answers whether another may follow. */
  private async newerPage(): Promise<boolean> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    const feed = this.state.feed;
    const channel = feed.channelId;
    if (channel === 0 || feed.loading || feed.loadingNewer || feed.reachedEnd) return false;
    const newest = mut.newestSeq(feed);
    const askedAt = feed.maxSeq;
    this.mutate((s) => {
      s.feed.loadingNewer = true;
    });
    let page: import("../gen/dto").PageResponse;
    try {
      page = await this.pageOf(
        channel,
        newest === undefined ? { limit: PAGE_SIZE } : { after: newest, limit: PAGE_SIZE },
      );
    } catch (e) {
      // Only the channel that set the flag clears it: another's has its own.
      if (!isCurrent(lifetime) || this.state.feed.channelId !== channel) return false;
      this.mutate((s) => {
        s.feed.loadingNewer = false;
      });
      if (generation !== this.channelGeneration) return false;
      this.newerRetry = { channel, at: Date.now() + PAGE_RETRY_WAIT_MS };
      this.fail(e);
      return false;
    }
    if (!isCurrent(lifetime) || this.state.feed.channelId !== channel) return false;
    // A page is in flight for a round trip, and the window can be somewhere else by
    // the time it lands (`moveWindow`): spliced on regardless it would sit past a hole.
    const continues =
      generation === this.channelGeneration && mut.newestSeq(this.state.feed) === newest;
    const short = page.messages.length < PAGE_SIZE;
    this.mutate((s) => {
      s.feed.loadingNewer = false;
      if (!continues) return;
      if (newest === undefined) {
        // That was the NEWEST page, not a continuation.
        s.feed.reachedStart = page.messages.length === 0 || (page.messages[0]?.seq ?? 0) <= 1;
      }
      s.feed.error = null;
      splicePage(s.feed, page, true, this.t);
      mut.trimHead(s.feed);
      mut.resolveNames(s.feed, s.directory, this.t);
      // A short page is the channel's end — a ceiling above it is holes
      // that deletions left, which is what lets the mark reach it (D277).
      s.feed.reachedEnd = mut.endHolds(s.feed, short, askedAt);
    });
    return continues && !this.state.feed.reachedEnd;
  }

  /** The window on the channel's newest page, in place of whatever stretch of its past
   * it stood in (the desktop's `latest`, D271): the arrow down, and a send from up
   * there (`comeBack`). Replaced rather than walked to: the window may be thousands
   * of messages from the end, and what is asked for is the end, not the pages between,
   * which `trimHead` would only drop. Short of the end after that, something was
   * posted while the page was in flight. */
  async jumpToLatest(): Promise<void> {
    if (this.state.feed.reachedEnd) return;
    const moved = await this.moveWindow({ limit: PAGE_SIZE }, (s, page, askedAt) => {
      s.feed.reachedStart = page.messages.length < PAGE_SIZE || (page.messages[0]?.seq ?? 0) <= 1;
      s.feed.reachedEnd = mut.endHolds(s.feed, true, askedAt);
    });
    if (moved) await this.closeGap();
  }

  /** The window around the place past its end where the unread messages begin, for the
   * arrow down (dioxus-plan.md D408, the desktop's `Destination::Unread`): the feed
   * lands on it (`focusSeq`), and the reader reads on down from there. */
  async jumpToUnread(seq: number): Promise<void> {
    const moved = await this.moveWindow({ around: seq, limit: PAGE_SIZE }, (s, page, askedAt) => {
      s.feed.reachedStart = page.messages.length === 0 || (page.messages[0]?.seq ?? 0) <= 1;
      s.feed.reachedEnd = mut.endHolds(s.feed, false, askedAt);
      s.feed.focusSeq = page.messages.find((m) => m.seq >= seq)?.seq ?? null;
    });
    if (moved && mut.sendWaiting(this.state.feed)) await this.jumpToLatest();
  }

  /** One page in place of the run the window holds (D271), with nothing else about
   * the window disturbed: no spinner, the composer and a selection as they were, and
   * what has not been confirmed still behind it. A failure leaves the window where the
   * reader is. Answers whether the page landed. */
  private async moveWindow(
    query: api.PageQuery,
    settle: (s: UiState, page: PageResponse, askedAt: number) => void,
  ): Promise<boolean> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    const feed = this.state.feed;
    const channel = feed.channelId;
    if (channel === 0 || feed.loading || this.windowMoving === generation) return false;
    const askedAt = feed.maxSeq;
    this.windowMoving = generation;
    this.mutate((s) => {
      s.feed.loadingNewer = true;
      // Wherever the window last landed is not where it is going.
      s.feed.focusSeq = null;
    });
    const here = () => isCurrent(lifetime) && this.state.feed.channelId === channel;
    try {
      const page = await this.pageOf(channel, query);
      if (!here()) return false;
      const current = generation === this.channelGeneration;
      this.mutate((s) => {
        s.feed.loadingNewer = false;
        if (!current) return;
        s.feed.error = null;
        mut.leaveRun(s.feed);
        splicePage(s.feed, page, true, this.t);
        settle(s, page, askedAt);
        mut.resolveNames(s.feed, s.directory, this.t);
      });
      return current;
    } catch (e) {
      if (!here()) return false;
      this.mutate((s) => {
        s.feed.loadingNewer = false;
      });
      if (generation !== this.channelGeneration) return false;
      this.newerRetry = { channel, at: Date.now() + PAGE_RETRY_WAIT_MS };
      this.fail(e);
      return false;
    } finally {
      if (this.windowMoving === generation) this.windowMoving = null;
    }
  }

  /** Back to the end for a reader who has just sent something from somewhere in the
   * channel's past (D271, the desktop's `come_back`; docs/fast-start.md D1091). Nobody
   * writes into a channel in order to keep looking somewhere else, and the bottom of a
   * window short of the end is a place with more underneath, with the new message
   * drawn under it. The bubble survives the window being rebuilt (`leaveRun`), and its
   * answer finds it by nonce whichever of the two lands first, so nothing here waits. */
  private comeBack(channel: number): void {
    const feed = this.state.feed;
    if (feed.channelId === channel && !feed.loading && !feed.reachedEnd) {
      void this.jumpToLatest();
    }
  }

  /** A send behind a window whose way back to the end did not make it: on the clock,
   * once the wait after the failure has passed. Its answer only finds it there, and
   * until then it says it is still going. */
  private comeBackAgain(now: number): void {
    const feed = this.state.feed;
    if (feed.reachedEnd || feed.loading || feed.loadingNewer || !mut.sendWaiting(feed)) return;
    if (this.newerRetry?.channel === feed.channelId && now < this.newerRetry.at) return;
    void this.jumpToLatest();
  }

  /** D160: `?after=` closed the message gap; the chips of what was already
   * on screen are the one thing it cannot heal. Best-effort. */
  private async refreshWindowReactions(): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    const feed = this.state.feed;
    const oldest = mut.oldestSeq(feed);
    const ceiling = mut.newestSeq(feed);
    if (oldest === undefined || ceiling === undefined) return;
    let cursor = Math.max(0, oldest - 1);
    const channel = feed.channelId;
    while (cursor < ceiling) {
      let page: import("../gen/dto").PageResponse;
      try {
        page = await this.pageOf(channel, {
          after: cursor,
          limit: PAGE_SIZE * 2,
        });
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      } catch {
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
        return; // a failure costs stale decoration, not data
      }
      const last = page.messages.at(-1)?.seq;
      if (last === undefined) return;
      const short = page.messages.length < PAGE_SIZE * 2;
      if (this.state.feed.channelId !== channel) return;
      this.mutate((s) => {
        // Of what the window holds, and nothing past it: its pages run on past the
        // newest message, and the window may have moved since it asked. Whatever
        // else they carry would land past a hole.
        splicePage(s.feed, mut.withinRun(s.feed, page), true, this.t);
      });
      cursor = last;
      if (short) return;
    }
  }

  // -------------------------------------------------------------------------
  // Broadcast channels (docs/broadcast-channels-plan.md D836–D872)

  /** The channel a link, the search or a forward names (D859, D871): opened
   * at the post the link names when the reader holds it; read without a
   * subscription when it is a public broadcast channel they do not
   * (`resolveBroadcast`); nothing otherwise. Answers whether a channel opened.
   * `quiet`: a link that leads nowhere says nothing — what an id that is not
   * a broadcast channel's has always done. */
  async openBroadcast(ref: BroadcastRef, quiet = false): Promise<boolean> {
    const lifetime = currentLifetime();
    const id = await this.resolveBroadcast(ref, quiet);
    if (id === null || !isCurrent(lifetime)) return false;
    await this.selectChannel(id, ref.seq !== null, ref.seq);
    return isCurrent(lifetime) && this.state.selectedChannel === id;
  }

  /** Which channel a link names (D859, D871), with a row to open it by: the
   * reader's own — found by its handle too — or, for a public broadcast
   * channel they hold none of, a row made from its card that no list draws
   * (`guest`, D837). A handle is resolved here and never ahead
   * (`GET /v1/broadcasts?handle=`). Null when there is none to open, which
   * the notice says unless `quiet`. A second ask for the same link while the
   * first is out is null too. */
  async resolveBroadcast(ref: BroadcastRef, quiet = false): Promise<number | null> {
    const lifetime = currentLifetime();
    const key = `${ref.channelId ?? ""}:${ref.handle ?? ""}`;
    if (this.broadcastOpens.has(key)) return null;
    this.broadcastOpens.add(key);
    try {
      let id = ref.channelId;
      let card: import("../gen/dto").ChannelCard | null = null;
      if (id === null && ref.handle !== null) {
        id = mut.handleTarget(this.state.rooms, {}, ref.handle);
        if (id === null) {
          const found = await api.fetchBroadcasts({ handle: ref.handle });
          if (!isCurrent(lifetime)) return null;
          card = found?.channels[0] ?? null;
          id = card?.channel_id ?? null;
        }
      }
      if (id === null) {
        if (!quiet) this.showNotice(this.t.broadcastNotFound());
        return null;
      }
      if (mut.roomOf(this.state.rooms, id)) return id;
      // On the list and its row not read yet (docs/fast-start.md D1059): somebody
      // is waiting on this one, and it goes before the rest of them.
      if (this.listed.has(id)) {
        await this.readRows([id]);
        if (!isCurrent(lifetime)) return null;
        if (mut.roomOf(this.state.rooms, id)) return id;
      }
      if (card === null) {
        try {
          card = (await api.fetchChannelCard(id)) ?? null;
        } catch {
          card = null;
        }
        if (!isCurrent(lifetime)) return null;
      }
      // Only a public broadcast channel is read without its door (D837);
      // whatever else the link names is the old no-op it always was.
      if (card === null || card.kind !== "broadcast" || card.is_private) {
        if (!quiet && (card === null || card.kind === "broadcast")) {
          this.showNotice(this.t.broadcastNotFound());
        }
        return null;
      }
      const guest = mut.guestRoomOf(card);
      this.mutate((s) => {
        if (!mut.roomOf(s.rooms, guest.channelId)) s.rooms.push(guest);
      });
      return id;
    } catch (e) {
      if (isCurrent(lifetime)) this.fail(e);
      return null;
    } finally {
      if (isCurrent(lifetime)) this.broadcastOpens.delete(key);
    }
  }

  /** The public broadcast channels whose name or handle holds `query` (D858,
   * D869, D871) — the top of the directory for an empty one. The search's
   * «Channels» section asks; a failure is an empty answer, as a search that
   * found nothing would be, rather than a notice over the list. */
  async searchBroadcasts(query: string): Promise<import("../gen/dto").ChannelCard[]> {
    const lifetime = currentLifetime();
    try {
      const { channels } = await api.fetchBroadcasts({ q: query.trim() });
      return isCurrent(lifetime) ? channels : [];
    } catch {
      return [];
    }
  }

  /** «New channel» (D836, D870, D872): a broadcast channel, public unless
   * said otherwise, with its handle when it is born with one — then the
   * state, which lists it, and the channel itself. */
  async createBroadcast(name: string, isPrivate: boolean, handle: string | null): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const { channel_id } = await api.createBroadcast({
        name,
        ...(isPrivate ? { is_private: true } : {}),
        ...(!isPrivate && handle !== null && handle !== "" ? { handle } : {}),
      });
      if (!isCurrent(lifetime)) return;
      await this.sync();
      if (!isCurrent(lifetime)) return;
      this.openChannel(channel_id);
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      // A gateway older than the kind has no such route (D477).
      if (api.isMissingRoute(e)) {
        this.showNotice(this.t.broadcastUnsupported());
        return;
      }
      this.fail(e);
    }
  }

  /** These posts of a broadcast channel have been on screen with the window in
   * front (D1081): the gateway counts the reader once for each, and the answer
   * brings how many people have seen them — on the open window's bubbles and on
   * the open thread's post (D1082). A lost report costs a number and nothing
   * else: what stays on screen is reported again within the minute. */
  async viewPosts(channelId: number, seqs: number[]): Promise<void> {
    const lifetime = currentLifetime();
    let views;
    try {
      ({ views } = await api.viewPosts(channelId, seqs));
    } catch {
      return;
    }
    if (!isCurrent(lifetime)) return;
    this.mutate((s) => {
      if (s.feed.channelId === channelId) mut.applyViews(s.feed, views);
      const thread = s.thread;
      if (thread === null || thread.channelId !== channelId) return;
      const row = views.find((r) => r.seq === thread.seq);
      if (row && row.count > thread.views) thread.views = row.count;
    });
  }

  /** «Subscribe» (D838): the channel's door — a public one's takes nothing —
   * and then the state, whose row of it carries what a subscriber is served
   * (the settings, any rights, a restriction) and puts it on the list. */
  async subscribe(channelId: number): Promise<void> {
    const lifetime = currentLifetime();
    await this.join(channelId);
    if (!isCurrent(lifetime)) return;
    if (mut.roomOf(this.state.rooms, channelId)?.member) await this.sync();
  }

  /** «Unsubscribe» (D838; never the owner, D112). A public channel stays open
   * where it is, read without a subscription from now on (D837); a private
   * one is gone with the membership (D114), at the next read of the state. The
   * head count moves with the frame that follows, not here. */
  async unsubscribe(channelId: number): Promise<void> {
    const lifetime = currentLifetime();
    try {
      await api.leaveChannel(channelId);
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        const row = mut.roomOf(s.rooms, channelId);
        if (!row) return;
        row.member = false;
        row.admin = false;
        row.rights = [];
        if (isBroadcastRoom(row) && !row.settings.is_private) row.guest = true;
      });
      await this.sync();
    } catch (e) {
      if (isCurrent(lifetime)) this.fail(e);
    }
  }

  /** A post's comments (D846), over the channel's feed: the thread's own
   * window and composer, the post drawn on top — the post as the window
   * holds it, or read from the gateway when it does not (a toast's press).
   * No read mark is posted in a thread (D861). */
  async openThread(ref: ThreadRef, post: Message | null = null): Promise<void> {
    const lifetime = currentLifetime();
    const room = mut.roomOf(this.state.rooms, ref.channelId);
    if (!room) return;
    const bubble =
      this.state.feed.channelId === ref.channelId
        ? this.state.feed.bubbles.find((b) => b.message.seq === ref.seq)
        : undefined;
    const held = post ?? bubble?.message ?? null;
    const generation = ++this.threadGeneration;
    // The channel's pages in the air are not this window's any more.
    this.channelGeneration += 1;
    this.requestedChannel = null;
    const current = () => isCurrent(lifetime) && generation === this.threadGeneration;
    this.transition("center", () => {
      if (!current()) return;
      this.mutate((s) => {
        s.selectedChannel = ref.channelId;
        s.thread = { ...ref, post: held, views: bubble?.views ?? 0 };
        mut.openFeed(s.feed, ref.threadId, this.t.commentsTitle(), 0, 0);
        s.feed.voice = mut.voiceOf(room);
      });
    });
    // The post stands over its comments, so it has been seen
    // (docs/broadcast-channels-plan.md D1081) — wherever the reader came from,
    // a notification's press included.
    void this.viewPosts(ref.channelId, [ref.seq]);
    try {
      const [page, fetched] = await Promise.all([
        this.pageOf(ref.threadId, { limit: PAGE_SIZE }),
        held === null
          ? this.pageOf(ref.channelId, { after: ref.seq - 1, limit: 1 })
          : Promise.resolve(null),
      ]);
      if (!current()) return;
      const found = fetched?.messages.find((m) => m.seq === ref.seq) ?? null;
      this.transition("center", () => {
        if (!current() || this.state.feed.channelId !== ref.threadId) return;
        this.mutate((s) => {
          if (s.thread !== null && s.thread.post === null && found !== null) s.thread.post = found;
          s.feed.loading = false;
          splicePage(s.feed, page, true, this.t);
          // A short page is the whole thread (chat-api.md §2.2), and so its
          // start, whatever its first comment's number: #1 may be deleted or
          // hidden, and the post is drawn over the start.
          s.feed.reachedStart =
            page.messages.length < PAGE_SIZE || (page.messages[0]?.seq ?? 0) <= 1;
          // The newest page is the end by definition, if nothing arrived past it
          // while it was out: it was asked with no ceiling known.
          s.feed.reachedEnd = mut.endHolds(s.feed, true, 0);
          // A thread is read as it opens (D863), and keeps no mark anywhere
          // else (D861): its arrow counts only what comes after.
          s.feed.lastReadSeq = Math.max(s.feed.lastReadSeq, s.feed.maxSeq);
          mut.resolveNames(s.feed, s.directory, this.t);
        });
        if (!this.state.feed.reachedEnd) void this.closeGap();
      });
    } catch (e) {
      if (!current() || this.state.feed.channelId !== ref.threadId) return;
      // A gateway with too much in work, as for a chat (`openAfterWait`).
      const left = api.busyLeftMs("requests");
      if (e instanceof api.ApiError && e.status === 503 && left > 0) {
        this.fail(e);
        setTimeout(() => {
          if (current() && this.state.feed.channelId === ref.threadId && this.state.feed.loading) {
            void this.openThread(ref, held);
          }
        }, left);
        return;
      }
      this.openingFailed(e);
    }
  }

  /** Back from a post's comments to its channel, at the post (D863). */
  async closeThread(): Promise<void> {
    const thread = this.state.thread;
    if (thread === null) return;
    this.threadGeneration += 1;
    this.mutate((s) => {
      s.thread = null;
    });
    await this.selectChannel(thread.channelId, true, thread.seq);
  }

  /** A post's two switches (D851): comments and reactions on or off under it,
   * from its menu. The answer is the post as it now stands, and its
   * `message_edited` follows to everybody. */
  async setPostOptions(
    seq: number,
    options: { comments?: boolean; reactions?: boolean },
  ): Promise<void> {
    const lifetime = currentLifetime();
    const channel = this.state.feed.channelId;
    if (channel === 0) return;
    try {
      const post = await api.setPostOptions(channel, seq, options);
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        if (s.feed.channelId !== channel) return;
        mut.reconcile(s.feed, post, this.t);
        mut.resolveNames(s.feed, s.directory, this.t);
      });
    } catch (e) {
      if (isCurrent(lifetime)) this.fail(e);
    }
  }

  /** The switches of the post being written in `channelId` (D851), for that
   * post alone. */
  setPostSwitches(channelId: number, switches: { comments?: boolean; reactions?: boolean }): void {
    this.mutate((s) => {
      s.postSwitches[channelId] = { ...s.postSwitches[channelId], ...switches };
    });
  }

  /** The face button of a holder of `comment_as_channel` (D848): whether the
   * comments written in this channel's threads go out in its name. */
  setCommentAsChannel(channelId: number, asChannel: boolean): void {
    this.mutate((s) => {
      if (asChannel) s.commentAsChannel[channelId] = true;
      else delete s.commentAsChannel[channelId];
    });
  }

  /** A broadcast channel's card (D864), or none. */
  openBroadcastCard(channelId: number | null): void {
    this.transition("sheet", () => {
      this.mutate((s) => {
        s.broadcastCard = channelId;
      });
    });
  }

  /** A window no frame reaches (D839): a broadcast channel read without a
   * subscription, or a post's comments under one. Every 30 seconds while it
   * stays open — the clock starting as it opens, having just read what there
   * was — and whenever the window comes back (`force`), it asks what is new
   * in it. The desktop's `poll_unheard`. */
  private pollGuest(now: number, force = false): void {
    const s = this.state;
    if (!unheard(s)) {
      this.guestPolled = null;
      return;
    }
    if (s.feed.loading) return;
    const channel = s.feed.channelId;
    const last = this.guestPolled;
    const due = last !== null && last.channel === channel && now - last.at >= GUEST_POLL_MS;
    if (due || force || last === null || last.channel !== channel) {
      this.guestPolled = { channel, at: now };
    }
    if (due || force) void this.pollUnheard(channel);
  }

  /** What a window no frame reaches missed (D839): the messages past its
   * newest — only while it holds the channel's end, so a reader up in its
   * history is not moved under — then the window read again for edits,
   * reactions and comment counts. */
  private async pollUnheard(channel: number): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    if (this.state.feed.reachedEnd) {
      await this.catchUp();
      if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
    }
    if (this.state.feed.channelId !== channel) return;
    await this.refreshWindowReactions();
  }

  // -------------------------------------------------------------------------
  // Sending

  /** Every keystroke calls this; the throttle mutates regardless of channel
   * (the state lives on the feed, so switching channels resets it). */
  noteTyping(): void {
    // Nobody is said to be typing in a broadcast channel or its threads
    // (docs/broadcast-channels-plan.md D856): the gateway drops the frame.
    const s = this.state;
    const open = s.selectedChannel === null ? undefined : mut.roomOf(s.rooms, s.selectedChannel);
    if (s.thread !== null || (open !== undefined && isBroadcastRoom(open))) return;
    let due = false;
    this.mutate((s) => {
      due = mut.shouldSendTyping(s.feed, Date.now());
    });
    const channel = this.state.feed.channelId;
    if (due && channel !== 0) this.socket?.sendTyping(channel);
  }

  send(text: string, staged: StagedFile[] = [], options: SendOptions = {}): void {
    // A forward waiting over the box goes from an empty one (D377).
    const forwarding = this.state.forward?.to === this.state.feed.channelId;
    if (text.trim() === "" && staged.length === 0 && !forwarding) return;
    this.prepareSend()?.(text, staged, options);
  }

  /** A command pressed in a message's words (pets-plan.md D402): sent into
   * the open chat as a message of its own — no reply, no forward, nothing the
   * markdown reading could take out of it — while the composer's draft, its
   * reply and the forward over it stay where they are. Outside a dialog, a
   * command that names no pet goes to the pet that wrote it: that is the pet
   * whose command it is, and bare it would reach every pet in the group that
   * declares the name. Only where the composer could send: nothing in a chat
   * the reader is not in, nor in a retired pet's dialog; a stopped one takes
   * `/restart` alone, as its Restart button sends it (D368). */
  sendCommand(command: string, authorId: number): void {
    const s = this.state;
    const room = s.rooms.find((r) => r.channelId === s.feed.channelId);
    const invocation = parseCommand(command);
    // Nor in a broadcast channel (docs/broadcast-channels-plan.md D841):
    // pressed in a post it would go out as a post of its own — or, from a
    // subscriber, as one the gateway refuses. A thread has no row, and its
    // comments send nothing either.
    if (!room?.member || isBroadcastRoom(room) || invocation === null) return;
    const pet = mut.petPeerOf(room, s.directory);
    if (pet !== null && s.account.pets !== null) {
      if (s.directory.people[pet]?.retired === true) return;
      if (room.petStopped) {
        if (`/${invocation.name}` === RESTART_TEXT) {
          void this.restartPet(pet).catch((e: unknown) =>
            this.showNotice(e instanceof Error ? e.message : String(e)),
          );
        }
        return;
      }
    }
    const author = s.directory.people[authorId];
    const text =
      invocation.pet === undefined && !isDmRoom(room) && author !== undefined && isPet(author)
        ? `${command}@${author.username}`
        : command;
    const context = this.sendContext(true);
    if (context === null) return;
    this.queueSend(
      Object.freeze({
        ...context,
        // Past the box, so the clock stays for what is written there (D1154).
        ttlS: null,
        noLinkPreview: false,
        forward: null,
        draft: text,
        parts: Object.freeze([Object.freeze({ text, entities: [], nonce: context.nonce })]),
        staged: Object.freeze([]),
      }),
    );
  }

  /** What a send takes at its gesture: the chat, the account, the limits and
   * the clock — and the composer's reply or edit, unless the send is `alone`,
   * one the box has no say in (D402). */
  private sendContext(alone: boolean): SendContext | null {
    const s = this.state;
    const channel = s.feed.channelId;
    if (channel === 0 || !session()) return null;
    // A comment is judged by its channel's row (docs/broadcast-channels-plan.md
    // D846): the limits, slowmode, the rights. A thread has no row of its own.
    const thread = s.thread !== null && s.thread.threadId === channel ? s.thread : null;
    const row = s.rooms.find((r) => r.channelId === (thread?.channelId ?? channel));
    const accountId = mut.selfIdOf(s.directory);
    const mode = alone ? null : s.feed.mode;
    const broadcast = row !== undefined && isBroadcastRoom(row);
    const standing = mut.standingIn(row, s.account.isAdmin, accountId);
    const post = broadcast && thread === null;
    const switches = post ? s.postSwitches[channel] : undefined;
    return Object.freeze({
      lifetime: currentLifetime(),
      accountId,
      selfName: mut.nameOf(s.directory, accountId),
      channel,
      replyTo: mode?.t === "replying" ? mode.seq : null,
      editing: mode?.t === "editing" ? mode.seq : null,
      nonce: crypto.randomUUID(),
      maxChars: row?.settings.max_message_chars ?? 4096,
      unlimited: s.account.isAdmin,
      // Slowmode binds comments and spares the staff (D852); posts are the
      // staff's.
      pause: broadcast
        ? post || isStaff(standing)
          ? 0
          : (row?.settings.slowmode_s ?? 0)
        : row?.admin
          ? 0
          : (row?.settings.slowmode_s ?? 0),
      // The channel's clock as it stands at the gesture (D298), whatever is
      // being sent — words, files, a voice note — and it comes off when the
      // send is queued (D1154). An edit takes none (D296), and nothing in a
      // channel disappears (D856).
      ttlS: mode?.t === "editing" || broadcast ? null : (s.sendTtl[channel] ?? null),
      post,
      asChannel:
        thread !== null &&
        s.commentAsChannel[thread.channelId] === true &&
        may(standing, "comment_as_channel"),
      ...(switches?.comments !== undefined ? { comments: switches.comments } : {}),
      ...(switches?.reactions !== undefined ? { reactions: switches.reactions } : {}),
    });
  }

  /** Snapshot the user's destination before any async attachment preparation. */
  prepareSend(): PreparedSend | null {
    const context = this.sendContext(false);
    if (context === null) return null;
    const { channel } = context;
    const composerRevision = this.state.feed.composerRevision;
    const mode = this.state.feed.mode;
    // Capture now, consume only once a real message is queued. A discarded
    // recording keeps the reply/edit; a newer composition is never cleared.
    let consumed = false;
    return (text, staged = [], options = {}) => {
      if (consumed || !isCurrent(context.lifetime)) return;
      if (!this.enqueueSend(context, text, staged, options.noLinkPreview === true)) return;
      consumed = true;
      if (
        this.state.feed.channelId === channel &&
        this.state.feed.composerRevision === composerRevision &&
        this.state.feed.mode === mode
      ) {
        this.cancelMode();
      }
    };
  }

  private enqueueSend(
    context: SendContext,
    text: string,
    staged: StagedFile[],
    noLinkPreview: boolean,
  ): boolean {
    // With everybody met by handle, so an `@username` — picked from the
    // composer's list or typed by hand — leaves as a mention, and an edit's words
    // become mentions again on save (D284). Whoever else the words tag is asked
    // about when this send's turn comes (`askTagged`).
    const parsed = parseMarkdown(text, mut.handlesOf(this.state.directory));
    const tags = unmetTags(parsed);
    const said = parsed.text.trim() !== "" || staged.length > 0;
    // The forward waiting over this box goes with the send (D377) — never with
    // an edit, which says nothing new.
    const waiting =
      context.editing === null && this.state.forward?.to === context.channel
        ? this.state.forward
        : null;
    if (!said && waiting === null) return false;
    let parts: Array<{ text: string; entities: import("../gen/dto").Entity[] }> = [];
    if (said) {
      const limit = sendLimit(staged.length > 0, context.maxChars);
      const split = splitForSend(parsed, context.editing !== null, limit);
      if (split.parts.length === 0 && waiting === null) return false;
      // A post is not split (docs/broadcast-channels-plan.md D841): each part
      // would be a post with comments of its own. The words go back to the
      // box, which let them go at the press.
      if (context.post && split.parts.length > 1) {
        this.showNotice(this.t.postTooLong(limit));
        this.giveBack(context.channel, text);
        return false;
      }
      if (split.truncated) this.showNotice(this.t.messageTooLongSplit(MAX_SPLIT_PARTS));
      parts = split.parts;
    }
    if (waiting !== null) {
      // Taken now: the bar comes down with the gesture.
      this.mutate((s) => {
        s.forward = null;
      });
    }
    this.spendSendTtl(context);
    const intent: SendIntent = Object.freeze({
      ...context,
      noLinkPreview,
      forward: waiting,
      draft: text,
      ...(tags.length > 0 ? { tags: Object.freeze(tags) } : {}),
      parts: Object.freeze(
        parts.map((part, index) =>
          Object.freeze({
            ...part,
            nonce: index === 0 ? context.nonce : crypto.randomUUID(),
          }),
        ),
      ),
      staged: Object.freeze(staged.map((item) => Object.freeze({ ...item }))),
    });
    this.queueSend(intent);
    return true;
  }

  private queueSend(intent: SendIntent): void {
    // A post's switches were for that post alone (D851).
    if (intent.post && this.state.postSwitches[intent.channel] !== undefined) {
      this.mutate((s) => {
        delete s.postSwitches[intent.channel];
      });
    }
    this.sendQueue.push(intent);
    void this.drainSends();
  }

  /** What a send says of who speaks and of a post's switches (D841, D848,
   * D851), as the gateway reads it: absent when there is nothing to say. */
  private static voiceBody(intent: SendIntent): Partial<SendBody> {
    return {
      ...(intent.asChannel ? { as_channel: true } : {}),
      ...(intent.post && intent.comments !== undefined ? { comments: intent.comments } : {}),
      ...(intent.post && intent.reactions !== undefined ? { reactions: intent.reactions } : {}),
    };
  }

  /** An optimistic copy said in the channel's name (D841, D848) is drawn as
   * the gateway will serve it to its sender: `sender_id` 0, the person behind
   * it as `posted_by`. */
  private static voiced(intent: SendIntent, m: Message): Message {
    if (!intent.post && !intent.asChannel) return m;
    return { ...m, sender_id: 0, as_channel: true, posted_by: intent.accountId };
  }

  private async drainSends(): Promise<void> {
    const lifetime = currentLifetime();
    if (this.sending) return;
    this.sending = true;
    try {
      for (;;) {
        const intent = this.sendQueue.shift();
        if (!intent) return;
        if (!isCurrent(intent.lifetime)) continue;
        await this.sendNow(intent);
        if (!isCurrent(lifetime)) return;
      }
    } catch (error) {
      if (isCurrent(lifetime)) this.fail(error);
    } finally {
      if (isCurrent(lifetime)) {
        this.sending = false;
      }
    }
  }

  /** Whoever the words tag and this client has not met (docs/fast-start.md D1057):
   * asked now, in the send's turn, and the words read again with them. A failure
   * leaves those tags as they are: words, which every reader draws as a tag anyway
   * (D463), and so does a gateway that has said nothing by `NAME_WAIT_MS` (D1092): the
   * sends behind this one wait on it, and a mention is not worth a message that does
   * not leave. What it says later is kept for the next send. A post stays one post,
   * whatever the second reading would cut it into. */
  private async askTagged(intent: SendIntent): Promise<SendIntent> {
    const lifetime = currentLifetime();
    const asking = async (): Promise<boolean> => {
      const rows = await api.lookupPeople([], intent.tags ?? []);
      if (isCurrent(lifetime)) this.meet(rows);
      return true;
    };
    const waited = new Promise<boolean>((done) => setTimeout(() => done(false), NAME_WAIT_MS));
    try {
      const answered = await Promise.race([asking(), waited]);
      if (!answered || !isCurrent(lifetime)) return intent;
    } catch {
      return intent;
    }
    const parsed = parseMarkdown(intent.draft, mut.handlesOf(this.state.directory));
    const limit = sendLimit(intent.staged.length > 0, intent.maxChars);
    const split = splitForSend(parsed, intent.editing !== null, limit);
    if (split.parts.length === 0 || (intent.post && split.parts.length > 1)) return intent;
    return Object.freeze({
      ...intent,
      parts: Object.freeze(
        split.parts.map((part, index) =>
          Object.freeze({
            ...part,
            nonce: intent.parts[index]?.nonce ?? crypto.randomUUID(),
          }),
        ),
      ),
    });
  }

  private async sendNow(given: SendIntent): Promise<void> {
    const lifetime = currentLifetime();
    if (!isCurrent(given.lifetime)) return;
    // Nothing is asked while the words tag nobody new, which is nearly always: a
    // bubble appears in the same tick as the press.
    const intent = given.tags === undefined ? given : await this.askTagged(given);
    if (!isCurrent(lifetime)) return;
    if (intent.sticker != null) {
      await this.sendStickerNow(intent, intent.sticker);
      return;
    }
    const { channel, editing, replyTo, staged, accountId: selfId, selfName } = intent;
    const { noLinkPreview, ttlS } = intent;
    const [first, ...tail] = intent.parts;
    if (editing !== null) {
      await this.commitEdit(
        channel,
        editing,
        first!.text,
        first!.entities,
        noLinkPreview,
        intent.draft,
      );
      if (!isCurrent(lifetime)) return;
      return;
    }
    if (first === undefined) {
      // Nothing of its own to say: a forward sent from an empty box (D377).
      if (intent.forward !== null) await this.sendForwards(intent, intent.forward, false);
      return;
    }
    const nonce = first.nonce;
    const sentAt = Date.now();
    const optimistic: Message = Runtime.voiced(intent, {
      channel_id: channel,
      seq: PENDING_SEQ,
      sender_id: selfId,
      sent_at_ms: sentAt,
      nonce,
      reply_to_seq: replyTo,
      text: first!.text,
      entities: first!.entities,
      attachments: [],
      ...(noLinkPreview ? { no_link_preview: true } : {}),
      ...optimisticTtl(ttlS),
    } as unknown as Message);

    // The bubble, into the feed of its channel if that is the one on screen —
    // stamped with the moment it appears, which for a `withheld` one is when
    // its files were up rather than when send was pressed, and which then
    // takes the feed nowhere (`Bubble.withheld`), and the window with it: the press
    // did that.
    const showBubble = (state: SendState, withheld: boolean) => {
      this.mutate((st) => {
        if (st.feed.channelId !== channel) return;
        const b = mut.newBubble({ ...optimistic, sent_at_ms: Date.now() }, selfName, true);
        if (withheld) b.withheld = true;
        // The quote the gateway will answer with (D273), taken from the
        // message being answered, so the header has its words while the send
        // is in flight.
        const target =
          replyTo === null ? undefined : st.feed.bubbles.find((x) => x.message.seq === replyTo);
        if (target) b.message.reply_to = mut.quoteOf(target.message);
        b.state = state;
        // A picture's own preview only: a staged film carries one too, for its
        // chip and the lightbox (D397), and the bubble draws these as images.
        b.localPreviews = staged
          .filter((f) => sendKindOf(f) === "photo")
          .map((f) => f.previewUrl)
          .filter((u): u is string => !!u);
        st.feed.bubbles.push(b); // PENDING_SEQ sorts it last by construction
        mut.regroup(st.feed);
        mut.resolveNames(st.feed, st.directory, this.t);
      });
      if (!withheld) this.comeBack(channel);
    };

    // The pill over the composer (chat-plan.md D398), and the stop its cross
    // pulls — none for a voice note, which raises no pill. The picked sizes
    // weigh its bar; its numbers wait for the bytes on the wire (`onProgress`
    // below).
    const pill = newUpload(
      channel,
      nonce,
      staged.map((f) => [sendKindOf(f), f.file.size] as const),
    );
    const stop = pill === null ? null : new AbortController();
    if (pill !== null && stop !== null) {
      this.uploadStop = { nonce, stop };
      this.mutate((st) => {
        st.upload = pill;
      });
    }
    const signal = stop?.signal;
    // A send that raises the pill is not in the feed until its files are up
    // (the owner's rule, D398): the pill says what is going on, and a bubble
    // with the picture already in it would say the message is there while
    // nobody else can see it yet. It shows when the last byte is up — or,
    // failed, when the send fails, so that what was typed survives. Words and
    // a voice note show at once, as always.
    const withheld = pill !== null;
    if (!withheld) {
      showBubble(staged.length > 0 ? "preparing" : "sending", false);
    } else {
      // The feed goes to its end at the press either way — where a send shown
      // at once appears, and where this one will — and not again when the
      // withheld bubble joins it: a long upload is time to go up and read.
      this.mutate((st) => {
        if (st.feed.channelId === channel) st.feed.withheldSends += 1;
      });
      this.comeBack(channel);
    }

    // Attachments first (chat-api.md §2.6): sha-dedup, 1 MiB chunks, per-file
    // progress folded onto the bubble by nonce.
    const refs: import("../gen/dto").AttachmentInput[] = [];
    for (let i = 0; i < staged.length; i += 1) {
      const item = staged[i]!;
      // `bytes` is the pill's numbers (D398): how many of the bytes this
      // attachment goes up as are up, of how many — a picture's two WebPs,
      // anything else as it is — never the picked file's.
      const onProgress = (fraction: number, bytes?: readonly [number, number]) => {
        if (!isCurrent(intent.lifetime)) return;
        this.mutate((st) => {
          const b = st.feed.bubbles.find((x) => x.message.nonce === nonce);
          if (b) b.sendProgress = (i + fraction) / staged.length;
          // By nonce too: a tick that outlived its send must not move the
          // next send's pill.
          const row = st.upload?.nonce === nonce ? st.upload.items[i] : undefined;
          if (row) {
            row.progress = Math.min(1, Math.max(0, fraction));
            if (bytes !== undefined) setUploadSent(row, bytes[0], bytes[1]);
          }
        });
      };
      try {
        const sendKind = sendKindOf(item);
        // A picture goes as two weights (D233): the 15 KiB thumbnail first,
        // so the far side has something to draw a second after «send», then
        // the 300 KiB preview that becomes the attachment itself.
        if (sendKind === "photo" && photoWeightsSupported()) {
          const { preview, thumb } = await photoWeights(item.file);
          if (!isCurrent(lifetime)) return;
          // What goes up is these two, however large the original was.
          const wire = thumb.blob.size + preview.blob.size;
          onProgress(0, [0, wire]);
          const thumbUp = await upload(
            thumb.blob,
            "photo",
            { width: thumb.width, height: thumb.height },
            (f) => onProgress(f * THUMB_SHARE, [thumb.blob.size * f, wire]),
            signal !== undefined ? { signal } : {},
          );
          if (!isCurrent(lifetime)) return;
          const previewUp = await upload(
            preview.blob,
            "photo",
            {
              fileName: item.file.name,
              width: preview.width,
              height: preview.height,
            },
            (f) =>
              onProgress(THUMB_SHARE + f * (1 - THUMB_SHARE), [
                thumb.blob.size + preview.blob.size * f,
                wire,
              ]),
            signal !== undefined ? { signal } : {},
          );
          if (!isCurrent(lifetime)) return;
          refs.push({
            file_id: previewUp.file_id,
            thumb_file_id: thumbUp.file_id,
            ...(item.file.name !== "" ? { file_name: item.file.name } : {}),
            ...(item.spoiler ? { spoiler: true } : {}),
          });
          continue;
        }
        // A picture that took the file door is still a picture: the budget
        // that applies is the original's, not the 100 MiB an installer gets.
        if (item.kind === "photo" && item.file.size > ORIGINAL_MAX_BYTES) {
          throw new UploadError(0, this.t.originalOverLimit(ORIGINAL_MAX_BYTES));
        }
        // Anything else goes up as it is: its bytes on the wire are known at
        // once.
        const size = item.file.size;
        onProgress(0, [0, size]);
        // A film through the «File» door (D397) goes as a file row, which
        // declares no geometry — nothing reads it there.
        const probed: Awaited<ReturnType<typeof probeFile>> = item.asFile
          ? {}
          : await probeFile(item, lifetime);
        if (!isCurrent(lifetime)) return;
        const result = await upload(
          item.file,
          sendKind,
          {
            fileName: item.file.name,
            ...(probed.width !== undefined ? { width: probed.width } : {}),
            ...(probed.height !== undefined ? { height: probed.height } : {}),
            ...(probed.durationMs !== undefined ? { durationMs: probed.durationMs } : {}),
          },
          (f) => onProgress(f, [size * f, size]),
          {
            unlimited: sendKind === "file" && intent.unlimited,
            ...(signal !== undefined ? { signal } : {}),
          },
        );
        if (!isCurrent(lifetime)) return;
        refs.push({
          file_id: result.file_id,
          ...(item.file.name !== "" ? { file_name: item.file.name } : {}),
          ...(item.waveform !== undefined ? { waveform: item.waveform } : {}),
          ...(item.spoiler ? { spoiler: true } : {}),
        });
      } catch (e) {
        if (!isCurrent(lifetime)) return;
        this.endUpload(nonce);
        // Called off from the pill (D398): not a failure, and nothing of it
        // stays behind in the feed.
        if (signal?.aborted) {
          this.callOff(intent);
          return;
        }
        // The withheld bubble first, failed, then the rest of a split and the
        // forward — the order they would have gone in.
        if (withheld) showBubble("failed", true);
        this.strandTail(channel, tail, noLinkPreview, ttlS);
        this.strandForwards(intent, intent.forward?.messages ?? []);
        this.markFailed(nonce, e instanceof Error ? e.message : String(e));
        return;
      }
    }
    // A press that landed after the last byte but before this line still
    // finds the message unsent, and is honoured.
    this.endUpload(nonce);
    if (signal?.aborted) {
      this.callOff(intent);
      return;
    }

    // Every byte is up: the withheld bubble joins the feed now, already
    // sending; one that has been there all along turns to sending.
    if (withheld) {
      showBubble("sending", true);
    } else {
      this.mutate((st) => {
        const b = st.feed.bubbles.find((x) => x.message.nonce === nonce);
        if (b) b.state = "sending";
      });
    }

    // The flag only when it is set, as serde's `skip_serializing_if` writes it:
    // a message that keeps its card says nothing about it. The lifetime the
    // same way (D296): a message that stays says nothing about that either.
    const body: SendBody = {
      nonce,
      text: first.text,
      entities: first.entities,
      attachments: refs,
      ...(replyTo !== null ? { reply_to_seq: replyTo } : {}),
      ...(noLinkPreview ? { no_link_preview: true } : {}),
      ...(ttlS !== null ? { ttl_s: ttlS } : {}),
      ...Runtime.voiceBody(intent),
    };

    try {
      const server = await api.sendMessage(channel, body);
      if (!isCurrent(lifetime)) return;
      this.confirm(channel, server);
      // A gateway older than `ttl_s` ignores it, and says so only by answering
      // without one (D296): the sender believes these words will go, and has to
      // be told that they will not. Said once per send, over its first part —
      // the rest go to the same gateway, and under slow mode would raise the
      // same line again every pause. Read off `ttl_s` and not off the deadline,
      // which a gateway that *does* support this leaves empty until somebody
      // reads the message (D302).
      if (ttlS !== null && server.ttl_s == null) {
        this.showNotice(this.t.ttlUnsupported());
      }
      await this.markSent(channel, server.seq);
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.strandTail(channel, tail, noLinkPreview, ttlS);
      this.strandForwards(intent, intent.forward?.messages ?? []);
      this.markFailed(nonce, e instanceof Error ? e.message : String(e));
      return;
    }

    // The tail, sequentially: seq is what the feed sorts on. Slow mode is
    // not bypassed for admins server-side exemption — the row knows.
    const pause = intent.pause;
    for (const part of tail) {
      if (pause > 0) await sessionDelay(pause * 1000, intent.lifetime);
      if (!isCurrent(lifetime)) return;
      const ok = await this.sendPart(intent, part);
      if (!isCurrent(lifetime)) return;
      if (!ok) {
        const rest = tail.slice(tail.indexOf(part) + 1);
        this.strandTail(channel, rest, noLinkPreview, ttlS);
        this.strandForwards(intent, intent.forward?.messages ?? []);
        return;
      }
    }
    // The forward after the words (D377), paced from the last of them.
    if (intent.forward !== null) await this.sendForwards(intent, intent.forward, true);
  }

  /** The cross on the pill over the composer (chat-plan.md D398): calls off
   * the send with this nonce while its attachments are still going up — the
   * upload stops at its request, the bubble leaves the feed, the rest of a
   * split is not sent, and a forward that was to follow goes back over the
   * composer. A press after that send finished, or naming one that raised no
   * pill, does nothing. */
  cancelUpload(nonce: string): void {
    const pending = this.uploadStop;
    if (pending === null || pending.nonce !== nonce) return;
    this.uploadStop = null;
    pending.stop.abort();
  }

  /** Takes the pill down (D398): the attachments are up, the send failed, or
   * it was called off. Only this send's — matched on its nonce. */
  /** The composer has seen the words a called-off send (D398) or a refused
   * edit gave back — taken into an empty box or let go under a new draft —
   * and they are not offered again. */
  takeReturnedDraft(): void {
    if (this.state.returnedDraft === null) return;
    this.mutate((st) => {
      st.returnedDraft = null;
    });
  }

  private endUpload(nonce: string): void {
    if (this.uploadStop?.nonce === nonce) this.uploadStop = null;
    if (this.state.upload?.nonce !== nonce) return;
    this.mutate((st) => {
      st.upload = null;
    });
  }

  /** A send called off from the pill (D398): it never reached the feed — a
   * send with a pill waits off it until its files are up — and what was to
   * follow it, the rest of a split, does not happen. A forward that was to go
   * after it goes back over the composer, unless another one waits there by
   * now: it was never sent, and picking it again from the other chat is the
   * work the bar exists to keep. The words go back to the box they were typed
   * in (`giveBack`). */
  private callOff(intent: SendIntent): void {
    const forward = intent.forward;
    this.giveBack(intent.channel, intent.draft);
    if (forward === null || forward.messages.length === 0) return;
    this.mutate((st) => {
      if (st.forward === null) st.forward = forward;
    });
  }

  /** Words the box let go of at the press, on their way back to it
   * (`returnedDraft`): a send called off from the pill (D398), an edit the
   * gateway did not take. The box of `channelId` takes them only if nothing
   * new has been typed there since. */
  private giveBack(channelId: number, text: string): void {
    if (text.trim() === "") return;
    this.mutate((st) => {
      st.returnedDraft = { channelId, text };
    });
  }

  /** The forward that goes with a send (forwarding.md D374, D377): each message
   * a `POST` of its own that names it, in the order it was said, spaced by the
   * channel's slow mode as a split's parts are — `paced` when something was
   * sent just before the first. Past the message budget's burst a forward goes
   * at the budget's own pace: a 429 is waited out and the same nonce asked
   * again, which the gateway answers once whatever happens (D324). A message
   * the gateway refuses for what it is — gone since it was picked, past the
   * ceilings of the chat it goes to — fails alone, and the next is judged on
   * its own. Stopped at the first other failure, with the rest stranded in the
   * feed as failed copies, as a split's are — unless the gateway cannot
   * forward at all, which the notice says once. On the send queue, as the
   * split's tail is: what is written after the forward is said after it. */
  private async sendForwards(
    intent: SendIntent,
    draft: ForwardDraft,
    paced: boolean,
  ): Promise<void> {
    const lifetime = currentLifetime();
    for (let i = 0; i < draft.messages.length; i += 1) {
      if (intent.pause > 0 && (paced || i > 0)) {
        await sessionDelay(intent.pause * 1000, intent.lifetime);
      }
      if (!isCurrent(lifetime)) return;
      const went = await this.forwardOne(intent, draft.from, draft.messages[i]!);
      if (!isCurrent(lifetime)) return;
      if (went === "sent" || went === "refused") continue;
      if (went === "failed") this.strandForwards(intent, draft.messages.slice(i + 1));
      return;
    }
  }

  /** One message of a forward: its copy drawn at once — the words, the
   * pictures and the line the gateway is about to write — then the send that
   * names it, and the server's copy over the drawn one by nonce. */
  private async forwardOne(
    intent: SendIntent,
    from: number,
    source: Message,
  ): Promise<"sent" | "refused" | "failed" | "unsupported"> {
    const lifetime = currentLifetime();
    const { channel, ttlS } = intent;
    const nonce = crypto.randomUUID();
    if (this.state.feed.channelId === channel) {
      this.mutate((st) => {
        const b = mut.newBubble(forwardCopy(intent, source, nonce), intent.selfName, true);
        b.state = "sending";
        st.feed.bubbles.push(b);
        mut.regroup(st.feed);
        mut.resolveNames(st.feed, st.directory, this.t);
      });
      this.comeBack(channel);
    }
    // The gateway keeps the card the words' author closed (D289); the
    // composer's own bar was about the comment, not about these.
    const body: SendBody = {
      nonce,
      text: "",
      entities: [],
      attachments: [],
      forward: { channel_id: from, seq: source.seq },
      ...(ttlS !== null ? { ttl_s: ttlS } : {}),
      ...Runtime.voiceBody(intent),
    };
    for (let waits = 0; ; waits += 1) {
      try {
        const server = await api.sendMessage(channel, body);
        if (!isCurrent(lifetime)) return "failed";
        this.confirm(channel, server);
        await this.markSent(channel, server.seq);
        return "sent";
      } catch (e) {
        if (!isCurrent(lifetime)) return "failed";
        const wait = waits < FORWARD_RETRIES ? forwardWaitMs(e, intent.pause) : null;
        if (wait !== null) {
          await sessionDelay(wait, intent.lifetime);
          if (!isCurrent(lifetime)) return "failed";
          continue;
        }
        const unsupported = cannotForward(e);
        this.markFailed(
          nonce,
          unsupported ? this.t.forwardUnsupported() : e instanceof Error ? e.message : String(e),
        );
        if (unsupported) return "unsupported";
        // A 400 is this message, for what it is; the rest may still go.
        return e instanceof api.ApiError && e.status === 400 ? "refused" : "failed";
      }
    }
  }

  /** The part of a forward that was not sent, as failed copies in the channel's
   * feed — strandTail()'s rule, and its reason: the bar came down when the
   * send began, and the feed is where the reader can still see what did not
   * go. */
  private strandForwards(intent: SendIntent, messages: ReadonlyArray<Message>): void {
    if (messages.length === 0) return;
    if (this.state.feed.channelId !== intent.channel) return; // never paint another channel
    this.mutate((s) => {
      for (const source of messages) {
        const b = mut.newBubble(
          forwardCopy(intent, source, crypto.randomUUID()),
          intent.selfName,
          true,
        );
        b.state = "failed";
        s.feed.bubbles.push(b);
      }
      mut.regroup(s.feed);
      mut.resolveNames(s.feed, s.directory, this.t);
    });
  }

  private async sendPart(intent: SendIntent, part: SendIntent["parts"][number]): Promise<boolean> {
    const lifetime = currentLifetime();
    if (!isCurrent(intent.lifetime)) return false;
    const { channel, accountId: selfId, selfName, noLinkPreview, ttlS } = intent;
    const { text, entities, nonce } = part;
    const sentAt = Date.now();
    const optimistic: Message = Runtime.voiced(intent, {
      channel_id: channel,
      seq: PENDING_SEQ,
      sender_id: selfId,
      sent_at_ms: sentAt,
      nonce,
      text,
      entities,
      attachments: [],
      ...(noLinkPreview ? { no_link_preview: true } : {}),
      ...optimisticTtl(ttlS),
    } as unknown as Message);
    if (this.state.feed.channelId === channel) {
      this.mutate((st) => {
        const b = mut.newBubble(optimistic, selfName, true);
        b.state = "sending";
        st.feed.bubbles.push(b);
        mut.regroup(st.feed);
      });
      this.comeBack(channel);
    }
    try {
      const server = await api.sendMessage(channel, {
        nonce,
        text,
        entities,
        attachments: [],
        ...(noLinkPreview ? { no_link_preview: true } : {}),
        ...(ttlS !== null ? { ttl_s: ttlS } : {}),
        ...Runtime.voiceBody(intent),
      });
      if (!isCurrent(lifetime)) return false;
      this.confirm(channel, server);
      await this.markSent(channel, server.seq);
      if (!isCurrent(lifetime)) return false;
      return true;
    } catch (e) {
      if (!isCurrent(lifetime)) return false;
      this.markFailed(nonce, e instanceof Error ? e.message : String(e));
      return false;
    }
  }

  /** The gateway's answer to a send of ours, folded into the window it was made in.
   * The answer usually is the next message, and its bubble is adopted where it
   * stands. When it is not (somebody else's message took the seq before it and has
   * not arrived, or the window is on its way back to the end) the feed leaves the
   * bubble as it was, and if the window held the end until this moment the gap is
   * closed, off to one side: the page brings the message back under its nonce. */
  private confirm(channel: number, server: Message): void {
    this.noteServerClock(server);
    if (this.state.feed.channelId !== channel) return;
    let lost = false;
    this.mutate((st) => {
      lost = mut.reconcile(st.feed, server, this.t);
      mut.resolveNames(st.feed, st.directory, this.t);
    });
    if (lost) void this.closeGap();
  }

  /** A message you just wrote is one you have read, and the mark lives on the
   * gateway, so it is told. Not from a window short of the end, whose reader has not
   * read what lies between for having written this (docs/fast-start.md D1091): that
   * window is on its way there, and whoever is at the end says so as it arrives. */
  private async markSent(channel: number, seq: number): Promise<void> {
    const feed = this.state.feed;
    if (feed.channelId === channel && !feed.reachedEnd) return;
    await this.markReadAt(channel, seq);
  }

  /** A failed bubble stays in the feed with its text intact — the composer
   * already cleared itself, so the feed is the only place the text survives.
   * There is no automatic retry. */
  private markFailed(nonce: string, message: string): void {
    this.mutate((s) => {
      const b = s.feed.bubbles.find((x) => x.message.nonce === nonce);
      if (b) b.state = "failed";
      s.notice = message;
    });
  }

  /** Never post past a hole: unsent parts become visible failed bubbles. */
  private strandTail(
    channel: number,
    parts: Array<{ text: string; entities: import("../gen/dto").Entity[] }>,
    noLinkPreview: boolean,
    ttlS: number | null,
  ): void {
    if (parts.length === 0) return;
    if (this.state.feed.channelId !== channel) return; // never paint another channel
    const selfId = mut.selfIdOf(this.state.directory);
    const selfName = mut.nameOf(this.state.directory, selfId);
    this.mutate((s) => {
      for (const part of parts) {
        const sentAt = Date.now();
        const m: Message = {
          channel_id: channel,
          seq: PENDING_SEQ,
          sender_id: selfId,
          sent_at_ms: sentAt,
          nonce: crypto.randomUUID(),
          text: part.text,
          entities: part.entities,
          attachments: [],
          ...(noLinkPreview ? { no_link_preview: true } : {}),
          ...optimisticTtl(ttlS),
        } as unknown as Message;
        const b = mut.newBubble(m, selfName, true);
        b.state = "failed";
        s.feed.bubbles.push(b);
      }
      mut.regroup(s.feed);
    });
  }

  /** `draft` is the box's text as typed, for the box to take back when the
   * gateway does not take the edit. */
  private async commitEdit(
    channel: number,
    seq: number,
    text: string,
    entities: import("../gen/dto").Entity[],
    noLinkPreview: boolean,
    draft: string,
  ): Promise<void> {
    const lifetime = currentLifetime();
    // The words as they were, under the card as it was (D467): no edit. The
    // box has let them go, the message keeps them and stays unmarked, and the
    // gateway is not asked — an older one would mark it.
    const held =
      this.state.feed.channelId === channel
        ? this.state.feed.bubbles.find((x) => x.message.seq === seq)
        : undefined;
    if (held && mut.editChangesNothing(held.message, text, entities, noLinkPreview)) return;
    try {
      // Always said, `false` included (D289): to the gateway an edit without
      // the field leaves the flag as it was — an older client's edit must not
      // bring back a card its author closed — so a card reopened here has to
      // be asked for out loud.
      const server = await api.editMessage(channel, seq, {
        text,
        entities,
        no_link_preview: noLinkPreview,
      });
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        if (s.feed.channelId !== channel) return;
        // Every reply quoting this message still carries the pre-edit words
        // (D273), and the author must not be shown them while the socket echo
        // is still in flight.
        mut.requote(s.feed, server);
        const b = s.feed.bubbles.find((x) => x.message.seq === seq);
        if (b) b.message = server;
        mut.resolveNames(s.feed, s.directory, this.t);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      // The message keeps its old words, and the new ones go back to the box,
      // which let them go at the press — refused past the hour (D409) as on a
      // line that dropped, what was typed is not lost.
      this.giveBack(channel, draft);
      this.fail(e);
    }
  }

  beginReply(seq: number): void {
    this.mutate((s) => {
      const b = s.feed.bubbles.find((x) => x.message.seq === seq);
      if (!b) return;
      s.feed.mode = { t: "replying", seq };
      s.feed.composerRevision += 1;
      // One bar at a time (forwarding.md D377): the newer gesture wins.
      if (s.forward?.to === s.feed.channelId) s.forward = null;
    });
  }

  beginEdit(seq: number): void {
    const now = this.serverNow();
    this.mutate((s) => {
      const b = s.feed.bubbles.find((x) => x.message.seq === seq);
      // Nor a forward, whose words are somebody else's (D375), nor anything
      // sent more than an hour ago (D409) — judged here, at the press: the E
      // of a bubble drawn before its hour ran out still calls this. Nor a
      // sticker, which has no words (docs/stickers-plan.md D550). A broadcast
      // channel's post has a day, and is its publisher's and `edit_posts`'s
      // (docs/broadcast-channels-plan.md D850, D867); a comment is not edited
      // while `comment` is taken from the reader in its channel (D923) — the
      // menu's rule, asked again here, so the two agree.
      if (
        !b ||
        isSticker(b.message) ||
        !mut.editableIn(mut.feedPlace(s), b, mut.selfIdOf(s.directory), now)
      ) {
        return;
      }
      s.feed.mode = { t: "editing", seq };
      s.feed.composerRevision += 1;
      if (s.forward?.to === s.feed.channelId) s.forward = null;
    });
  }

  // -------------------------------------------------------------------------
  // Forwarding (docs/forwarding.md D374–D378)

  /** Picks a message of the open chat, or puts it back (D377). */
  toggleSelected(seq: number): void {
    this.mutate((s) => {
      mut.toggleSelected(s.feed, seq);
    });
  }

  /** Puts every pick back, which ends the mode. */
  clearSelection(): void {
    if (this.state.feed.selected.length === 0) return;
    this.mutate((s) => {
      s.feed.selected = [];
    });
  }

  /** Sets a forward waiting over the composer of `to` (D377) — `seqs` of the
   * open chat `from`, as picked or as the window holds them, and only what may
   * go — and answers whether one waits. The picking that chose them is over,
   * and so is whatever the composer of `to` was doing to a message there when
   * `to` is the chat on screen. Opening `to` is the caller's: the shell
   * navigates, as a row of the list does. */
  forwardTo(from: number, seqs: number[], to: number): boolean {
    const s = this.state;
    if (from === 0 || s.feed.channelId !== from || !s.rooms.some((r) => r.channelId === to)) {
      return false;
    }
    let made = false;
    this.mutate((st) => {
      const messages = mut.forwardableOf(st.feed, seqs);
      if (messages.length === 0) return;
      st.feed.selected = [];
      if (st.feed.channelId === to) {
        st.feed.mode = { t: "new" };
        st.feed.composerRevision += 1;
      }
      // A broadcast channel's words name the channel on the bar
      // (docs/broadcast-channels-plan.md D860), as it stands now.
      const voice = st.feed.voice;
      st.forward =
        voice === null ? { from, to, messages } : { from, to, messages, voice: { ...voice } };
      made = true;
    });
    return made;
  }

  /** Lets the forward waiting over a composer go. */
  cancelForward(): void {
    if (this.state.forward === null) return;
    this.mutate((s) => {
      s.forward = null;
    });
  }

  cancelMode(): void {
    this.mutate((s) => {
      s.feed.mode = { t: "new" };
      s.feed.composerRevision += 1;
    });
  }

  /** Deletes a message of the open chat for everybody in it (chat-plan.md
   * D407) — the reader's answer to the question, or a send in flight. */
  deleteMessage(seq: number): Promise<void> {
    return this.removeMessage(seq, true);
  }

  /** Deletes a message of the open chat for the reader alone (chat-plan.md
   * D407): anybody's, theirs too — everybody else keeps it. A gateway older
   * than the route is said to be one, and nothing goes for everybody in its
   * place. */
  hideMessage(seq: number): Promise<void> {
    return this.removeMessage(seq, false);
  }

  /** The two above: the message leaves the window on the answer, as its
   * `message_deleted` would take it. */
  private async removeMessage(seq: number, everybody: boolean): Promise<void> {
    const lifetime = currentLifetime();
    const channel = this.state.feed.channelId;
    if (channel === 0) return;
    try {
      await (everybody ? api.deleteMessage(channel, seq) : api.hideMessage(channel, seq));
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        const open = s.feed.channelId === channel;
        if (open) mut.applyDeletion(s.feed, seq, this.t);
        const replacement = open && s.feed.reachedEnd ? mut.newestMessage(s.feed) : null;
        mut.roomsOnDeleted(
          s.rooms,
          channel,
          seq,
          replacement,
          s.selectedChannel,
          Date.now(),
          this.t,
        );
        s.forward = mut.forwardWithout(s.forward, channel, seq);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (!everybody && api.isMissingRoute(e)) {
        this.showNotice(this.t.deleteForMeUnsupported());
        return;
      }
      this.fail(e);
    }
  }

  /** Deletes a selection of `channelId` in one request (docs/message-selection.md
   * D380) — the messages the reader confirmed, named by the caller, so a pick
   * made while the question was up is not deleted with the rest. Every one
   * leaves on the answer through `forget`, as its `message_deleted` would take
   * it, and the selection empties as they go. A refusal keeps the selection;
   * a gateway older than the route answers 405, and is said to be one. */
  deleteMessages(channelId: number, seqs: number[]): Promise<void> {
    return this.removeMessages(channelId, seqs, true);
  }

  /** `deleteMessages` for the reader alone (chat-plan.md D407): one request
   * by the selection's «for me» route, refused whole or done whole. */
  hideMessages(channelId: number, seqs: number[]): Promise<void> {
    return this.removeMessages(channelId, seqs, false);
  }

  private async removeMessages(
    channelId: number,
    seqs: number[],
    everybody: boolean,
  ): Promise<void> {
    if (channelId === 0 || seqs.length === 0) return;
    const lifetime = currentLifetime();
    try {
      await (everybody ? api.deleteMessages(channelId, seqs) : api.hideMessages(channelId, seqs));
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        for (const seq of seqs) this.forget(s, channelId, seq);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (!everybody && api.isMissingRoute(e)) {
        this.showNotice(this.t.deleteForMeUnsupported());
        return;
      }
      if (e instanceof api.ApiError && e.status === 405) {
        this.showNotice(this.t.deleteManyUnsupported());
        return;
      }
      this.fail(e);
    }
  }

  /** The picks' words onto the clipboard (D381), as `selectionText` writes
   * them; then the notice that they are there, and the picking is over — as
   * after a forward and a deletion. A clipboard the browser refused says so
   * and keeps the selection. */
  async copySelected(): Promise<void> {
    const s = this.state;
    const channel = s.feed.channelId;
    // What is said in a broadcast channel's name heads its block with the
    // channel's name (docs/broadcast-channels-plan.md D841): its `sender_id`
    // 0 is nobody.
    const voice = s.feed.voice;
    const text = mut.selectionText(
      s.feed.selected,
      (id) => (id === 0 && voice !== null ? voice.name : mut.nameOf(s.directory, id)),
      this.t,
    );
    if (text === null) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      this.showNotice(this.t.copyFailed());
      return;
    }
    this.showNotice(this.t.selectionCopied());
    if (this.state.feed.channelId === channel) this.clearSelection();
  }

  // -------------------------------------------------------------------------
  // Disappearing messages (docs/disappearing-messages.md D296–D298)

  /** The composer's clock: the lifetime whatever is sent to `channelId` next
   * goes out under, or null to turn it off. Per channel and for the session
   * (D298), and for one send gesture (D1154) — `prepareSend` reads it at every
   * one. A lifetime outside D296's range is not taken: the gateway refuses the
   * send it would ride on rather than clamp it, so the dialog's gate is
   * repeated here. */
  setSendTtl(channelId: number, ttlS: number | null): void {
    if (ttlS !== null && !isValidTtl(ttlS)) return;
    this.mutate((s) => {
      if (ttlS === null) delete s.sendTtl[channelId];
      else s.sendTtl[channelId] = ttlS;
    });
  }

  /** A lifetime is for one message (D1154). Off at the gesture, so the mark
   * comes down with the press and a second message typed while this one is on
   * its way does not catch it. */
  private spendSendTtl({ channel, ttlS }: SendContext): void {
    if (ttlS !== null) this.setSendTtl(channel, null);
  }

  /** The gateway's now, as near as this client can tell (D298): what a
   * deadline is read against, here and in a bubble's countdown. */
  serverNow(): number {
    return mut.serverNow(this.state.clock, Date.now());
  }

  /** The answer to a send of ours is the gateway's clock read a moment ago,
   * as a live frame is (`onMessage`). Before the answer is folded in, so that
   * its deadline is read against the offset it has just corrected. */
  private noteServerClock(answer: Message): void {
    this.mutate((s) => {
      mut.noteClockSample(s.clock, answer.sent_at_ms, Date.now());
    });
  }

  /** A message is gone: the one way out of this client's state (D253), taken
   * by the gateway's `message_deleted` and by a deadline reached on our own
   * clock alike (D298) — in either order and any number of times. The window
   * drops a row it holds and is quiet about one it does not; the sidebar's
   * line stops quoting it once, because it then quotes something else or
   * nothing; and it is counted as a hole once (`roomsOnDeleted`). */
  private forget(s: UiState, channelId: number, seq: number): void {
    const open = s.feed.channelId === channelId;
    if (open) mut.applyDeletion(s.feed, seq, this.t);
    // The sidebar quotes the newest message of every room, open or not, and
    // has to stop quoting one that is gone. What is newest now only a window
    // holding the channel's end can say — and only among what the server has
    // confirmed.
    const replacement = open && s.feed.reachedEnd ? mut.newestMessage(s.feed) : null;
    mut.roomsOnDeleted(s.rooms, channelId, seq, replacement, s.selectedChannel, Date.now(), this.t);
    // A forward waiting over a composer lets a message of it that is gone go
    // (forwarding.md D377): the gateway would refuse to copy it.
    s.forward = mut.forwardWithout(s.forward, channelId, seq);
  }

  /** `state` less every copy of a message past its deadline on the gateway's
   * clock — each through `forget`, exactly as its `message_deleted` would
   * take it and will again, harmlessly, when it comes. The server's frame is
   * what normally removes a bubble; this is for when it cannot arrive — a
   * dead socket, a suspended laptop — because a copy that outlives its
   * deadline on a screen is the one thing its sender was promised would not
   * happen. The same state back when nothing is due. */
  private withoutExpired(state: UiState): UiState {
    const now = mut.serverNow(state.clock, Date.now());
    const due = mut.dueMessages(state.feed, state.rooms, now);
    if (due.length === 0) return state;
    return produce(state, (s) => {
      for (const m of due) this.forget(s, m.channelId, m.seq);
    });
  }

  /** Lets go of what is due now and stands the timer for what is due next. */
  private expireDue(): void {
    const before = this.state;
    const next = this.withoutExpired(before);
    if (next !== before) this.store.setState(next, true);
    this.armExpiry();
    // A line whose message went at its deadline asks what is newest now (D468).
    if (next.rooms !== before.rooms) this.askLines();
  }

  /** One timer, for the nearest deadline held. `mutate` stands it again
   * whenever the set of deadlines or the clock's offset may have moved. */
  private armExpiry(): void {
    this.stopExpiry();
    if (this.disposed) return;
    const deadline = mut.nextDeadline(this.state.feed, this.state.rooms);
    if (deadline === null) return;
    const lifetime = currentLifetime();
    this.expiryTimer = setTimeout(
      () => {
        this.expiryTimer = null;
        if (isCurrent(lifetime)) this.expireDue();
      },
      Math.max(0, deadline - this.serverNow()),
    );
  }

  private stopExpiry(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  // -------------------------------------------------------------------------
  // Reactions (D145–D167)

  async toggleReaction(seq: number, emoji: string): Promise<void> {
    const lifetime = currentLifetime();
    const s = this.state;
    const channel = s.feed.channelId;
    if (channel === 0) return;
    const b = s.feed.bubbles.find((x) => x.message.seq === seq);
    if (!b) return;
    const selfId = mut.selfIdOf(s.directory);
    const next = b.myReaction === emoji ? null : emoji; // picking the set emoji clears it
    let previous: string | null = null;
    this.mutate((st) => {
      previous = mut.setMyReaction(st.feed, seq, next, selfId);
    });
    try {
      const answer = next
        ? await api.setReaction(channel, seq, next)
        : await api.clearReaction(channel, seq);
      if (!isCurrent(lifetime)) return;
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.applyReactions(st.feed, seq, answer.reactions);
          const bb = st.feed.bubbles.find((x) => x.message.seq === seq);
          if (bb) bb.myReaction = answer.my_reaction ?? null;
        });
      }
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.setMyReaction(st.feed, seq, previous, selfId);
        });
      }
      this.mutate((st) => {
        st.notice = e instanceof Error ? e.message : String(e);
      });
    }
  }

  // -------------------------------------------------------------------------
  // Read marks

  /** The reader reached the bottom (scroll spam calls this; the divider
   * check keeps the repaints away). */
  markRead(): void {
    if (!this.canRead()) return;
    // The line says where this visit's reading began, and reaching the channel's
    // end is what passes it: the bottom of a window short of that has more
    // underneath (D271, docs/fast-start.md D1091).
    if (this.state.feed.reachedEnd && this.state.feed.dividerBefore !== null) {
      this.mutate((s) => {
        mut.dismissUnreadDivider(s.feed);
      });
    }
    const seq = mut.readableSeq(this.state.feed);
    if (seq === null) return;
    void this.markReadAt(this.state.feed.channelId, seq);
  }

  /** A message has been wholly on screen while the tab was in front (D249):
   * the badge comes down as messages pass under the eye instead of waiting for
   * the bottom. The unread divider stays — reaching the end is what takes that
   * down, and that goes through `markRead` above. */
  markReadUpTo(seen: number): void {
    if (!this.canRead()) return;
    const seq = mut.readableSeqUpTo(this.state.feed, seen);
    if (seq === null) return;
    void this.markReadAt(this.state.feed.channelId, seq);
  }

  private canRead(): boolean {
    return !document.hidden && (typeof document.hasFocus !== "function" || document.hasFocus());
  }

  private async markReadAt(channel: number, seq: number): Promise<void> {
    const lifetime = currentLifetime();
    if (seq === PENDING_SEQ) return; // must never be posted (poisons the mark)
    // Not in a thread, which has no row and no badge
    // (docs/broadcast-channels-plan.md D861), nor in a channel read without a
    // subscription, which is nobody's to mark (D837). Nothing is posted, and
    // the window's own mark still moves: its arrow counts what came after the
    // reader was here, not everything it holds.
    const row = mut.roomOf(this.state.rooms, channel);
    if (!row || row.guest) {
      this.mutate((s) => {
        if (s.feed.channelId === channel && s.feed.lastReadSeq < seq) s.feed.lastReadSeq = seq;
      });
      return;
    }
    try {
      const { last_read_seq } = await api.markRead(channel, seq);
      if (!isCurrent(lifetime)) return;
      this.applyReadMark(channel, last_read_seq);
    } catch {
      if (!isCurrent(lifetime)) return;
      // A lost read mark costs a badge, not data.
    }
  }

  private applyReadMark(channel: number, effective: number): void {
    this.mutate((s) => {
      mut.roomsOnRead(s.rooms, channel, effective);
      if (s.feed.channelId === channel && s.feed.lastReadSeq < effective) {
        s.feed.lastReadSeq = effective; // never backwards
      }
    });
  }

  // -------------------------------------------------------------------------
  // Join / membership (D114)

  /** The bare door. Nothing rides with it since D295: a private channel is
   * entered by its invite instead, through {@link joinByToken}. */
  async join(channelId: number): Promise<void> {
    const lifetime = currentLifetime();
    this.mutate((s) => {
      const row = mut.roomOf(s.rooms, channelId);
      if (row) row.joining = true;
    });
    try {
      const joined = await api.joinChannel(channelId);
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        mut.roomsOnJoined(s.rooms, channelId, joined.admin);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        const row = mut.roomOf(s.rooms, channelId);
        if (row) row.joining = false;
        s.notice = e instanceof Error ? e.message : String(e);
      });
    }
  }

  /** Answers whether it joined: a refusal has already become the notice. */
  async joinByToken(channelId: number, token: string): Promise<boolean> {
    const lifetime = currentLifetime();
    try {
      const joined = await api.joinChannel(channelId, { token });
      if (!isCurrent(lifetime)) return false;
      this.mutate((s) => {
        mut.roomsOnJoined(s.rooms, channelId, joined.admin);
      });
      return true;
    } catch (e) {
      if (!isCurrent(lifetime)) return false;
      this.fail(e);
      return false;
    }
  }

  // -------------------------------------------------------------------------
  // Link cards (docs/link-previews.md D278–D282)

  /**
   * A page's card (D280), asked of the gateway once per URL: a second want
   * while the first is queued or in the air is nothing, and so is one after
   * the answer — until a failure's wait is over (`LinkPreviewEntry`). Four at
   * a time across the app and the rest in line, first come first served.
   */
  wantLinkPreview(url: string): void {
    if (url === "" || this.previewsAsked.has(url)) return;
    if (!asksAgain(this.state.linkPreviews[url], Date.now())) return;
    const lifetime = currentLifetime();
    this.previewsAsked.add(url);
    this.mutate((s) => {
      s.linkPreviews[url] = { t: "loading" };
    });
    this.previewLane.run(async () => {
      let entry: LinkPreviewEntry;
      try {
        const preview = await api.fetchLinkPreview(url);
        entry =
          preview && typeof preview === "object"
            ? { t: "ready", preview }
            : { t: "none", retryAt: null };
      } catch (e) {
        entry = { t: "none", retryAt: askLater(e) ? Date.now() + LINK_RETRY_MS : null };
      }
      if (!isCurrent(lifetime)) return;
      this.previewsAsked.delete(url);
      this.mutate((s) => {
        s.linkPreviews[url] = entry;
      });
    });
  }

  /**
   * The card of a channel this reader holds no row of (D281) — a private one
   * somebody was sent the invite to is what it exists for. A channel they hold
   * is drawn from its row and asks nothing. Otherwise a page's discipline, in
   * a line of its own: a 404 is no card for the session.
   */
  wantChannelCard(channelId: number, token?: string | null): void {
    if (mut.roomOf(this.state.rooms, channelId)) return;
    const key = channelCardKey(channelId, token);
    if (this.cardsAsked.has(key)) return;
    if (!asksAgain(this.state.channelCards[key], Date.now())) return;
    const lifetime = currentLifetime();
    this.cardsAsked.add(key);
    this.mutate((s) => {
      s.channelCards[key] = { t: "loading" };
    });
    this.cardLane.run(async () => {
      let entry: ChannelCardEntry;
      try {
        const card = await api.fetchChannelCard(channelId, token);
        entry =
          card && typeof card === "object" ? { t: "ready", card } : { t: "none", retryAt: null };
      } catch (e) {
        entry = { t: "none", retryAt: askLater(e) ? Date.now() + LINK_RETRY_MS : null };
      }
      if (!isCurrent(lifetime)) return;
      this.cardsAsked.delete(key);
      this.mutate((s) => {
        s.channelCards[key] = entry;
      });
    });
  }

  /**
   * The card of a public broadcast channel a link names by its handle
   * (docs/broadcast-channels-plan.md D871): asked of the directory
   * (`?handle=`), which answers that channel or nothing, and kept under the
   * handle and under the channel's id alike — the card under a link draws it
   * by the id from then on. A channel the reader holds asks nothing.
   */
  wantHandleCard(handle: string): void {
    if (mut.handleTarget(this.state.rooms, {}, handle) !== null) return;
    const key = handleCardKey(handle);
    if (this.cardsAsked.has(key)) return;
    if (!asksAgain(this.state.channelCards[key], Date.now())) return;
    const lifetime = currentLifetime();
    this.cardsAsked.add(key);
    this.mutate((s) => {
      s.channelCards[key] = { t: "loading" };
    });
    this.cardLane.run(async () => {
      let answer: ChannelCardEntry;
      try {
        const card = (await api.fetchBroadcasts({ handle }))?.channels[0];
        answer = card ? { t: "ready", card } : { t: "none", retryAt: null };
      } catch (e) {
        answer = { t: "none", retryAt: askLater(e) ? Date.now() + LINK_RETRY_MS : null };
      }
      if (!isCurrent(lifetime)) return;
      this.cardsAsked.delete(key);
      this.mutate((s) => {
        s.channelCards[key] = answer;
        if (answer.t === "ready") s.channelCards[channelCardKey(answer.card.channel_id)] = answer;
      });
    });
  }

  /**
   * Join a channel from a link to it — its card's «Вступить», or an invite
   * link's own words (D279). One the reader is already in just opens.
   *
   * Without a token it is a public door, and only for a channel whose row
   * the reader holds: one they cannot see has no door a link without a token
   * could open, and no card offers one. The channel opens and the join goes
   * out.
   *
   * With a token it is the join route's own sequence (`JoinRoute`, the React
   * router's coordinator): the join, then the state — a private channel's row
   * arrives with it and not before (D115) — then the channel. A refused token
   * stops at the notice it raised, which the resync would clear.
   */
  async joinFromLink(channelId: number, token?: string | null): Promise<void> {
    const lifetime = currentLifetime();
    const row = mut.roomOf(this.state.rooms, channelId);
    if (row?.member) {
      this.openChannel(channelId);
      return;
    }
    if (!token) {
      if (!row) return;
      this.openChannel(channelId);
      // A broadcast channel's door is a subscription, whose row the state
      // then carries whole (docs/broadcast-channels-plan.md D838).
      await (isBroadcastRoom(row) ? this.subscribe(channelId) : this.join(channelId));
      return;
    }
    if (this.linkJoins.has(channelId)) return;
    this.linkJoins.add(channelId);
    try {
      if (!(await this.joinByToken(channelId, token))) return;
      if (!isCurrent(lifetime)) return;
      await this.sync();
      if (!isCurrent(lifetime)) return;
      this.openChannel(channelId);
    } finally {
      if (isCurrent(lifetime)) this.linkJoins.delete(channelId);
    }
  }

  /** The conversation with a friend (D279's «Написать»): the friendship
   * carries its channel, so there is nothing to ask. A pet is nobody's
   * friend, and its dialog has a door of its own (`openPetDm`). */
  openDm(userId: number): void {
    if (this.state.account.pets !== null && isPet(this.state.directory.people[userId])) {
      void this.openPetDm(userId).catch((e: unknown) => this.fail(e));
      return;
    }
    const friend = this.state.friends.friends.find((f) => f.user_id === userId);
    if (friend) this.openChannel(friend.channel_id);
  }

  // -------------------------------------------------------------------------
  // Pets (docs/pets-plan.md D349–D368)
  //
  // The verbs a person has about a pet. Each throws its refusal to the caller,
  // which shows it where the verb was pressed (the card's notice, the
  // dialog's Restart bar); the management sheet speaks to the gateway itself,
  // the way the channel console does.

  /** The row of the dialog with `petId`, when the reader has one. */
  private petDmOf(petId: number): import("./types").Room | undefined {
    return this.state.rooms.find((r) => isDmRoom(r) && r.peerId === petId);
  }

  /**
   * The dialog with a pet (D358, the card's «Написать»): the row when there
   * is one. Otherwise the gateway starts it — the pet hears of it and may
   * answer from then on — and the row is made here from the answer, exactly as
   * its `channel_created` makes it, because that frame and this answer race
   * and the second of them is a no-op.
   */
  openPetDm(petId: number): Promise<void> {
    const held = this.petDmOf(petId);
    if (held) {
      this.openChannel(held.channelId);
      return Promise.resolve();
    }
    return this.sessionAction(
      () => api.openPetDm(petId),
      ({ channel_id }) => {
        this.mutate((s) => {
          mut.roomsOnCreated(
            s.rooms,
            channel_id,
            mut.nameOf(s.directory, petId),
            "dm",
            petId,
            petId,
            mut.selfIdOf(s.directory),
          );
        });
        this.openChannel(channel_id);
      },
    );
  }

  /** Stop (D368): nothing the reader writes reaches the pet, and it may not
   * write to them, until they send `/restart`. Every device hears it from the
   * `pet_stopped` frame; this one does not wait for it. */
  stopPet(petId: number): Promise<void> {
    return this.sessionAction(
      () => api.stopPet(petId),
      () => {
        const row = this.petDmOf(petId);
        if (!row) return;
        this.mutate((s) => {
          mut.roomsOnPetStopped(s.rooms, row.channelId, true);
        });
      },
    );
  }

  /**
   * Restart (D368): the message `/restart` into the dialog, which the gateway
   * takes as the Stop taken back and hands the pet as an ordinary command.
   * Sent straight rather than through the composer's queue — it is pressed on
   * the card as often as in the dialog, where there is no composer to queue
   * in — and past the box's clock (D1154). Its echo draws it; the answer is
   * folded in so the open dialog does not wait.
   */
  restartPet(petId: number): Promise<void> {
    const row = this.petDmOf(petId);
    if (!row) return Promise.resolve();
    const channel = row.channelId;
    return this.sessionAction(
      () =>
        api.sendMessage(channel, {
          nonce: crypto.randomUUID(),
          text: RESTART_TEXT,
          entities: [],
          attachments: [],
        }),
      (answer) => {
        this.noteServerClock(answer);
        this.mutate((s) => {
          mut.roomsOnPetStopped(s.rooms, channel, false);
          mut.roomsOnMessage(s.rooms, answer, s.selectedChannel, true, Date.now(), this.t);
          if (s.feed.channelId === channel) {
            mut.reconcile(s.feed, answer, this.t);
            s.feed.lastReadSeq = Math.max(s.feed.lastReadSeq, answer.seq);
            mut.resolveNames(s.feed, s.directory, this.t);
          }
        });
        void this.markReadAt(channel, answer.seq);
      },
    );
  }

  /** A group's own admins put the pet into it (D359); the pet arrives with an
   * ordinary `membership` frame and a `joined` line that names who did it. */
  addPetToGroup(channelId: number, petId: number): Promise<void> {
    return this.sessionAction(
      () => api.addPetToGroup(channelId, petId),
      () => {
        const room = mut.roomOf(this.state.rooms, channelId);
        this.pushToast(this.t.petAdded(room?.name ?? `#${channelId}`), null);
      },
    );
  }

  /** Retire (D365) — the owner's or a server admin's, and for good. The pet's
   * own frame says so to everybody; this client does not wait for it. */
  retirePet(petId: number): Promise<void> {
    return this.sessionAction(
      () => api.retirePet(petId),
      () => {
        this.mutate((s) => {
          const pet = s.directory.people[petId];
          if (pet) pet.retired = true;
        });
        this.onPetsMoved([
          { petId, rev: this.state.directory.people[petId]?.petRev ?? 0, retired: true },
        ]);
        void this.refreshPetAbilities();
      },
    );
  }

  /** `GET /v1/me` again for `pets` alone (D363): making a pet can use up the
   * owner's allowance and retiring one can give it back, and only that answer
   * says which. Best-effort — the form's own answer was the one that mattered. */
  async refreshPetAbilities(): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const me = await api.fetchMe();
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        s.account.pets = me.pets ?? null;
      });
    } catch {
      // The button stays as it was until the next sign-in.
    }
  }

  /**
   * The chat's `/` list (D360), asked for when the composer first opens it
   * there and kept for the session until a pet's `membership` frame or its
   * card moving drops it (`dropCommands`). Nothing on a server without pets,
   * and nothing in a dialog with a person — there is no pet in it to declare
   * anything. A `loading` whose ask a teardown forgot is asked again.
   */
  /** «Details» opened over one of the reader's own messages (chat-plan.md
   * D422, D423): who read it is asked of the gateway the first time, and
   * again only once a `seen` frame — or a resync — says it may have a reader
   * the answer lacks. The submenu draws `s.reads` meanwhile. */
  wantReads(channelId: number, seq: number): void {
    // Written inside the draft below, so declared through `as` — see sync().
    let ask = false as boolean;
    this.mutate((s) => {
      ask = mut.wantReads(s.reads, channelId, seq);
    });
    if (!ask) return;
    const lifetime = currentLifetime();
    api.fetchMessageReads(channelId, seq).then(
      (r) => {
        if (!isCurrent(lifetime)) return;
        this.mutate((s) => {
          mut.readsLanded(s.reads, channelId, seq, r.reads ?? []);
        });
      },
      () => {
        if (!isCurrent(lifetime)) return;
        this.mutate((s) => {
          mut.readsLanded(s.reads, channelId, seq, null);
        });
      },
    );
  }

  wantCommands(channelId: number): void {
    const s = this.state;
    if (s.account.pets === null) return;
    const entry = s.commands[channelId];
    if (entry?.t === "ready" || (entry?.t === "loading" && this.commandAsks.has(channelId))) return;
    const room = mut.roomOf(s.rooms, channelId);
    if (!room || (isDmRoom(room) && mut.petPeerOf(room, s.directory) === null)) return;
    const ask = ++this.commandAskSeq;
    this.commandAsks.set(channelId, ask);
    const lifetime = currentLifetime();
    this.mutate((st) => {
      st.commands[channelId] = { t: "loading" };
    });
    api.fetchCommands(channelId).then(
      (commands) => {
        if (!isCurrent(lifetime) || this.commandAsks.get(channelId) !== ask) return;
        const list = Array.isArray(commands) ? commands : [];
        this.mutate((st) => {
          st.commands[channelId] = { t: "ready", commands: list };
        });
        // A pet the directory has not heard of has no name for `/name@pet`
        // and no face for its row: asked about like anybody else (D1057).
        for (const c of list) mut.met(this.state.directory, c.pet_id);
      },
      () => {
        if (!isCurrent(lifetime) || this.commandAsks.get(channelId) !== ask) return;
        this.mutate((st) => {
          st.commands[channelId] = { t: "none" };
        });
      },
    );
  }

  /** Forgets the `/` lists of `channels`, so the next opening asks again —
   * and asks at once for the open chat's, whose list may be on screen. An ask
   * still in the air for one of them is thrown away when it lands. */
  private dropCommands(channels: readonly number[]): void {
    const held = channels.filter((id) => this.state.commands[id] !== undefined);
    if (held.length === 0) return;
    for (const id of held) this.commandAsks.delete(id);
    this.mutate((s) => {
      for (const id of held) delete s.commands[id];
    });
    const open = this.state.feed.channelId;
    if (held.includes(open)) this.wantCommands(open);
  }

  // -------------------------------------------------------------------------
  // Stickers (docs/stickers-plan.md D546–D566) — the desktop's
  // `runtime::stickers`, method for method. Reads fail into nothing but a 401
  // (they are asked as the panel opens or a section comes near); the
  // editor's writes leave the gateway's sentence under the form.

  /** A sticker pressed in the panel (D550): a message of its own in the open
   * chat, under its clock, answering what the composer answers — and the
   * forward waiting over the box after it. The box's draft and an edit under
   * way stay where they are. Slow mode holds it as it holds words (D128): the
   * gateway refuses every send inside the wait, so a press inside it says how
   * long is left and sends nothing. Answers whether it went. */
  sendSticker(sticker: Sticker): boolean {
    const editing = this.state.feed.mode.t === "editing";
    const context = this.sendContext(editing);
    if (context === null) return false;
    const { channel } = context;
    const left = this.slowWait(channel);
    if (left > 0) {
      this.showNotice(this.t.slowModeWait(left));
      return false;
    }
    const waiting = this.state.forward?.to === channel ? this.state.forward : null;
    const replying = this.state.feed.mode.t === "replying";
    this.mutate((s) => {
      if (waiting !== null) s.forward = null;
      if (replying && s.feed.channelId === channel) s.feed.mode = { t: "new" };
    });
    this.spendSendTtl(context);
    this.queueSend(
      Object.freeze({
        ...context,
        noLinkPreview: false,
        forward: waiting,
        draft: "",
        parts: Object.freeze([Object.freeze({ text: "", entities: [], nonce: context.nonce })]),
        staged: Object.freeze([]),
        sticker,
      }),
    );
    return true;
  }

  /** The seconds slow mode still holds the reader back in `channel` (D128) —
   * the composer's own arithmetic (`slowWait`), whose send button counts them:
   * none where the reader administers the chat or the server, whom the gateway
   * exempts too, and otherwise the pause less the time since the newest of the
   * reader's own messages the gateway has. */
  private slowWait(channel: number): number {
    const s = this.state;
    const row = s.rooms.find((r) => r.channelId === channel);
    const pause = row?.settings.slowmode_s ?? 0;
    if (pause <= 0 || row?.admin === true || s.account.isAdmin) return 0;
    let lastMs = 0;
    for (let i = s.feed.bubbles.length - 1; i >= 0; i -= 1) {
      const b = s.feed.bubbles[i]!;
      if (b.isOwn && b.state === "sent") {
        lastMs = b.message.sent_at_ms;
        break;
      }
    }
    if (lastMs <= 0) return 0;
    const nowS = Math.floor(Date.now() / 1000);
    return Math.max(0, pause - (nowS - Math.floor(lastMs / 1000)));
  }

  /** The sticker's send: its bubble at once — the bytes are the pack's and on
   * the server already — then the message that names it, and the forward. */
  private async sendStickerNow(intent: SendIntent, sticker: Sticker): Promise<void> {
    const lifetime = currentLifetime();
    const { channel, replyTo, accountId: selfId, selfName, ttlS } = intent;
    const nonce = intent.parts[0]?.nonce ?? crypto.randomUUID();
    const optimistic = Runtime.voiced(intent, {
      channel_id: channel,
      seq: PENDING_SEQ,
      sender_id: selfId,
      sent_at_ms: Date.now(),
      nonce,
      reply_to_seq: replyTo,
      text: "",
      entities: [],
      // What the gateway will write from the pack's row (D550), so the bubble
      // does not change under the reader when the answer lands.
      attachments: [attachmentOf(sticker)],
      ...optimisticTtl(ttlS),
    } as unknown as Message);
    this.mutate((st) => {
      if (st.feed.channelId !== channel) return;
      const b = mut.newBubble(optimistic, selfName, true);
      const target =
        replyTo === null ? undefined : st.feed.bubbles.find((x) => x.message.seq === replyTo);
      if (target) b.message.reply_to = mut.quoteOf(target.message);
      b.state = "sending";
      st.feed.bubbles.push(b);
      mut.regroup(st.feed);
      mut.resolveNames(st.feed, st.directory, this.t);
    });
    this.comeBack(channel);
    const body: SendBody = {
      nonce,
      text: "",
      entities: [],
      attachments: [],
      ...(replyTo !== null ? { reply_to_seq: replyTo } : {}),
      ...(ttlS !== null ? { ttl_s: ttlS } : {}),
      sticker: { pack_id: sticker.pack_id, file_id: sticker.file_id },
      ...Runtime.voiceBody(intent),
    };
    try {
      const server = await api.sendMessage(channel, body);
      if (!isCurrent(lifetime)) return;
      this.confirm(channel, server);
      // The recents (D561), as the gateway moved its own.
      this.mutate((st) => stickers.noteSent(st.stickers, sticker));
      await this.markSent(channel, server.seq);
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.strandForwards(intent, intent.forward?.messages ?? []);
      this.markFailed(nonce, e instanceof Error ? e.message : String(e));
      return;
    }
    if (intent.forward !== null) await this.sendForwards(intent, intent.forward, true);
  }

  /** A read nobody asked for in so many words fails into nothing — but a 401
   * still signs out. */
  private quietSticker(e: unknown): void {
    if (e instanceof api.ApiError && e.unauthorized) this.fail(e);
  }

  /** The installed packs and the recents (D560, D561). A bare 404 on the
   * first is a gateway older than stickers (chat-api.md §7): the door stays
   * shut for the session. A list asked before an install or a removal that
   * was answered meanwhile lands nothing — it may be older than the write —
   * and is asked again. */
  async loadStickers(): Promise<void> {
    const lifetime = currentLifetime();
    const epoch = this.state.stickers.installedWrites;
    try {
      const list = await api.fetchInstalledStickerPacks();
      if (!isCurrent(lifetime)) return;
      const landed = { yes: true };
      this.mutate((s) => {
        landed.yes = stickers.landInstalled(s.stickers, list, epoch);
      });
      if (!landed.yes) {
        await this.loadStickers();
        return;
      }
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (api.isMissingRoute(e)) {
        this.mutate((s) => {
          s.stickers.support = "no";
        });
        return;
      }
      this.quietSticker(e);
      return;
    }
    try {
      const recents = await api.fetchStickerRecents();
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => stickers.landRecents(s.stickers, recents.stickers));
    } catch (e) {
      if (isCurrent(lifetime)) this.quietSticker(e);
    }
  }

  /** A pack's stickers, whole — a panel section come near the screen, a sheet
   * opening (`again`, for the counts as they are now), a sheet's «Try again».
   * A 404 is a pack this reader cannot see; any other failure leaves the
   * sheet saying so. A read overtaken by a write to the pack lands nothing —
   * the gateway may have answered it before the write — and is asked again. */
  wantStickerPack(packId: number, again = false): void {
    const claim = { claimed: false, epoch: 0 };
    this.mutate((s) => {
      claim.claimed = stickers.claimPack(s.stickers, packId, again);
      claim.epoch = stickers.packEpoch(s.stickers, packId);
    });
    if (!claim.claimed) return;
    const lifetime = currentLifetime();
    api.fetchStickerPack(packId).then(
      (pack) => {
        if (!isCurrent(lifetime)) return;
        const landed = { yes: true };
        this.mutate((s) => {
          landed.yes = stickers.landRead(s.stickers, pack, claim.epoch);
        });
        if (!landed.yes) this.wantStickerPack(packId, true);
      },
      (e: unknown) => {
        if (!isCurrent(lifetime)) return;
        if (e instanceof api.ApiError && e.notFound) {
          this.mutate((s) => stickers.packGone(s.stickers, packId));
        } else {
          this.mutate((s) => stickers.packFailed(s.stickers, packId));
          this.quietSticker(e);
        }
      },
    );
  }

  /** What one emoji alone in the box offers (D574), by its canonical form —
   * asked as the bar over the box mounts, every time, since the recents and
   * the installed packs move; the last answer stays on screen meanwhile. A
   * failure — a gateway older than the route among them — offers nothing and
   * says nothing. */
  wantStickerSuggestions(emoji: string): void {
    const claim = { claimed: false };
    this.mutate((s) => {
      claim.claimed = stickers.claimSuggestions(s.stickers, emoji);
    });
    if (!claim.claimed) return;
    const lifetime = currentLifetime();
    api.fetchStickerSuggestions(emoji).then(
      (offered) => {
        if (!isCurrent(lifetime)) return;
        this.mutate((s) => stickers.landSuggestions(s.stickers, emoji, offered.stickers));
      },
      (e: unknown) => {
        if (!isCurrent(lifetime)) return;
        this.mutate((s) => stickers.suggestionsFailed(s.stickers, emoji));
        if (!api.isMissingRoute(e)) this.quietSticker(e);
      },
    );
  }

  /** The catalogue's question (D566); the one already on screen asks
   * nothing. */
  searchStickerPacks(query: string, order: "popular" | "new"): void {
    const out: { ask: stickers.CatalogAsk | null } = { ask: null };
    this.mutate((s) => {
      out.ask = stickers.askCatalog(s.stickers.catalog, query, order);
    });
    if (out.ask !== null) this.fetchStickerCatalog(out.ask);
  }

  /** The next page of the question on screen — the list's end come near. */
  moreStickerPacks(): void {
    const out: { ask: stickers.CatalogAsk | null } = { ask: null };
    this.mutate((s) => {
      out.ask = stickers.moreCatalog(s.stickers.catalog);
    });
    if (out.ask !== null) this.fetchStickerCatalog(out.ask);
  }

  /** The catalogue's «Try again»: the page that failed, asked once more — a
   * failure is never asked again by the scrolling alone. */
  retryStickerPacks(): void {
    const out: { ask: stickers.CatalogAsk | null } = { ask: null };
    this.mutate((s) => {
      out.ask = stickers.retryCatalog(s.stickers.catalog);
    });
    if (out.ask !== null) this.fetchStickerCatalog(out.ask);
  }

  private fetchStickerCatalog(ask: stickers.CatalogAsk): void {
    const lifetime = currentLifetime();
    api.fetchStickerCatalog(ask.query, ask.order, ask.cursor).then(
      (page) => {
        if (!isCurrent(lifetime)) return;
        this.mutate((s) => {
          stickers.landCatalog(s.stickers.catalog, ask.generation, page);
        });
      },
      (e: unknown) => {
        if (!isCurrent(lifetime)) return;
        this.mutate((s) => {
          stickers.failCatalog(s.stickers.catalog, ask.generation);
        });
        this.quietSticker(e);
      },
    );
  }

  /** Into the panel (D560): first among the installed. The gateway's refusal
   * — two hundred already — is its own sentence (D339). */
  installStickerPack(packId: number): Promise<void> {
    return this.sessionAction(
      () => api.installStickerPack(packId),
      () => this.mutate((s) => stickers.setInstalled(s.stickers, packId, true)),
    );
  }

  uninstallStickerPack(packId: number): Promise<void> {
    return this.sessionAction(
      () => api.uninstallStickerPack(packId),
      () => this.mutate((s) => stickers.setInstalled(s.stickers, packId, false)),
    );
  }

  /** Puts a sticker sheet up, or takes it down. A pack's sheet reads its pack
   * again; the editor's list, the reader's own packs; the catalogue its first
   * page, once. */
  openStickerSheet(sheet: StickerSheet | null): void {
    this.mutate((s) => stickers.openSheet(s.stickers, sheet));
    if (sheet === null) return;
    if (sheet.t === "pack") this.wantStickerPack(sheet.id, true);
    else if (sheet.t === "editor" && sheet.id !== null) this.wantStickerPack(sheet.id, true);
    else if (sheet.t === "editor") void this.loadOwnStickerPacks();
    else {
      const c = this.state.stickers.catalog;
      this.searchStickerPacks(c.query, c.order);
    }
  }

  async loadOwnStickerPacks(): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const list = await api.fetchOwnStickerPacks();
      if (isCurrent(lifetime)) this.mutate((s) => stickers.landOwn(s.stickers, list));
    } catch (e) {
      if (isCurrent(lifetime)) this.quietSticker(e);
    }
  }

  /** One of the editor's requests: busy while it is out, the gateway's
   * sentence under the form when it is refused. */
  private async stickerEdit<T>(
    request: () => Promise<T>,
    apply: (result: T, s: StickersState) => void,
  ): Promise<void> {
    const lifetime = currentLifetime();
    this.mutate((s) => {
      s.stickers.editor = { busy: true, error: null };
    });
    try {
      const result = await request();
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        s.stickers.editor.busy = false;
        apply(result, s.stickers);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        s.stickers.editor = { busy: false, error: e instanceof Error ? e.message : String(e) };
      });
      if (e instanceof api.ApiError && e.unauthorized) this.fail(e);
    }
  }

  /** The editor's own refusal of a picked file (D546, D564), in words. */
  setStickerEditorError(error: string | null): void {
    this.mutate((s) => {
      s.stickers.editor.error = error;
    });
  }

  /** A new draft (D566); the editor opens on it, if the reader's packs are
   * still the sheet on screen. */
  createStickerPack(title: string): Promise<void> {
    return this.stickerEdit(
      () => api.createStickerPack(title.trim()),
      (pack, s) => stickers.landCreated(s, pack),
    );
  }

  renameStickerPack(packId: number, title: string): Promise<void> {
    return this.stickerEdit(
      () => api.renameStickerPack(packId, title.trim()),
      (pack, s) => stickers.landEdit(s, pack),
    );
  }

  /** The owner's delete (D566): out of every list, and back to the reader's
   * packs. */
  deleteStickerPack(packId: number): Promise<void> {
    return this.stickerEdit(
      () => api.deleteStickerPack(packId),
      (_, s) => {
        stickers.forgetPack(s, packId);
        if (s.sheet?.t === "editor" && s.sheet.id === packId) {
          s.sheet = { t: "editor", id: null };
          s.back = null;
        }
      },
    );
  }

  publishStickerPack(packId: number): Promise<void> {
    return this.stickerEdit(
      () => api.publishStickerPack(packId),
      (pack, s) => stickers.landEdit(s, pack),
    );
  }

  /** The judged file goes up into its pack (D548): its bytes and — a TGS's —
   * its poster, in base64, with its emoji, in one request. */
  addSticker(
    packId: number,
    file: Uint8Array,
    poster: Blob | null,
    emoji: string[],
  ): Promise<void> {
    return this.stickerEdit(
      async () => {
        const posterBytes = poster === null ? null : new Uint8Array(await poster.arrayBuffer());
        return api.addSticker(packId, {
          file: toBase64(file),
          ...(posterBytes !== null ? { poster: toBase64(posterBytes) } : {}),
          emoji,
        });
      },
      (sticker, s) => stickers.landSticker(s, sticker),
    );
  }

  setStickerEmoji(packId: number, fileId: string, emoji: string[]): Promise<void> {
    return this.stickerEdit(
      () => api.editSticker(packId, fileId, { emoji }),
      (sticker, s) => stickers.landSticker(s, sticker),
    );
  }

  deleteSticker(packId: number, fileId: string): Promise<void> {
    return this.stickerEdit(
      () => api.deleteSticker(packId, fileId),
      (_, s) => stickers.dropSticker(s, packId, fileId),
    );
  }

  /** A server administrator's take-down (D551). */
  removeStickerPack(packId: number, blockFiles: boolean): Promise<void> {
    return this.sessionAction(
      () => api.removeStickerPack(packId, blockFiles),
      () => this.mutate((s) => stickers.packGone(s.stickers, packId)),
    );
  }

  /**
   * A group (D274), on a channel's terms. A 404 is a gateway older than
   * groups: it has no such route and made nothing, which is said in the page's
   * language rather than as the bare status — and nothing falls back to a
   * channel, which would be public under a name meant for a few people.
   */
  async createGroup(name: string): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const { channel_id } = await api.createGroup(name);
      if (!isCurrent(lifetime)) return;
      await this.sync();
      if (!isCurrent(lifetime)) return;
      this.openChannel(channel_id);
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (e instanceof api.ApiError && e.status === 404) {
        this.mutate((s) => {
          s.notice = this.t.groupUnsupported();
        });
        return;
      }
      this.fail(e);
    }
  }

  // -------------------------------------------------------------------------
  // The 1 s tick (2 s before D464): typing expiry, ring expiry, idle
  // transitions — one clock.

  private startTick(): void {
    this.stopTick();
    this.tickTimer = setInterval(() => this.tick(), TICK_MS);
  }

  private stopTick(): void {
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private noteActivity(): void {
    this.lastActivityMs = Date.now();
  }

  private tick(): void {
    const now = Date.now();
    const stopRing = this.state.ringing !== null && now - this.state.ringing.sinceMs >= RING_FOR_MS;
    const serverNow = this.serverNow();
    this.mutate((s) => {
      mut.tickTyping(s.typing, now);
      if (stopRing) s.ringing = null;
      // A restriction ends by the clock and not by a frame
      // (docs/broadcast-channels-plan.md D853): at its end the thread's foot
      // and the menus come back.
      mut.endRestrictions(s.rooms, serverNow);
    });
    if (stopRing) this.onRing?.(false);
    // Presence transitions (D205): in-page signals only, 300 s threshold,
    // transitions-only behind the double gate.
    const away = now - this.lastActivityMs >= AFK_AFTER_MS;
    const s = this.state;
    const speak = s.connected && s.presence.enabled && s.presence.selfAfk !== away;
    if (speak) {
      this.mutate((st) => {
        st.presence.selfAfk = away;
      });
      this.socket?.sendActivity(away);
    }
    // The expiry timer counts running time, and a machine that slept through a
    // deadline wakes with it still counting (D298): this clock looks again
    // against the wall, and stands the timer for what is actually left.
    this.expireDue();
    this.pollGuest(now);
    this.comeBackAgain(now);
  }

  // -------------------------------------------------------------------------

  setTab(tab: import("./types").SidebarTab): void {
    this.transition("tab", () => {
      this.mutate((s) => {
        s.tab = tab;
      });
    });
    try {
      localStorage.setItem("echofloat.tab", tab);
    } catch {
      /* Keep the live selection. */
    }
  }

  clearNotice(): void {
    this.transition("toast", () => {
      this.mutate((s) => {
        s.notice = null;
      });
    });
  }

  showNotice(text: string): void {
    this.transition("toast", () => {
      this.mutate((s) => {
        s.notice = text;
      });
    });
  }

  // ---------------------------------------------------------------------
  // The own profile (F2)

  /** `""` clears, and every answer is the whole UserJson (D64/D65). */
  saveProfile(displayName: string, bio: string): Promise<void> {
    return this.sessionAction(
      () =>
        api.updateProfile({
          username: this.state.selfUsername,
          display_name: displayName.trim(),
          bio: bio.trim(),
        }),
      (me) => this.adoptMe(me),
    );
  }

  uploadAvatar(file: File): Promise<void> {
    return this.sessionAction(
      async (check) => {
        const result = await upload(file, "photo", { fileName: file.name }, () => {}, {});
        check();
        return api.setAvatar(result.file_id);
      },
      (me) => this.adoptMe(me),
    );
  }

  removeAvatar(): Promise<void> {
    return this.sessionAction(
      () => api.deleteAvatar(),
      (me) => this.adoptMe(me),
    );
  }

  /** R44: the change revokes older tokens, so the socket's next rejection is
   * expected — the window is armed BEFORE the request, the fresh JWT is
   * adopted from the answer, and the socket redials with it instead of
   * logging out. */
  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    armReauthWindow();
    return this.sessionAction(
      () => api.changePassword(currentPassword, newPassword),
      ({ jwt }) => {
        const s = session();
        if (s) setSession({ ...s, jwt });
        this.startSocket(); // redial with the fresh JWT
      },
    );
  }

  // ---------------------------------------------------------------------
  // The user card + friends (F3)

  /** Always a FRESH friendship read (F3): the card shows a placeholder
   * while it flies, never defaults. */
  openCard(userId: number): void {
    const generation = ++this.cardGeneration;
    // The recheck stays INSIDE the swap callback: a view transition runs its
    // callback at capture time (next frame), and recheckCard reads
    // state.card.userId — called outside, it would see the card still closed.
    this.transition("sheet", () => {
      if (generation !== this.cardGeneration) return;
      this.mutate((s) => {
        s.card = { userId, relation: null, pet: null };
      });
      void this.recheckCard();
    });
  }

  /** An `@handle` pressed in a message's words (dioxus-plan.md D463): opens
   * the card of whoever answers to it, and answers whether anybody does.
   * Looked up at the press and never ahead of it — among the people this tab has
   * met, and when nobody there answers, of the gateway (docs/fast-start.md D1057).
   * Nobody after that is `false`, and the tag says «Not found». */
  async openHandle(handle: string): Promise<boolean> {
    let id = this.personNamed(handle);
    if (id === null) {
      const lifetime = currentLifetime();
      try {
        const rows = await api.lookupPeople([], [handle]);
        if (!isCurrent(lifetime)) return false;
        this.meet(rows);
      } catch {
        // Nobody, as far as this press can tell.
      }
      id = this.personNamed(handle);
    }
    if (id === null) return false;
    this.openCard(id);
    return true;
  }

  /** Who answers to `handle` in the directory — blind to case, as the gateway
   * compares usernames (migration 0035). */
  private personNamed(handle: string): number | null {
    const want = handle.toLowerCase();
    for (const [id, username] of mut.handlesOf(this.state.directory)) {
      if (username.trim().toLowerCase() === want) return id;
    }
    return null;
  }

  closeCard(): void {
    const generation = ++this.cardGeneration;
    this.transition("sheet", () => {
      if (generation !== this.cardGeneration) return;
      this.mutate((s) => {
        s.card = { userId: null, relation: null, pet: null };
      });
    });
  }

  /** Re-read after an action moved the relation. */
  reopenCard(): Promise<void> {
    return this.recheckCard();
  }

  addFriend(userId: number): Promise<void> {
    return this.sessionAction(() => api.sendFriendRequest(userId));
  }

  cancelRequest(userId: number): Promise<void> {
    return this.sessionAction(() => api.cancelFriendRequest(userId));
  }

  acceptRequest(userId: number): Promise<void> {
    return this.sessionAction(() => api.acceptFriendRequest(userId));
  }

  /** Silent by design (D171): nothing reaches the sender. */
  declineRequest(userId: number): Promise<void> {
    return this.sessionAction(() => api.declineFriendRequest(userId));
  }

  unfriendUser(userId: number): Promise<void> {
    return this.sessionAction(() => api.unfriend(userId));
  }

  /** «Delete chat» on a former friend's conversation (friends-plan.md D412).
   * The row goes as soon as the gateway agrees rather than when the socket
   * says so: the frame reaches only this account's devices, and this is the
   * one that asked. A 404 is another device having deleted it first — gone
   * all the same. */
  forgetConversation(channelId: number): Promise<void> {
    return this.sessionAction(
      () =>
        api.forgetConversation(channelId).catch((e: unknown) => {
          if (!(e instanceof api.ApiError && e.status === 404)) throw e;
        }),
      () => this.onChannelDeleted(channelId),
    );
  }

  /** «Delete chat» on a live dialog, for the reader alone (friends-plan.md
   * D465): everything up to the newest message this client has been told of
   * — never one that landed after, which the reader has not seen. The mark
   * lands on the answer rather than on the frame, which reaches only this
   * account's devices and finds the work done. A gateway older than the route
   * answers a bare 404, and the reader is told so: nothing was deleted. */
  clearHistory(channelId: number): Promise<void> {
    const row = mut.roomOf(this.state.rooms, channelId);
    if (!row) return Promise.resolve();
    return this.sessionAction(
      () =>
        api.clearHistory(channelId, row.maxSeq).catch((e: unknown) => {
          if (api.isMissingRoute(e)) {
            this.showNotice(this.t.deleteChatUnsupported());
            return null;
          }
          throw e;
        }),
      (answer) => {
        if (answer !== null) this.onHistoryCleared(channelId, answer.cleared_seq);
      },
    );
  }

  blockUser(userId: number): Promise<void> {
    return this.sessionAction(() => api.blockUser(userId));
  }

  unblock(userId: number): Promise<void> {
    return this.sessionAction(() => api.unblockUser(userId));
  }

  /** `auto | dnd | invisible` (D183); the answer is the whole UserJson. */
  setManualStatus(status: "auto" | "dnd" | "invisible"): Promise<void> {
    return this.sessionAction(
      () => api.setStatus(status),
      (me) => this.adoptMe(me),
    );
  }

  /** «Send usage statistics» (analytics-plan.md D437), inverted: `true` stops
   * the per-day records about this account and deletes the ones kept. The
   * switch moves when the server's answer says it has, never before. */
  setAnalyticsOff(off: boolean): Promise<void> {
    return this.sessionAction(
      () => api.patchMe({ analytics_off: off }),
      (me) => this.adoptMe(me),
    );
  }

  /** Decline a DM call (D178): per-callee and silent-to-others. */
  async declineIncomingCall(): Promise<void> {
    const lifetime = currentLifetime();
    const ring = this.state.ringing;
    if (!ring) return;
    this.transition("toast", () => {
      this.mutate((s) => {
        s.ringing = null;
      });
    });
    this.onRing?.(false);
    const row = this.state.rooms.find((r) => r.channelId === ring.channelId);
    if (row && row.kind === "dm") {
      await api.declineCall(ring.channelId).catch(() => {});
      if (!isCurrent(lifetime)) return;
    }
  }

  // -------------------------------------------------------------------------
  // Voice (F4)

  private voice: VoiceSession | null = null;

  async joinVoice(channelId: number): Promise<void> {
    const lifetime = currentLifetime();
    if (!voiceSupported()) {
      this.showNotice(this.t.voiceUnsupported());
      return;
    }
    if (this.state.voice?.channelId === channelId) return;
    // Straight from one call into another: the person is busy starting the
    // next one, and a question over it would be in the way (D515).
    if (this.voice) this.endVoice(false);
    const sess = session();
    if (!sess) return;
    // Answering by joining IS the answer — the ring card goes away.
    if (this.state.ringing?.channelId === channelId) {
      this.transition("toast", () => {
        this.mutate((s) => {
          s.ringing = null;
        });
      });
      this.onRing?.(false);
    }
    this.mutate((s) => {
      s.voice = mut.voiceConnecting(channelId);
    });
    const voice = new VoiceSession({
      jwt: sess.jwt,
      events: this.voiceEvents(channelId, () => this.voice === voice),
    });
    this.voice = voice;
    try {
      await voice.join(channelId, false);
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (this.voice === voice) {
        this.voice = null;
        this.mutate((s) => {
          s.voice = null;
        });
        const text = e instanceof Error ? e.message : String(e);
        this.showNotice(
          /getUserMedia|Permission|NotAllowed/i.test(text) ? this.t.voiceMicDenied() : text,
        );
        voice.leave();
      }
    }
  }

  leaveVoice(): void {
    this.endVoice(true);
  }

  /** Hanging up. `ask`: our own hang-up ends the call for us, and a call
   * that came up with somebody in it is asked about (docs/call-rating.md
   * D515) — read from the call as it stood, before it is forgotten. */
  private endVoice(ask: boolean): void {
    if (this.voice) sounds.play("leave");
    this.voice?.leave();
    this.voice = null;
    this.mutate((s) => {
      if (ask) {
        const rating = mut.ratingAsk(s.voice);
        if (rating) s.callRating = rating;
      }
      s.voice = null;
    });
  }

  /** The answer to the after-call question (docs/call-rating.md D516): a
   * rating sends the one word and says thank you once the gateway has it;
   * `null` — the dialog closed — takes the question down and sends nothing.
   * A refusal says nothing either: the press is done, and an error about a
   * rating is about nothing the person can fix. */
  async rateCall(rating: "good" | "bad" | null): Promise<void> {
    const ask = this.state.callRating;
    if (!ask) return;
    this.mutate((s) => {
      s.callRating = null;
    });
    if (!rating) return;
    const lifetime = currentLifetime();
    try {
      await api.rateCall(ask.channelId, rating);
    } catch {
      return;
    }
    if (isCurrent(lifetime)) this.showNotice(this.t.callRatingThanks());
  }

  toggleMute(): void {
    const v = this.state.voice;
    if (!v || !this.voice) return;
    const next = !v.muted;
    sounds.play(next ? "mute" : "unmute");
    this.voice.setMuted(next);
    this.mutate((s) => {
      if (s.voice) s.voice.muted = next;
    });
  }

  toggleDeafen(): void {
    const v = this.state.voice;
    if (!v || !this.voice) return;
    const next = !v.deafened;
    this.voice.setDeafened(next);
    this.mutate((s) => {
      if (s.voice) s.voice.deafened = next;
    });
  }

  setVoiceVolume(userId: number, volume: number): void {
    this.voice?.setVolume(userId, volume);
    this.mutate((s) => {
      if (s.voice) s.voice.volumes[userId] = volume;
    });
  }

  // -------------------------------------------------------------------------
  // Stream viewing (F5)

  watchStream(userId: number): void {
    const v = this.state.voice;
    const peer = v?.peers.find((p) => p.userId === userId);
    if (!v || !peer || !peer.screenSharing || !this.voice) return;
    if (!shareViewSupported()) {
      this.showNotice(this.t.voiceUnsupported());
      return;
    }
    this.voice.watch({
      senderId: peer.senderId,
      hasAudio: peer.screenShareHasAudio,
    });
    this.voice.setShareVolume(1);
    this.mutate((s) => {
      if (s.voice) {
        s.voice.share = {
          userId,
          paused: peer.screenSharePaused,
          hasAudio: peer.screenShareHasAudio,
          volume: 1,
        };
      }
    });
  }

  stopWatching(): void {
    this.voice?.unwatch();
    this.mutate((s) => {
      if (s.voice) s.voice.share = null;
    });
  }

  setShareVolume(volume: number): void {
    this.voice?.setShareVolume(volume);
    this.mutate((s) => {
      if (s.voice?.share) s.voice.share.volume = volume;
    });
  }

  /** The stream view's canvas registers its painter for decoded frames;
   * null unregisters. Frames are VideoFrames the painter must close. */
  private sharePainterOwner: object | null = null;

  registerSharePainter(painter: SharePainter | null): () => void {
    const owner = {};
    const voice = this.voice;
    this.sharePainterOwner = owner;
    voice?.setPainter(painter);
    return () => {
      if (this.sharePainterOwner !== owner) return;
      this.sharePainterOwner = null;
      if (this.voice === voice) voice?.setPainter(null);
    };
  }

  private shareEndText(reason: number): string {
    switch (reason) {
      case 2:
        return this.t.streamEndedSourceGone();
      case 3:
        return this.t.streamEndedLeft();
      case 4:
        return this.t.streamEndedAdmin();
      default:
        return this.t.streamEndedStopped();
    }
  }

  /** Every VoiceSession event lands in the store through here; the guard
   * keeps a late event from a torn-down session off the next call's state. */
  private voiceEvents(channelId: number, ownsCall: () => boolean): VoiceEvents {
    const lifetime = currentLifetime();
    const mutateVoice = (fn: (v: import("./types").VoiceCall) => void) => {
      this.mutate((s) => {
        if (s.voice && s.voice.channelId === channelId) fn(s.voice);
      });
    };
    const events: VoiceEvents = {
      onUp: () => {
        sounds.play("join");
        mutateVoice((v) => {
          v.phase = "up";
          // Whoever the roster already holds is company from now (D515).
          if (v.peers.length > 0) v.company = true;
        });
      },
      onRoster: (peers) =>
        mutateVoice((v) => {
          v.peers = peers.map((p) => ({ ...p }));
          if (v.phase === "up" && peers.length > 0) v.company = true;
        }),
      onPeerJoined: (peer) => {
        sounds.play("join");
        mutateVoice((v) => {
          mut.voicePeerUpsert(v, { ...peer });
          if (v.phase === "up") v.company = true;
        });
      },
      onPeerLeft: (userId) => {
        sounds.play("leave");
        mutateVoice((v) => mut.voicePeerLeft(v, userId));
      },
      onPeerVoice: (userId, muted, deafened) =>
        mutateVoice((v) => mut.voicePeerFlags(v, userId, muted, deafened)),
      onPeerRtt: (userId, rttMs) => mutateVoice((v) => mut.voicePeerRtt(v, userId, rttMs)),
      onServerMute: (userId, muted) => mutateVoice((v) => mut.voiceServerMute(v, userId, muted)),
      onSpeakers: (userIds) =>
        mutateVoice((v) => {
          v.speaking = userIds;
        }),
      onAudience: (audience) =>
        mutateVoice((v) => {
          v.audience = audience;
        }),
      onPeerShare: (userId, share) => {
        const wasWatching = this.state.voice?.share?.userId === userId;
        // On the edge only (D342): the roster a joiner is handed comes through
        // `onRoster`, not here, so a share already running says nothing — but
        // a pause is reported as the same active share again, and is no edge.
        const wasSharing =
          this.state.voice?.peers.find((p) => p.userId === userId)?.screenSharing === true;
        if (share.active !== wasSharing) {
          sounds.play(share.active ? "stream_start" : "stream_end");
        }
        mutateVoice((v) =>
          mut.voicePeerShare(v, userId, share.active, share.paused, share.hasAudio),
        );
        if (wasWatching && !share.active) {
          this.voice?.unwatch();
          this.showNotice(this.shareEndText(share.reason));
        }
      },
      // The mark and nothing more (camera-plan.md D703): no sound, no notice.
      onPeerCamera: (userId, active) => mutateVoice((v) => mut.voicePeerCamera(v, userId, active)),
      onCode: (code) =>
        mutateVoice((v) => {
          v.code = code;
        }),
      onGroup: (_members, unlisted) =>
        mutateVoice((v) => {
          v.unlisted = unlisted;
        }),
      onStats: (stats) =>
        mutateVoice((v) => {
          v.rttMs = stats.rttMs;
          v.lossPct = stats.lossPct;
          v.concealmentPct = stats.concealmentPct;
        }),
      onEnded: (reason) => {
        if (this.state.voice?.channelId !== channelId) return;
        this.voice = null;
        this.mutate((s) => {
          // Ended around us — by the gateway, or by a socket that dropped and
          // took the call with it — is over all the same, and asked about the
          // same (D515). Not when the channel went: there is nowhere left to
          // post the rating to.
          if (reason !== "channelDeleted") {
            const rating = mut.ratingAsk(s.voice);
            if (rating) s.callRating = rating;
          }
          s.voice = null;
        });
        if (reason === "dropped" || reason === "serverError") {
          this.showNotice(this.t.voiceDropped());
        }
      },
    };
    return Object.fromEntries(
      Object.entries(events).map(([name, callback]) => [
        name,
        (...args: unknown[]) => {
          if (isCurrent(lifetime) && ownsCall()) Reflect.apply(callback, events, args);
        },
      ]),
    ) as unknown as VoiceEvents;
  }
}

function splicePage(
  feed: import("./types").FeedState,
  page: import("../gen/dto").PageResponse,
  atEnd: boolean,
  t: Dict,
): void {
  const first = page.messages[0]?.seq;
  const last = page.messages.at(-1)?.seq;
  const messages = page.messages.map(sanitizeMessage);
  for (const message of messages) {
    // Refreshes also restore edits missed during disconnection, and a history
    // page may itself be the first acknowledgement of a pending send.
    if (
      feed.bubbles.some(
        (b) =>
          b.message.seq === message.seq ||
          (message.nonce !== "" && b.message.nonce === message.nonce && b.state !== "sent"),
      )
    ) {
      mut.foldServed(feed, message, t);
    }
  }
  mut.appendMessages(feed, messages, atEnd);
  mut.liftCeiling(feed);
  // The span half matters even when append inserts nothing — a visible-range
  // re-fetch after a socket drop is all duplicates, and the chips are the
  // part that still has to land (D160).
  if (first !== undefined && last !== undefined) {
    mut.applyPageReactions(feed, [first, last], page.reactions ?? [], page.my_reactions ?? []);
    // …and a broadcast channel's comment counts beside its posts
    // (docs/broadcast-channels-plan.md D849), whose absence is none.
    if (feed.voice !== null) mut.applyPageComments(feed, [first, last], page.comments ?? []);
  }
  // …and how many people have seen them (D1082), which only ever grows.
  mut.applyViews(feed, page.views ?? []);
}

/** The lossy-page rule (chat-api.md §7) at the JSON boundary: an element the
 * generated types cannot vouch for still has to be placeable; one that is
 * not even placeable is dropped, not guessed at. Runtime shape-checking is
 * deliberately shallow — the visible-line rule is enforced by rendering
 * unknown service kinds, not by rejecting rows. */
function sanitizeMessage(m: Message): Message {
  return m;
}

/** Whether the open window is one no frame reaches
 * (docs/broadcast-channels-plan.md D839): a broadcast channel the reader does
 * not subscribe to, read in its own feed or in a post's comments under it —
 * the frames of both go to the channel's subscribers alone. The desktop's
 * `unheard`. */
function unheard(s: UiState): boolean {
  const thread = s.thread !== null && s.thread.threadId === s.feed.channelId ? s.thread : null;
  const row = mut.roomOf(s.rooms, thread?.channelId ?? s.feed.channelId);
  return row !== undefined && isBroadcastRoom(row) && !row.member;
}

function stripTag(m: Message & { t?: string }): Message {
  if (!("t" in m)) return m;
  const { t: _tag, ...rest } = m;
  return rest as unknown as Message;
}

function normalizeManual(v: unknown): "dnd" | "invisible" | null {
  return v === "dnd" || v === "invisible" ? v : null;
}

/** `ask`, waited out as a read behind the first screen may be (D321, D1075): nobody
 * is looking at it, so it sits through the waits the gateway names and the lane's
 * 429 a few times before it is given up. The desktop's `lane_json`. */
async function patientLane<T>(ask: () => Promise<T>, lifetime: SessionLifetime): Promise<T> {
  for (let waited = 0; ; waited += 1) {
    try {
      return await api.patiently("requests", LANE_PATIENCE, ask);
    } catch (e) {
      if (!(e instanceof api.ApiError) || e.status !== 429 || waited >= LANE_PATIENCE) throw e;
      await sessionDelay(LANE_WAIT_MS, lifetime);
    }
  }
}

/** The handles words tag that no mention of theirs covers, nor their code or their
 * links (D463): the people a send has to ask about (docs/fast-start.md D1057).
 * Without the `@`, each once, and no more than one request may name. */
function unmetTags(parsed: ParsedMarkdown): string[] {
  const { text, entities } = parsed;
  const handles = findTags(text, entities).map((tag) =>
    text.slice(tag.offset + 1, tag.offset + tag.length),
  );
  return [...new Set(handles)].slice(0, PEOPLE_TAGS);
}

/** How many characters one part of a send may hold: fewer under an attachment
 * (chat-api.md §2.3), and never past the channel's own limit. */
function sendLimit(attached: boolean, maxChars: number): number {
  return Math.min(attached ? 1024 : 4096, maxChars);
}

/** The words of a send as the messages they go out as (D131). An edit stays one
 * message: it changes the one it names. */
function splitForSend(parsed: ParsedMarkdown, editing: boolean, limit: number): SplitResult {
  return editing
    ? { parts: [parsed], truncated: false }
    : splitMessage(parsed.text, parsed.entities, limit);
}

/** What an optimistic bubble says of its lifetime (D298): the lifetime, and no
 * deadline — nothing is counting until somebody else has read it (D302), which
 * cannot have happened to a message that has not been sent yet. Absent, like
 * the flag beside it, on a message that stays. */
function optimisticTtl(ttlS: number | null): { ttl_s?: number } {
  return ttlS !== null ? { ttl_s: ttlS } : {};
}

/** Whether a link card's held answer is worth asking for again: none held; a
 * `loading` whose ask is gone — the ask itself is tracked apart, so this is
 * one a torn-down runtime left behind; or a failure whose wait is over. */
function asksAgain(entry: LinkPreviewEntry | ChannelCardEntry | undefined, now: number): boolean {
  if (entry === undefined || entry.t === "loading") return true;
  return entry.t === "none" && entry.retryAt !== null && now >= entry.retryAt;
}

/** Whether a link card that failed may be asked for again later (D280): over
 * the budget (429), a fetch that outlived its request (503), whatever the
 * network or the server's own trouble did (0, 5xx). A 404 — nothing to show,
 * a refused address, a gateway older than the route — and every other refusal
 * (a 400 for an address that is not http(s)) is the answer for the session. */
function askLater(e: unknown): boolean {
  const status = e instanceof api.ApiError ? e.status : 0;
  return status === 0 || status === 429 || status >= 500;
}

/** A few requests in the air and the rest in line, first come first served.
 * `clear` forgets the line and the count together — an account that ended
 * takes its requests with it — and a request of the old generation that
 * settles afterwards touches neither. */
class Lane {
  private readonly line: Array<() => Promise<void>> = [];
  private flying = 0;
  private generation = 0;

  constructor(private readonly width: number) {}

  run(job: () => Promise<void>): void {
    this.line.push(job);
    this.pump();
  }

  clear(): void {
    this.line.length = 0;
    this.flying = 0;
    this.generation += 1;
  }

  private pump(): void {
    while (this.flying < this.width) {
      const job = this.line.shift();
      if (!job) return;
      this.flying += 1;
      const generation = this.generation;
      void job()
        .catch(() => {})
        .finally(() => {
          if (generation !== this.generation) return;
          this.flying -= 1;
          this.pump();
        });
    }
  }
}

export interface StagedFile {
  file: File;
  /** What the file *is*. Not necessarily what it is sent as — see `asFile`. */
  kind: import("../gen/dto").FileKind;
  /**
   * «Send as file» (D233): upload the original bytes untouched, as a file row
   * with no thumbnail and no preview, instead of the two light weights a
   * picture is normally reduced to. Set by the paperclip's «File» door
   * (D397) for whatever it picks. A flag beside `kind` rather than an
   * overwrite of it, because the ceiling that applies is still the picture's.
   */
  asFile?: boolean;
  /** An object URL of the file, for a picture or a film that travels as one:
   * its chip and the lightbox draw it (D397), and a picture's is what the
   * optimistic bubble shows. */
  previewUrl?: string;
  /** Voice notes carry their packed waveform, already base64. */
  waveform?: string;
  /** Voice notes: the recorder measured it; the server has no decoder. */
  durationMs?: number;
  spoiler?: boolean;
}

/** What this attachment travels as, which is its kind unless its sender chose
 * to send a picture as a file (D233). */
function sendKindOf(item: StagedFile): import("../gen/dto").FileKind {
  return item.asFile ? "file" : item.kind;
}

/** The share of a picture's upload progress the thumbnail gets: it is a
 * twentieth of the bytes, and a bar that sat at 50 % for the thumbnail would
 * be lying about which half is the wait. */
const THUMB_SHARE = 0.15;

/** width/height are read BEFORE uploading — the server has no decoder for
 * video and trusts the client (chat-api.md §1.3).
 *
 * Photos do not come here any more: their geometry is whatever the weights
 * came out as (`lib/photo.ts`), and an original sent as a *file* has no
 * geometry to declare — a file row draws no picture. */
async function probeFile(
  item: StagedFile,
  lifetime: SessionLifetime,
): Promise<{ width?: number; height?: number; durationMs?: number }> {
  assertCurrent(lifetime);
  if (item.kind === "video" || item.kind === "video_message") {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(item.file);
      const v = document.createElement("video");
      const cleanup = () => {
        lifetime.signal.removeEventListener("abort", cancel);
        v.onloadedmetadata = null;
        v.onerror = null;
        v.removeAttribute("src");
        v.load();
        URL.revokeObjectURL(url);
      };
      const cancel = () => {
        cleanup();
        reject(lifetime.signal.reason);
      };
      lifetime.signal.addEventListener("abort", cancel, { once: true });
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        const out = {
          width: v.videoWidth,
          height: v.videoHeight,
          durationMs: Math.round(v.duration * 1000),
        };
        cleanup();
        resolve(out);
      };
      v.onerror = () => {
        cleanup();
        resolve({});
      };
      v.src = url;
    });
  }
  if (item.kind === "voice" && item.durationMs !== undefined) {
    return { durationMs: item.durationMs };
  }
  return {};
}

/** The copy of `source` a forward draws before the gateway has made it
 * (forwarding.md D377): our message, in flight, carrying the words, the
 * pictures and the header the gateway will write — `Forwarded::of`, as
 * `forwardHeaderOf` spells it. */
function forwardCopy(intent: SendIntent, source: Message, nonce: string): Message {
  const copy = {
    channel_id: intent.channel,
    seq: PENDING_SEQ,
    sender_id: intent.accountId,
    sent_at_ms: Date.now(),
    nonce,
    forwarded: mut.forwardHeaderOf(source),
    text: source.text,
    entities: source.entities ?? [],
    attachments: source.attachments ?? [],
    ...(source.no_link_preview ? { no_link_preview: true } : {}),
    ...optimisticTtl(intent.ttlS),
  } as unknown as Message;
  // Into a broadcast channel's feed, or as the channel in a thread, the copy
  // is the channel's (docs/broadcast-channels-plan.md D841, D848).
  return intent.post || intent.asChannel
    ? { ...copy, sender_id: 0, as_channel: true, posted_by: intent.accountId }
    : copy;
}

/** Whether a forward's refusal is a gateway older than forwarding (D377,
 * chat-api.md §7): it drops `forward` with everything else it does not know
 * and refuses the empty send that is left, with its own sentence for an empty
 * message in either of its languages (`gateway/src/locale.rs`). A gateway that
 * forwards never says that to one: what it copies is never empty. */
function cannotForward(e: unknown): boolean {
  return (
    e instanceof api.ApiError &&
    e.status === 400 &&
    (e.serverText === "message is empty" || e.serverText === "Сообщение пустое")
  );
}

/** How long a forward the gateway turned away with a 429 waits before asking
 * again under the same nonce (D377), or null when that is no wait at all. The
 * message budget's 429 — told by its sentence, in either language the gateway
 * answers in — refills in seconds. Slow mode's asks for the channel's own gap,
 * which our last message there started and which is over once it has passed
 * in full: asked any sooner, every ask would spend a token of the budget the
 * gateway counts first, the one every other send of ours draws on. The
 * desktop's `forward_wait`. */
function forwardWaitMs(e: unknown, pauseS: number): number | null {
  if (!(e instanceof api.ApiError) || e.status !== 429) return null;
  if (
    e.serverText === "too many messages; slow down" ||
    e.serverText === "Слишком много сообщений. Подождите немного."
  ) {
    return FORWARD_RETRY_AFTER_MS;
  }
  return pauseS > 0 ? pauseS * 1000 : null;
}
